/**
 * NetworkClubMeeting
 * Jitsi meeting component voor netwerkclub meetings
 * Met extended agenda sidebar voor quick add items
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { JitsiMeeting } from '@jitsi/react-sdk';
import VideoMeetingOverlay, { type OverlaySettings, type OverlayEntry } from './components/VideoMeetingOverlay';
import { auth, db, storage } from '../../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, addDoc, serverTimestamp, doc, onSnapshot, setDoc, updateDoc, deleteField, Timestamp, query, orderBy, limit as fbLimit, where } from 'firebase/firestore';
import { openAI } from '../../services/openAIService';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { Icon } from '../icons';
import { 
  Users, MessageSquare, Share2, Settings, Maximize2, Minimize2,
  Video, VideoOff, Mic, MicOff, PhoneOff, Monitor, MoreVertical,
  Calendar, Plus, X, Loader
} from '../icons';
import { AgendaItem } from '../../types/sharedAgenda.types';
import { sharedAgendaItemVisibleInMeeting } from '../../lib/sharedAgendaVisibility';
import { SessionStructureEngine, BlockCountdownOverlay, LiveCommitmentReview, SessionTimerOverlay, MeetingWidgetsOverlay, useStructuredGrowth, useSessionFlow } from '../../modules/structuredGrowth';
import type { MemberMeta, SessionBlock } from '../../modules/structuredGrowth';
import SelfStartOverlay from './components/SelfStartOverlay';
import MeetingCountdown from './components/MeetingCountdown';
import NuLivePanel from './components/NuLivePanel';
import PitchReactions from './components/PitchReactions';
import AttendanceHistory from './components/AttendanceHistory';
import { QrDisplay } from './components/GuestLanding';
import BeamerPitchView from './components/BeamerPitchView';
import { usePitchMedia, extractYoutubeVideoId } from './hooks/usePitchMedia';
import { useMeetingRapport } from './hooks/useMeetingRapport';
import {
  COLLECTIONS,
  TIMER_DEFAULT_SECONDS,
  FEEDBACK_TOAST_MS,
  STRINGS,
  THEME_DEFAULTS,
  TRANSITION_DEFAULT_SECONDS,
  STORAGE_PATHS,
} from './meetingConstants';

/** YouTube-video-id uit gangbare URL-vormen (niet: startpagina / zoeken / playlist zonder v=). */
function extractYoutubeVideoIdFromUrl(url: URL): string | null {
  const h = url.hostname.replace(/^www\./, '').toLowerCase();
  if (h === 'youtu.be') {
    const seg = url.pathname.split('/').filter(Boolean)[0];
    return seg?.split('?')[0] ?? null;
  }
  if (h === 'youtube.com' || h === 'm.youtube.com' || h === 'youtube-nocookie.com') {
    const v = url.searchParams.get('v');
    if (v) return v;
    const shorts = url.pathname.match(/^\/shorts\/([^/?#]+)/);
    if (shorts?.[1]) return shorts[1];
    const embed = url.pathname.match(/^\/embed\/([^/?#]+)/);
    if (embed?.[1]) return embed[1];
    const live = url.pathname.match(/^\/live\/([^/?#]+)/);
    if (live?.[1]) return live[1];
    return null;
  }
  return null;
}

/**
 * Zet gebruikers-URL om naar iets dat in een iframe mag/kan (waar mogelijk).
 * Blokkeert YouTube/Vimeo zonder concrete video — voorkomt "verbinding geweigerd" op de startpagina.
 */
function resolveEmbeddableShareUrl(raw: string): { ok: true; url: string } | { ok: false; message: string } {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { ok: false, message: 'Ongeldige URL.' };
  }
  const host = url.hostname.replace(/^www\./, '').toLowerCase();

  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtu.be' || host === 'youtube-nocookie.com') {
    const id = extractYoutubeVideoIdFromUrl(url);
    if (id) return { ok: true, url: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` };
    return {
      ok: false,
      message:
        'YouTube: gebruik een videolink (bijv. youtube.com/watch?v=… of youtu.be/…). De startpagina kan niet in de meeting.',
    };
  }

  if (host === 'vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    if (id && /^\d+$/.test(id)) return { ok: true, url: `https://player.vimeo.com/video/${id}?autoplay=1` };
    return { ok: false, message: 'Vimeo: plak een link naar één video (bijv. vimeo.com/123456789).' };
  }

  if (host === 'player.vimeo.com') {
    const m = url.pathname.match(/\/video\/(\d+)/);
    if (m?.[1]) return { ok: true, url: `https://player.vimeo.com/video/${m[1]}?autoplay=1` };
  }

  if (host === 'docs.google.com') {
    const r = raw;
    if (url.pathname.includes('/presentation/')) {
      return { ok: true, url: r.replace(/\/(edit|pub|preview)(.*)?$/, '/embed') };
    }
    if (url.pathname.includes('/document/')) {
      return { ok: true, url: r.replace(/\/(edit|pub|preview)(.*)?$/, '/pub?embedded=true') };
    }
    if (url.pathname.includes('/spreadsheets/')) {
      return { ok: true, url: r.replace(/\/(edit|pub|preview)(.*)?$/, '/pubhtml') };
    }
  }

  if (host === 'figma.com') {
    return { ok: true, url: `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(raw)}` };
  }

  if (host === 'loom.com' && url.pathname.startsWith('/share/')) {
    return { ok: true, url: raw.replace('/share/', '/embed/') };
  }

  return { ok: true, url: raw };
}

/** Klikbare http(s)-links in agendatekst (Firestore sync + overlay zijn los van elkaar). */
function linkifyMeetingText(text: string): React.ReactNode[] {
  const re = /(https?:\/\/[^\s<>"']+)/gi;
  const parts = text.split(re);
  return parts.map((part, i) =>
    /^https?:\/\//i.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noreferrer"
        className="text-sky-300/95 underline underline-offset-2 break-all hover:text-sky-200"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

const COUNTRY_FLAGS: Record<string, string> = {
  'Nederland': '🇳🇱', 'België': '🇧🇪', 'Duitsland': '🇩🇪', 'Frankrijk': '🇫🇷',
  'Verenigd Koninkrijk': '🇬🇧', 'Spanje': '🇪🇸', 'Italië': '🇮🇹', 'Oostenrijk': '🇦🇹',
  'Zwitserland': '🇨🇭', 'Denemarken': '🇩🇰', 'Zweden': '🇸🇪', 'Noorwegen': '🇳🇴',
  'Finland': '🇫🇮', 'Polen': '🇵🇱', 'Tsjechië': '🇨🇿', 'Hongarije': '🇭🇺',
  'Portugal': '🇵🇹', 'Griekenland': '🇬🇷', 'Ierland': '🇮🇪', 'Luxemburg': '🇱🇺',
  'Andere': '🌍',
};

interface NetworkClubMeetingProps {
  roomName: string;
  meetingTitle?: string;
  clubName?: string;
  entityId?: string;
  entityType?: 'networkclub' | 'mastermind' | 'community' | string;
  eventId?: string;
  onLeave: () => void;
  isHost?: boolean;
  onRecordingUploadStatus?: (status: 'uploading' | 'done' | 'error', url?: string) => void;
  presentationColors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  autoOpenPresentation?: boolean;
  clubLogoUrl?: string;
  overlaySettings?: Partial<OverlaySettings>;
  /** Wanneer true rendert de meeting inline (geen fixed inset-0 overlay).
   *  Gebruiker kan zelf via de Fullscreen-knop naar volledig scherm gaan. */
  inlineMode?: boolean;
  /** Leden namen voor sessietijdlijn */
  memberNames?: string[];
  /** Leden Firebase UIDs — zelfde volgorde als memberNames */
  memberUids?: string[];
  /** Aantal leden voor sessietijdlijn */
  memberCount?: number;
  /** Leden avatar URLs voor sessietijdlijn */
  memberAvatars?: (string | null)[];
  /** Extra per-lid metadata (country, timezone) — zelfde volgorde als memberNames */
  memberMeta?: MemberMeta[];
  /** Wanneer true start de countdown overlay automatisch bij mounting (host start meeting) */
  autoStartCountdown?: boolean;
  /** Firestore ID van de club — voor laden van networkClubGuests */
  clubId?: string;
  /** Wanneer true start de meeting direct in fysieke modus — Jitsi initialiseert niet */
  initialPhysical?: boolean;
}

type PresentationSlot = {
  id: string;
  presenterName: string;
  topic: string;
  durationMinutes: number;
  status: 'planned' | 'live' | 'done';
  startedAtMs?: number;
  endedAtMs?: number;
  isGuest?: boolean; // true = guest spotlight slot
};

/** Gedeeld bestand dat leden kunnen uploaden tijdens een meeting (DocumentView) */
interface SharedDoc {
  url: string;
  name: string;
  type: 'image' | 'video' | 'pdf' | 'other';
  uploadedByUid: string;
  uploadedByName: string;
  uploadedByAvatar: string | null;
  storagePath: string;
}

// ── Fase 1 nieuwe types ────────────────────────────────────────────────────

type SlotType = 'intro' | 'opening' | 'pitch' | 'presentation' | 'analysis' | 'closing' | 'pause';

type MediaItem = {
  id: string;
  type: 'image' | 'video' | 'youtube';
  storageUrl: string;
  thumbnailUrl?: string;
  durationSeconds?: number;   // afspeelduur video of weergaveduur foto
  loop?: boolean;              // video loopt terug als pitch langer duurt
  displaySeconds?: number;    // seconden per foto (instelbaar per lid)
  youtubeUrl?: string;
  youtubeVideoId?: string;
  order: number;
};

type AttendanceEntry = {
  userId: string;
  presenterName: string;
  status: 'present' | 'absent';
  markedAtMs: number;
  isGuest?: boolean;
  guestName?: string;
  invitedBy?: string;
};

type SavedLink = {
  id: string;
  url: string;
  label: string;
  addedByName: string;
};

type MeetingSettings = {
  meetingMode: 'online' | 'physical';
  selfStartEnabled: boolean;
  defaultTimerSeconds: number;
  defaultMediaDisplaySeconds: number;
  transitionMusicUrl?: string;
  transitionDurationSeconds?: number;
  transitionMusicVolume?: number;
  updatedAt?: any;
  updatedBy?: string;
};

// ── Uitgebreide LivePitchEntry (Fase 1) ───────────────────────────────────

type LivePitchEntry = {
  userId: string;
  presenterName: string;
  title: string;
  message: string;
  searchQuestion?: string;
  recommendation?: {
    targetName: string;
    reason: string;
    savedToInbox?: boolean;
  };
  // Fase 1 nieuw:
  slotNumber?: number;
  attendance?: 'present' | 'absent' | 'unknown';
  mediaItems?: MediaItem[];
  mediaDisplaySeconds?: number;
  isOrganizerSlot?: boolean;
  slotType?: SlotType;
  selfStartEnabled?: boolean;
  updatedAtMs: number;
  enabled?: boolean;
};

const NetworkClubMeeting: React.FC<NetworkClubMeetingProps> = ({
  roomName,
  meetingTitle,
  clubName,
  entityId,
  entityType,
  eventId,
  onLeave,
  isHost = false,
  onRecordingUploadStatus,
  presentationColors,
  autoOpenPresentation = false,
  clubLogoUrl,
  overlaySettings,
  inlineMode = false,
  memberNames = [],
  memberUids = [],
  memberCount,
  memberAvatars = [],
  memberMeta = [],
  autoStartCountdown = false,
  clubId,
  initialPhysical = false,
}) => {
  const [user] = useAuthState(auth);
  /** Bron van waarheid voor tijdlijn/timer — los van parent `isHost` (voorkomt verkeerde context). */
  const [myEntityMemberRole, setMyEntityMemberRole] = useState<string | null>(null);
  useEffect(() => {
    if (!entityId || !user?.uid) {
      setMyEntityMemberRole(null);
      return;
    }
    const q = query(
      collection(db, 'entityMembers'),
      where('entityId', '==', entityId),
      where('userId', '==', user.uid),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const r = snap.docs[0]?.data()?.role;
        const norm = typeof r === 'string' ? r.toLowerCase().trim() : '';
        setMyEntityMemberRole(norm || null);
      },
      () => setMyEntityMemberRole(null),
    );
    return () => unsub();
  }, [entityId, user?.uid]);
  const canControlSessionTimeline =
    myEntityMemberRole === 'owner' ||
    myEntityMemberRole === 'organizer' ||
    myEntityMemberRole === 'board';

  // ── Structured Growth: haal instelbare sessieblokken op ──────────────────
  const { settings: sgSettings } = useStructuredGrowth(
    entityId || null,
    (entityType as 'mastermind' | 'community' | 'networkclub') ?? 'networkclub',
  );
  const sgBlocks = useMemo(() => {
    const raw = sgSettings?.sessionStructure;
    return raw && raw.length > 0 ? [...raw].sort((a, b) => a.order - b.order) : null;
  }, [sgSettings]);

  const sgSessionFlow = useSessionFlow(
    entityId ?? '',
    roomName ?? '',
    sgBlocks ?? [],
    { canControl: canControlSessionTimeline },
  );

  // ── Presentation timeline: actieve PDF-pagina voor fysieke modus ──────────
  const [pdfTimelineSlots, setPdfTimelineSlots] = useState<import('./networkClubTypes').PresentationSlot[]>([]);
  const [activeSlotIndex, setActiveSlotIndex] = useState(0);

  useEffect(() => {
    if (!entityId) return;
    const unsub = onSnapshot(doc(db, 'meetingPresentationTimelines', entityId), (snap) => {
      if (snap.exists()) {
        const data = snap.data() as { slots?: import('./networkClubTypes').PresentationSlot[] };
        setPdfTimelineSlots(data.slots ?? []);
      }
    });
    return () => unsub();
  }, [entityId]);

  const enabledPresentationSlots = useMemo(
    () => pdfTimelineSlots.filter((s) => s.enabled).sort((a, b) => a.order - b.order),
    [pdfTimelineSlots],
  );
  const activePresSlot = enabledPresentationSlots[activeSlotIndex] ?? null;
  const activePageNumber = activePresSlot?.type === 'pdf_page' ? (activePresSlot.pageNumber ?? 1) : undefined;

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showBeamerPreview, setShowBeamerPreview] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showAgenda, setShowAgenda] = useState(true);
  const [participantCount, setParticipantCount] = useState(1);
  const snapshotParticipantsRef = useRef<{ displayName: string; avatarUrl: string | null }[]>([]);
  // Union van alle unieke deelnemers die ooit aanwezig waren (verdwijnt niet als iemand weggaat)
  const allParticipantsEverRef = useRef<Map<string, { displayName: string; avatarUrl: string | null }>>(new Map());
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const [expandedAgendaItemId, setExpandedAgendaItemId] = useState<string | null>(null);
  const [showAddAgendaItem, setShowAddAgendaItem] = useState(false);
  const [newAgendaItem, setNewAgendaItem] = useState({ title: '', description: '' });
  const [agendaListenerError, setAgendaListenerError] = useState<string | null>(null);
  const [agendaSaveError, setAgendaSaveError] = useState<string | null>(null);
  const [agendaFilterUid, setAgendaFilterUid] = useState<string | null>(null);
  const [editingAgendaItemId, setEditingAgendaItemId] = useState<string | null>(null);
  const [editingAgendaDraft, setEditingAgendaDraft] = useState('');
  const [agendaDeleteUndo, setAgendaDeleteUndo] = useState<{ id: string; title: string } | null>(null);
  const agendaDeleteUndoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Mastermind / Netwerkclub Taken ────────────────────────────────────────
  interface MastermindTask {
    id: string;
    entityId: string;
    entityName?: string;
    title: string;
    ownerUid: string;
    ownerName: string;
    ownerAvatar: string | null;
    status: 'open' | 'done' | 'blocked' | 'carried_over';
    createdInMeeting?: string;
    createdAt: any;
    completedAt?: any;
  }
  const [mastermindTasks, setMastermindTasks] = useState<MastermindTask[]>([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskAssigneeUid, setNewTaskAssigneeUid] = useState<string>('self');
  const [taskFeedback, setTaskFeedback] = useState<string | null>(null);
  const [taskCardUser, setTaskCardUser] = useState<string | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingTaskTitle, setEditingTaskTitle] = useState('');
  const [editingTaskAssigneeUid, setEditingTaskAssigneeUid] = useState<string>('self');
  // Collapsible sidebar sections
  /** Keys in set = ingeklapt. Bij openen Jitsi alles dicht. */
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () =>
      new Set([
        'timeline',
        'commitments',
        'slideshow',
        'gedeeldScherm',
        'website',
        'guestSpotlight',
        'agenda',
        'taken',
      ]),
  );
  const toggleSection = (key: string) => setCollapsedSections(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  // ─────────────────────────────────────────────────────────────────────────
  const [presentationSlots, setPresentationSlots] = useState<PresentationSlot[]>([]);
  const [presenterNameInput, setPresenterNameInput] = useState('');

  // Auto-open presentation overlay when requested from parent
  const [showLivePitch, setShowLivePitch] = useState(autoOpenPresentation);
  const enableLivePresentation = entityType ? entityType === 'networkclub' : true;
  const [presentationTopicInput, setPresentationTopicInput] = useState('');
  const [presentationMinutesInput, setPresentationMinutesInput] = useState('10');
  const [presentationTick, setPresentationTick] = useState(Date.now());
  const [livePitchEntries, setLivePitchEntries] = useState<LivePitchEntry[]>([]);

  // Hard feature gate: live pitch presentation is only for networkclubs.
  useEffect(() => {
    if (!enableLivePresentation && showLivePitch) {
      setShowLivePitch(false);
    }
  }, [enableLivePresentation, showLivePitch]);
  const [livePitchCurrentUserTitle, setLivePitchCurrentUserTitle] = useState('');
  const [livePitchCurrentUserMessage, setLivePitchCurrentUserMessage] = useState('');
  const [livePitchCurrentUserSearchQ, setLivePitchCurrentUserSearchQ] = useState('');
  const [livePitchCurrentUserRecTarget, setLivePitchCurrentUserRecTarget] = useState('');
  const [livePitchCurrentUserRecReason, setLivePitchCurrentUserRecReason] = useState('');
  const [recSavedFeedback, setRecSavedFeedback] = useState(false);

  // ── Fase 1: Aanwezigheid ─────────────────────────────────────────────────
  const [attendanceEntries, setAttendanceEntries] = useState<AttendanceEntry[]>([]);
  const [myAttendance, setMyAttendance] = useState<'present' | 'absent' | null>(null);
  // Pre-event registered guests (from networkClubGuests collection)
  const [preEventGuests, setPreEventGuests] = useState<{ id: string; name: string; company?: string; status: string }[]>([]);

  // ── Fase 1: Meeting-instellingen (modus online/fysiek) ───────────────────
  const [meetingMode, setMeetingMode] = useState<'online' | 'physical'>(initialPhysical ? 'physical' : 'online');
  /** Tijdens fysieke start: negeer oude Firestore-cache met meetingMode online (anders Jitsi). */
  const skipCachedOnlineOverPhysicalRef = useRef(!!initialPhysical);
  useLayoutEffect(() => {
    skipCachedOnlineOverPhysicalRef.current = !!initialPhysical;
  }, [initialPhysical]);
  /** PDF-template uit meetingSettings — eerste pagina op beamer als fallback-achtergrond */
  const [templatePdfUrl, setTemplatePdfUrl] = useState<string | undefined>(undefined);
  /** Pitch-template: 1-page achtergrond PDF + zones per veld */
  const [pitchTemplatePdfUrl, setPitchTemplatePdfUrl] = useState<string | undefined>(undefined);
  const [pitchTemplateZones, setPitchTemplateZones] = useState<import('./networkClubTypes').TemplateZone[]>([]);
  const [selfStartEnabled, setSelfStartEnabled] = useState(false);
  const [defaultMediaDisplaySeconds, setDefaultMediaDisplaySeconds] = useState(5);
  /** Sidebar zichtbaar voor alle deelnemers (online + fysiek). Host-only controls binnen de sidebar zijn apart afgeschermd. */
  const canUseMeetingHostSidebar = true;

  // ── Fase 1: Foto's (legacy inline upload — replaced by usePitchMedia below) ──
  const [carouselIndex, setCarouselIndex] = useState(0);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // ── Fase 2: Media hook (foto + video upload met progress) ────────────────
  const pitchMedia = usePitchMedia({
    entityId: entityId ?? '',
    roomName,
    userId: user?.uid ?? '',
  });
  // Alias for backwards compat with Fase 1 inline references
  const pitchMediaItems = pitchMedia.mediaItems;

  // ── Fase 2: Rapport hook ─────────────────────────────────────────────────
  const { rapportState, generate: generateRapport, reset: resetRapport } = useMeetingRapport();

  // ── Fase 2: Countdown + selfstart UI state ────────────────────────────────
  // Start countdown automatically when host launches meeting from setup panel
  const [showCountdown, setShowCountdown] = useState(autoStartCountdown && isHost);
  /** Sync uit Firestore — voor “Meekijken” als overlay even weg is */
  const [countdownRunningRemote, setCountdownRunningRemote] = useState(false);
  const prevCountdownRunningRef = useRef<boolean | null>(null);
  // autoStartCountdown only opens the overlay for the host (idle state).
  // The host then configures and presses "Start Countdown" manually — that writes to Firestore
  // so all members also see the running countdown with the correct startedAtMs timestamp.
  // We intentionally do NOT write countdownRunning:true here to avoid stale timestamps.

  const [countdownMinutes, setCountdownMinutes] = useState(4);
  const [countdownMusicUrl, setCountdownMusicUrl] = useState<string | null>(null);
  const [countdownVideoUrl, setCountdownVideoUrl] = useState<string | null>(null);
  const [countdownImageUrl, setCountdownImageUrl] = useState<string | null>(null);
  const [showRapportPanel, setShowRapportPanel] = useState(false);

  // ── Fase 2.5: Overgangs-instellingen ─────────────────────────────────────
  const [transitionMusicUrl, setTransitionMusicUrl] = useState<string | null>(null);
  const [transitionDurationSeconds, setTransitionDurationSeconds] = useState<number>(TRANSITION_DEFAULT_SECONDS);
  const [transitionMusicVolume, setTransitionMusicVolume] = useState<number>(0.5);
  const [transitionActive, setTransitionActive] = useState(false);
  const [transitionNextEntry, setTransitionNextEntry] = useState<LivePitchEntry | null>(null);
  const [uploadingTransitionMusic, setUploadingTransitionMusic] = useState(false);
  const transitionMusicInputRef = useRef<HTMLInputElement>(null);

  // ── Fase 2.5: YouTube URL invoer in pitch-formulier ──────────────────────
  const [youtubeInputUrl, setYoutubeInputUrl] = useState('');
  const [youtubeInputLoop, setYoutubeInputLoop] = useState(false);
  const [youtubeInputError, setYoutubeInputError] = useState<string | null>(null);

  // ── Fase 2.5: Foto-weergave seconden (per lid) ────────────────────────────
  const [myMediaDisplaySeconds, setMyMediaDisplaySeconds] = useState<number>(5);

  // ── Fase 3: UI state ─────────────────────────────────────────────────────
  const [showQrCode, setShowQrCode] = useState(false);
  const [showAttendanceHistory, setShowAttendanceHistory] = useState(false);

  const [livePitchRunningUserId, setLivePitchRunningUserId] = useState<string | null>(null);
  const [livePitchStartedAtMs, setLivePitchStartedAtMs] = useState<number | null>(null);
  const [livePitchNowMs, setLivePitchNowMs] = useState<number>(Date.now());
  const [livePitchTimerDurationSeconds, setLivePitchTimerDurationSeconds] = useState<number>(60);
  const apiRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  /** Eén automatische fullscreen-poging per fysieke sessie (host + beamer) */
  const physicalFullscreenRequestedRef = useRef<string | null>(null);

  // Join/leave toast notifications
  const [joinToasts, setJoinToasts] = useState<{ id: string; name: string; avatar?: string; ts: number }[]>([]);

  // Live participants list (reactive mirror of snapshotParticipantsRef)
  const [liveParticipants, setLiveParticipants] = useState<{ displayName: string; avatarUrl: string | null }[]>([]);

  // Firestore chat
  const [chatMessages, setChatMessages] = useState<{ id: string; uid: string; name: string; avatar: string | null; text: string; imageUrl?: string; fileUrl?: string; fileName?: string; ts: number }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const chatFileRef = useRef<HTMLInputElement | null>(null);
  const [chatUploading, setChatUploading] = useState(false);

  // Lobby / knock requests
  const [knockRequests, setKnockRequests] = useState<{ id: string; name: string; avatar?: string }[]>([]);

  // Slideshow
  const [slides, setSlides] = useState<{ url: string; name: string; type: 'image' | 'video'; durationSeconds?: number; caption?: string }[]>([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [slideshowUploading, setSlideshowUploading] = useState(false);
  const [slideAutoPlay, setSlideAutoPlay] = useState(false);
  const [slideDefaultDuration, setSlideDefaultDuration] = useState(8); // seconds per slide
  const [expandedSlideIdx, setExpandedSlideIdx] = useState<number | null>(null);
  const slideAutoPlayRef = useRef(false);
  const slideFileRef = useRef<HTMLInputElement | null>(null);

  // ── Feature toggles (synced via meetingSettings) ─────────────────────────
  const [docViewEnabled, setDocViewEnabled] = useState(false);
  const [memberUploadEnabled, setMemberUploadEnabled] = useState(false); // host geeft leden toestemming te uploaden
  const [linkOpenerEnabled, setLinkOpenerEnabled] = useState(true);
  const [guestSpotlightEnabled, setGuestSpotlightEnabled] = useState(true);
  const [commitmentReviewEnabled, setCommitmentReviewEnabled] = useState(true);
  const [slideshowEnabled, setSlideshowEnabled] = useState(true);
  const [taskReminderEnabled, setTaskReminderEnabled] = useState(false);
  const [timerOverlayEnabled, setTimerOverlayEnabled] = useState(true);
  const [autoRecordEnabled, setAutoRecordEnabled] = useState(false);
  const autoRecordEnabledRef = useRef(false);
  const hasAutoMarkedAttendanceRef = useRef(false);
  const isHostRef = useRef(isHost);
  isHostRef.current = isHost; // sync elke render zodat event-listeners altijd actueel zijn

  // DocumentView — gedeeld scherm: leden uploaden bestanden als overlay
  const [sharedDocs, setSharedDocs] = useState<SharedDoc[]>([]);
  const [activeDocIdx, setActiveDocIdx] = useState(0);
  const [showDocView, setShowDocView] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const docFileRef = useRef<HTMLInputElement | null>(null);
  // Website URL sharing
  const [sharedUrl, setSharedUrl] = useState<string | null>(null);
  const [showUrlView, setShowUrlView] = useState(false);
  const [sharedUrlInput, setSharedUrlInput] = useState('');
  const [urlShareError, setUrlShareError] = useState<string | null>(null);
  const [urlFrameState, setUrlFrameState] = useState<'loading' | 'loaded' | 'blocked'>('loading');
  // Floating quick-share panel (visible to all when docViewEnabled)
  const [showSharePanel, setShowSharePanel] = useState(false);
  const sharePanelUrlRef = useRef('');
  // Opgeslagen links (max 3) — gedeeld via meetingSharedDocs
  const [savedLinks, setSavedLinks] = useState<SavedLink[]>([]);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [showAddLink, setShowAddLink] = useState(false);
  // Attendance popover (host)
  const [showAttendancePopover, setShowAttendancePopover] = useState(false);
  const attendancePopoverRef = useRef<HTMLDivElement | null>(null);

  // Recording state
  const [showRecordingPrompt, setShowRecordingPrompt] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const setRecording = useCallback((v: boolean) => {
    isRecordingRef.current = v;
    setIsRecording(v);
  }, []);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [uploadProgress, setUploadProgress] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const uploadProgressRef = useRef<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const isRecordingRef = useRef(false);
  const setUpload = useCallback((v: 'idle' | 'uploading' | 'done' | 'error') => {
    uploadProgressRef.current = v;
    setUploadProgress(v);
  }, []);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const audioRecorderRef = useRef<MediaRecorder | null>(null); // audio-only recorder for Whisper
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordedBlobRef = useRef<Blob | null>(null);
  const recordingSecondsRef = useRef(0); // ref to track current duration (avoids closure bug)

  // Generate display name
  const displayName = user?.displayName || user?.email?.split('@')[0] || 'Gast';
  const livePitchTheme = {
    primary: presentationColors?.primary || THEME_DEFAULTS.primary,
    secondary: presentationColors?.secondary || THEME_DEFAULTS.secondary,
    accent: presentationColors?.accent || THEME_DEFAULTS.accent,
  };

  // Handle Jitsi API ready
  const handleApiReady = (api: any) => {
    apiRef.current = api;
    
    // Voeg lokale gebruiker toe aan de union bij start
    const localKey = user?.uid || 'local';
    allParticipantsEverRef.current.set(localKey, {
      displayName: user?.displayName || user?.email?.split('@')[0] || 'Jij',
      avatarUrl: user?.photoURL ?? null,
    });

    const updateParticipants = () => {
      const info = api.getParticipantsInfo() as { participantId?: string; displayName: string; avatarURL?: string }[];

      // Haal lokale participant-ID op om dubbele toevoeging te voorkomen
      const myId = (api.getMyParticipantId?.() as string | undefined) ?? null;

      // Filter lokale gebruiker én Jitsi-serverdeelnemers (focus/jicofo) uit de lijst
      const remoteInfo = info.filter((p) => {
        if (myId && p.participantId === myId) return false;
        const name = (p.displayName ?? '').toLowerCase().trim();
        if (name === 'focus' || name === 'jicofo' || name === '') return false;
        return true;
      });

      setParticipantCount(remoteInfo.length + 1);

      const localEntry = {
        displayName: user?.displayName || user?.email?.split('@')[0] || 'Jij',
        avatarUrl: user?.photoURL ?? null,
      };

      const remoteEntries = remoteInfo.map((p) => ({
        displayName: p.displayName || 'Deelnemer',
        avatarUrl: p.avatarURL ?? null,
      }));

      const list = [localEntry, ...remoteEntries];

      // Update union — voeg toe, verwijder nooit
      remoteEntries.forEach((e) => {
        const key = e.displayName;
        if (key && !allParticipantsEverRef.current.has(key)) {
          allParticipantsEverRef.current.set(key, e);
        }
      });

      // snapshot = volledige union (iedereen die ooit aanwezig was)
      snapshotParticipantsRef.current = Array.from(allParticipantsEverRef.current.values());
      setLiveParticipants(list);
    };

    api.addListener('participantJoined', (p: any) => {
      updateParticipants();
      const name = p?.displayName || 'Deelnemer';
      const avatar = p?.avatarURL || undefined;
      const id = `jt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setJoinToasts((prev) => [...prev, { id, name, avatar, ts: Date.now() }]);
      setTimeout(() => setJoinToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
    });
    api.addListener('participantLeft', updateParticipants);
    updateParticipants();

    // Lobby knock events (when lobby is enabled)
    api.addListener('knockingParticipant', (p: any) => {
      const kId = p?.participant?.id || `k-${Date.now()}`;
      const kName = p?.participant?.name || 'Onbekend';
      setKnockRequests((prev) => prev.some((r) => r.id === kId) ? prev : [...prev, { id: kId, name: kName }]);
    });

    api.addListener('videoConferenceLeft', async () => {
      console.log('📞 Meeting ended');
      try { if (document.fullscreenElement) await document.exitFullscreen(); } catch {}
      onLeave();
    });

    api.addListener('videoConferenceError', (error: any) => {
      console.error('❌ Meeting error:', error);
    });

    // Auto-opname + auto-aanwezigheid zodra de conference klaar is
    api.addListener('videoConferenceJoined', () => {
      // Toon opname-prompt altijd voor de host (ongeacht instelling) — vereist 1 klik door browser
      if (isHostRef.current && !isRecordingRef.current) {
        setTimeout(() => setShowRecordingPrompt(true), 1500);
      }
      // Automatisch aanwezigheid registreren bij betreden videocall
      if (!hasAutoMarkedAttendanceRef.current) {
        hasAutoMarkedAttendanceRef.current = true;
        setTimeout(() => void markAttendance('present'), 800);
      }
    });
  };

  // ── Firestore chat listener ──────────────────────────────────────────────
  useEffect(() => {
    if (!roomName) return;
    const q = query(
      collection(db, 'meetingChat', roomName, 'messages'),
      orderBy('createdAt', 'asc'),
      fbLimit(200),
    );
    const unsub = onSnapshot(q, (snap) => {
      setChatMessages(snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          uid: data.uid ?? '',
          name: data.name ?? 'Lid',
          avatar: data.avatar ?? null,
          text: data.text ?? '',
          imageUrl: data.imageUrl,
          fileUrl: data.fileUrl,
          fileName: data.fileName,
          ts: data.createdAt?.toMillis?.() ?? Date.now(),
        };
      }));
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });
    return () => unsub();
  }, [roomName]);

  const sendChatMessage = useCallback(async (text: string, imageUrl?: string, fileUrl?: string, fileName?: string) => {
    if ((!text.trim() && !imageUrl && !fileUrl) || !user) return;
    setChatSending(true);
    try {
      await addDoc(collection(db, 'meetingChat', roomName, 'messages'), {
        uid: user.uid,
        name: user.displayName || user.email || 'Lid',
        avatar: user.photoURL || null,
        text: text.trim(),
        ...(imageUrl ? { imageUrl } : {}),
        ...(fileUrl ? { fileUrl, fileName: fileName || 'bestand' } : {}),
        createdAt: serverTimestamp(),
      });
      setChatInput('');
    } catch (err) {
      console.error('Chat send failed:', err);
    } finally {
      setChatSending(false);
    }
  }, [roomName, user]);

  const handleChatFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setChatUploading(true);
    try {
      const storageRef = ref(storage, `meetingChat/${roomName}/${Date.now()}-${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const isImage = file.type.startsWith('image/');
      if (isImage) {
        await sendChatMessage('', url);
      } else {
        await sendChatMessage('', undefined, url, file.name);
      }
    } catch (err) {
      console.error('Chat file upload failed:', err);
    } finally {
      setChatUploading(false);
      if (chatFileRef.current) chatFileRef.current.value = '';
    }
  }, [roomName, user, sendChatMessage]);

  // ── Firestore slideshow sync ──────────────────────────────────────────────
  useEffect(() => {
    if (!roomName) return;
    const unsub = onSnapshot(doc(db, 'meetingSlides', roomName), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (Array.isArray(data.slides)) setSlides(data.slides);
      if (typeof data.currentIndex === 'number') setSlideIndex(data.currentIndex);
      if (typeof data.active === 'boolean') setShowSlideshow(data.active);
      if (typeof data.autoPlay === 'boolean') { setSlideAutoPlay(data.autoPlay); slideAutoPlayRef.current = data.autoPlay; }
      if (typeof data.defaultDuration === 'number') setSlideDefaultDuration(data.defaultDuration);
    });
    return () => unsub();
  }, [roomName]);

  // ── Firestore DocumentView (Gedeeld Scherm) sync ─────────────────────────
  useEffect(() => {
    if (!roomName) return;
    const unsub = onSnapshot(doc(db, 'meetingSharedDocs', roomName), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (Array.isArray(data.docs)) setSharedDocs(data.docs);
      if (typeof data.activeIdx === 'number') setActiveDocIdx(data.activeIdx);
      if (typeof data.active === 'boolean') setShowDocView(data.active);
      // Website URL sharing
      if (typeof data.sharedUrl === 'string') setSharedUrl(data.sharedUrl || null);
      else if (data.sharedUrl === null) setSharedUrl(null);
      if (typeof data.urlActive === 'boolean') setShowUrlView(data.urlActive);
      // Opgeslagen links
      if (Array.isArray(data.savedLinks)) setSavedLinks(data.savedLinks);
    });
    return () => unsub();
  }, [roomName]);

  // Gedeelde URL: altijd loading + fallback-timeout (één effect i.v.m. effect-volgorde / stale state)
  useEffect(() => {
    if (!showUrlView || !sharedUrl) return;
    setUrlFrameState('loading');
    const t = window.setTimeout(() => {
      setUrlFrameState((s) => (s === 'loading' ? 'loaded' : s));
    }, 12000);
    return () => clearTimeout(t);
  }, [sharedUrl, showUrlView]);

  // Auto-open share panel for ALL members when docViewEnabled is switched on
  const prevDocViewEnabledRef = useRef(false);
  useEffect(() => {
    if (docViewEnabled && !prevDocViewEnabledRef.current) {
      setShowSharePanel(true); // pop up the quick-share panel for everyone
    }
    prevDocViewEnabledRef.current = docViewEnabled;
  }, [docViewEnabled]);

  // Switch Jitsi to tile view when camera PiP is active (content overlay covering main area)
  useEffect(() => {
    const pipActive = (showDocView && docViewEnabled && sharedDocs.length > 0)
      || (slideshowEnabled && showSlideshow && slides.length > 0)
      || (showUrlView && !!sharedUrl);
    try {
      (apiRef.current as any)?.executeCommand?.('setTileView', pipActive);
    } catch { /* API not yet ready */ }
  }, [showDocView, docViewEnabled, sharedDocs.length, slideshowEnabled, showSlideshow, slides.length, showUrlView, sharedUrl]);

  const handleSlideUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setSlideshowUploading(true);
    try {
      const newSlides: { url: string; name: string; type: 'image' | 'video' }[] = [...slides];
      for (const file of Array.from(files)) {
        const storageRef = ref(storage, `meetingSlides/${roomName}/${Date.now()}-${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        const type = file.type.startsWith('video/') ? 'video' as const : 'image' as const;
        newSlides.push({ url, name: file.name, type });
      }
      setSlides(newSlides);
      await setDoc(doc(db, 'meetingSlides', roomName), { slides: newSlides, currentIndex: slideIndex, active: showSlideshow }, { merge: true });
    } catch (err) {
      console.error('Slide upload failed:', err);
    } finally {
      setSlideshowUploading(false);
      if (slideFileRef.current) slideFileRef.current.value = '';
    }
  }, [roomName, slides, slideIndex, showSlideshow]);

  const setRemoteSlideIndex = useCallback(async (idx: number) => {
    setSlideIndex(idx);
    await setDoc(doc(db, 'meetingSlides', roomName), { currentIndex: idx }, { merge: true }).catch(() => {});
  }, [roomName]);

  const toggleSlideshowActive = useCallback(async (active: boolean) => {
    setShowSlideshow(active);
    await setDoc(doc(db, 'meetingSlides', roomName), { active, slides, currentIndex: slideIndex }, { merge: true }).catch(() => {});
  }, [roomName, slides, slideIndex]);

  const updateSlideSettings = useCallback(async (idx: number, patch: { durationSeconds?: number; caption?: string }) => {
    const updated = slides.map((s, i) => i === idx ? { ...s, ...patch } : s);
    setSlides(updated);
    await setDoc(doc(db, 'meetingSlides', roomName), { slides: updated }, { merge: true }).catch(() => {});
  }, [roomName, slides]);

  const moveSlide = useCallback(async (from: number, to: number) => {
    if (to < 0 || to >= slides.length) return;
    const updated = [...slides];
    const [item] = updated.splice(from, 1);
    updated.splice(to, 0, item);
    setSlides(updated);
    const newIdx = from === slideIndex ? to : slideIndex;
    setSlideIndex(newIdx);
    await setDoc(doc(db, 'meetingSlides', roomName), { slides: updated, currentIndex: newIdx }, { merge: true }).catch(() => {});
  }, [roomName, slides, slideIndex]);

  const updateSlideshowGlobal = useCallback(async (patch: { autoPlay?: boolean; defaultDuration?: number }) => {
    if (patch.autoPlay !== undefined) { setSlideAutoPlay(patch.autoPlay); slideAutoPlayRef.current = patch.autoPlay; }
    if (patch.defaultDuration !== undefined) setSlideDefaultDuration(patch.defaultDuration);
    await setDoc(doc(db, 'meetingSlides', roomName), patch, { merge: true }).catch(() => {});
  }, [roomName]);

  const removeSlide = useCallback(async (idx: number) => {
    const updated = slides.filter((_, i) => i !== idx);
    setSlides(updated);
    const newIdx = Math.min(slideIndex, Math.max(0, updated.length - 1));
    setSlideIndex(newIdx);
    await setDoc(doc(db, 'meetingSlides', roomName), { slides: updated, currentIndex: newIdx }, { merge: true }).catch(() => {});
  }, [roomName, slides, slideIndex]);

  // ── DocumentView functions ────────────────────────────────────────────────

  const [docUploadError, setDocUploadError] = useState<string | null>(null);

  const handleDocUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setDocUploading(true);
    setDocUploadError(null);
    try {
      const newDocs: SharedDoc[] = [...sharedDocs];
      for (const file of Array.from(files)) {
        console.log(`[DocView] Uploaden: ${file.name} (${(file.size / 1024).toFixed(0)} KB)`);
        const storagePath = `meetingSharedDocs/${roomName}/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        console.log(`[DocView] Upload gelukt: ${url}`);
        let type: SharedDoc['type'] = 'other';
        if (file.type.startsWith('image/')) type = 'image';
        else if (file.type.startsWith('video/')) type = 'video';
        else if (file.type === 'application/pdf') type = 'pdf';
        newDocs.push({
          url, name: file.name, type,
          uploadedByUid: user?.uid ?? '',
          uploadedByName: displayName,
          uploadedByAvatar: user?.photoURL ?? null,
          storagePath,
        });
      }
      setSharedDocs(newDocs);
      await setDoc(doc(db, 'meetingSharedDocs', roomName), {
        docs: newDocs, activeIdx: activeDocIdx, active: showDocView,
      }, { merge: true });
      console.log(`[DocView] Firestore bijgewerkt — ${newDocs.length} bestand(en)`);
    } catch (err: any) {
      console.error('[DocView] Upload mislukt:', err);
      setDocUploadError(err?.code === 'storage/unauthorized'
        ? 'Geen toegang tot opslag. Vraag de beheerder om storage-regels te deployen.'
        : `Upload mislukt: ${err?.message ?? 'onbekende fout'}`);
    } finally {
      setDocUploading(false);
      if (docFileRef.current) docFileRef.current.value = '';
    }
  }, [roomName, sharedDocs, activeDocIdx, showDocView, user, displayName]);

  const toggleDocViewActive = useCallback(async (active: boolean, idx?: number) => {
    const newIdx = idx ?? activeDocIdx;
    setShowDocView(active);
    if (idx !== undefined) setActiveDocIdx(newIdx);
    await setDoc(doc(db, 'meetingSharedDocs', roomName), { active, activeIdx: newIdx }, { merge: true }).catch(() => {});
  }, [roomName, activeDocIdx]);

  const deleteSharedDoc = useCallback(async (idx: number) => {
    const d = sharedDocs[idx];
    if (d?.storagePath) {
      try { await deleteObject(ref(storage, d.storagePath)); } catch { /* already gone */ }
    }
    const newDocs = sharedDocs.filter((_, i) => i !== idx);
    setSharedDocs(newDocs);
    const newIdx = Math.min(activeDocIdx, Math.max(0, newDocs.length - 1));
    setActiveDocIdx(newIdx);
    await setDoc(doc(db, 'meetingSharedDocs', roomName), { docs: newDocs, activeIdx: newIdx }, { merge: true }).catch(() => {});
  }, [roomName, sharedDocs, activeDocIdx]);

  const toggleDocViewEnabled = useCallback(async (enabled: boolean) => {
    setDocViewEnabled(enabled);
    await setDoc(doc(db, 'meetingSettings', entityId ?? roomName), { docViewEnabled: enabled }, { merge: true }).catch(() => {});
  }, [entityId, roomName]);

  const toggleMemberUploadEnabled = useCallback(async (enabled: boolean) => {
    setMemberUploadEnabled(enabled);
    await setDoc(doc(db, 'meetingSettings', entityId ?? roomName), { memberUploadEnabled: enabled }, { merge: true }).catch(() => {});
  }, [entityId, roomName]);

  // ── Website URL sharing ───────────────────────────────────────────────────

  const pushUrlLive = useCallback(async (raw: string) => {
    if (!raw.trim()) return;
    const normalized = raw.trim().startsWith('http://') || raw.trim().startsWith('https://')
      ? raw.trim() : `https://${raw.trim()}`;
    const resolved = resolveEmbeddableShareUrl(normalized);
    if (!resolved.ok) {
      setUrlShareError(resolved.message);
      return;
    }
    setUrlShareError(null);
    const embedUrl = resolved.url;
    setUrlFrameState('loading');
    setSharedUrl(embedUrl);
    setShowUrlView(true);
    setShowDocView(false);
    await setDoc(doc(db, 'meetingSharedDocs', roomName), {
      sharedUrl: embedUrl, urlActive: true, active: false,
    }, { merge: true }).catch(() => {});
  }, [roomName]);

  const closeUrlView = useCallback(async () => {
    setShowUrlView(false);
    await setDoc(doc(db, 'meetingSharedDocs', roomName), { urlActive: false }, { merge: true }).catch(() => {});
  }, [roomName]);

  const clearSharedUrl = useCallback(async () => {
    setSharedUrl(null);
    setShowUrlView(false);
    setSharedUrlInput('');
    setUrlShareError(null);
    await setDoc(doc(db, 'meetingSharedDocs', roomName), { sharedUrl: null, urlActive: false }, { merge: true }).catch(() => {});
  }, [roomName]);

  // ── Opgeslagen links (max 3) ──────────────────────────────────────────────
  const handleAddSavedLink = useCallback(async () => {
    const raw = newLinkUrl.trim();
    if (!raw || savedLinks.length >= 3) return;

    // Prefix https:// if no protocol
    let finalUrl = raw;
    if (!/^https?:\/\//i.test(finalUrl)) finalUrl = `https://${finalUrl}`;

    // Validate URL before saving
    let hostname = '';
    try {
      hostname = new URL(finalUrl).hostname;
    } catch {
      setUrlShareError('Ongeldige URL. Controleer het adres en probeer opnieuw.');
      return;
    }
    if (!hostname) {
      setUrlShareError('Ongeldige URL — geen geldig domein gevonden.');
      return;
    }

    const label = newLinkLabel.trim() || hostname.replace('www.', '');
    const newLink: SavedLink = { id: `${Date.now()}`, url: finalUrl, label, addedByName: displayName };
    const updated = [...savedLinks, newLink];
    setSavedLinks(updated);
    setNewLinkUrl('');
    setNewLinkLabel('');
    setShowAddLink(false);
    setUrlShareError(null);
    await setDoc(doc(db, 'meetingSharedDocs', roomName), { savedLinks: updated }, { merge: true }).catch(() => {});
  }, [newLinkUrl, newLinkLabel, savedLinks, displayName, roomName]);

  const handleRemoveSavedLink = useCallback(async (id: string) => {
    const link = savedLinks.find(l => l.id === id);
    const updated = savedLinks.filter(l => l.id !== id);
    setSavedLinks(updated);
    // Sluit de live view als de verwijderde link momenteel live staat
    const isCurrentlyLive = link && sharedUrl === link.url && showUrlView;
    if (isCurrentlyLive) {
      setShowUrlView(false);
      setSharedUrl(null);
      await setDoc(doc(db, 'meetingSharedDocs', roomName), { savedLinks: updated, sharedUrl: null, urlActive: false }, { merge: true }).catch(() => {});
    } else {
      await setDoc(doc(db, 'meetingSharedDocs', roomName), { savedLinks: updated }, { merge: true }).catch(() => {});
    }
  }, [savedLinks, sharedUrl, showUrlView, roomName]);

  // Lobby admit/reject helpers
  const admitKnock = useCallback((participantId: string) => {
    apiRef.current?.executeCommand('answerKnockingParticipant', participantId, true);
    setKnockRequests((prev) => prev.filter((r) => r.id !== participantId));
  }, []);

  const rejectKnock = useCallback((participantId: string) => {
    apiRef.current?.executeCommand('answerKnockingParticipant', participantId, false);
    setKnockRequests((prev) => prev.filter((r) => r.id !== participantId));
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Control functions
  const toggleMute = () => {
    if (apiRef.current) {
      apiRef.current.executeCommand('toggleAudio');
    }
  };

  const toggleVideo = () => {
    if (apiRef.current) {
      apiRef.current.executeCommand('toggleVideo');
    }
  };

  const startScreenShare = () => {
    if (apiRef.current) {
      apiRef.current.executeCommand('toggleShareScreen');
    }
  };

  const startPresentationMode = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().catch(() => {});
    }
  };

  const livePitchDocId = entityId ? `${entityId}_${roomName}` : null;

  const saveLivePitchDoc = useCallback(async (payload: any) => {
    if (!livePitchDocId || !entityId) return;
    await setDoc(
      doc(db, 'meetingLivePitches', livePitchDocId),
      {
        entityId,
        roomName,
        ...payload,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }, [entityId, livePitchDocId, roomName]);

  useEffect(() => {
    if (!livePitchDocId) return;
    const unsub = onSnapshot(doc(db, 'meetingLivePitches', livePitchDocId), (snap) => {
      if (!snap.exists()) {
        setLivePitchEntries([]);
        setLivePitchRunningUserId(null);
        setLivePitchStartedAtMs(null);
        return;
      }
      const data: any = snap.data();
      setLivePitchEntries(Array.isArray(data?.entries) ? data.entries : []);
      setLivePitchRunningUserId(typeof data?.runningUserId === 'string' ? data.runningUserId : null);
      setLivePitchStartedAtMs(typeof data?.startedAtMs === 'number' ? data.startedAtMs : null);
      setLivePitchTimerDurationSeconds(typeof data?.timerDurationSeconds === 'number' ? data.timerDurationSeconds : 60);
    });
    return () => unsub();
  }, [livePitchDocId]);

  useEffect(() => {
    const mine = livePitchEntries.find((entry) => entry.userId === user?.uid);
    if (!mine) return;
    setLivePitchCurrentUserTitle(mine.title || '');
    setLivePitchCurrentUserMessage(mine.message || '');
    setLivePitchCurrentUserSearchQ(mine.searchQuestion || '');
    setLivePitchCurrentUserRecTarget(mine.recommendation?.targetName || '');
    setLivePitchCurrentUserRecReason(mine.recommendation?.reason || '');
    if (typeof mine.mediaDisplaySeconds === 'number') setMyMediaDisplaySeconds(mine.mediaDisplaySeconds);
    if (Array.isArray(mine.mediaItems)) pitchMedia.hydrateFromEntry(mine.mediaItems as MediaItem[]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePitchEntries, user?.uid]);

  // ── Auto-laden pitch-draft bij meeting-start ─────────────────────────────
  // Laad opgeslagen "Mijn pitch" tab-data als het lid nog geen entry in de livePitch heeft
  useEffect(() => {
    if (!user?.uid || !entityId) return;
    const alreadyHasEntry = livePitchEntries.some(e => e.userId === user.uid);
    if (alreadyHasEntry) return; // Niet overschrijven als al aanwezig

    let cancelled = false;
    (async () => {
      try {
        const { doc: fsDoc, getDoc: fsGetDoc } = await import('firebase/firestore');
        const { db: fsDb } = await import('../../firebase');
        const snap = await fsGetDoc(fsDoc(fsDb, 'meetingPitchDrafts', `${entityId}_${user.uid}`));
        if (cancelled || !snap.exists()) return;
        const d = snap.data() as any;
        if (d.title || d.message || d.searchQuestion) {
          setLivePitchCurrentUserTitle(d.title || '');
          setLivePitchCurrentUserMessage(d.message || '');
          setLivePitchCurrentUserSearchQ(d.searchQuestion || '');
          setLivePitchCurrentUserRecTarget(d.recTarget || '');
          setLivePitchCurrentUserRecReason(d.recReason || '');
        }
      } catch { /* stil */ }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, user?.uid]);

  // ── Fase 1: Luister naar aanwezigheid ────────────────────────────────────
  useEffect(() => {
    if (!entityId) return;
    const attendanceDocId = `${entityId}_${roomName}`;
    const unsub = onSnapshot(doc(db, 'meetingAttendance', attendanceDocId), (snap) => {
      if (!snap.exists()) { setAttendanceEntries([]); return; }
      const data: any = snap.data();
      const entries: AttendanceEntry[] = Array.isArray(data?.entries) ? data.entries : [];
      setAttendanceEntries(entries);
      // Eigen status bijhouden
      const mine = entries.find((e) => e.userId === user?.uid);
      if (mine) setMyAttendance(mine.status);
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, roomName]);

  // ── Auto-aanwezigheid bij fysieke meeting (geen Jitsi join-event) ────────
  useEffect(() => {
    if (meetingMode !== 'physical' || !user?.uid || !entityId) return;
    if (hasAutoMarkedAttendanceRef.current) return;
    hasAutoMarkedAttendanceRef.current = true;
    const t = setTimeout(() => void markAttendance('present'), 1200);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingMode, user?.uid, entityId]);

  // ── Pre-event gasten uit networkClubGuests ───────────────────────────────
  useEffect(() => {
    if (!clubId && !entityId) return;
    const q = clubId
      ? query(collection(db, 'networkClubGuests'), where('clubId', '==', clubId))
      : query(collection(db, 'networkClubGuests'), where('entityId', '==', entityId));
    const unsub = onSnapshot(q, (snap) => {
      setPreEventGuests(snap.docs.map((d) => {
        const data = d.data();
        return { id: d.id, name: data.name || '', company: data.company || undefined, status: data.status || 'invited' };
      }));
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, entityId]);

  // ── Mastermind / Netwerkclub taken (live sync) ───────────────────────────
  // Geen orderBy('createdAt') op de query: bij nieuwe docs is createdAt eerst null (serverTimestamp
  // pending) en vallen ze dan uit een geordende query — lijst leek "niet live" tot server ack.
  useEffect(() => {
    if (!entityId) return;
    const q = query(collection(db, 'mastermindTasks'), where('entityId', '==', entityId));
    const taskCreatedMs = (createdAt: unknown): number => {
      if (createdAt instanceof Timestamp) return createdAt.toMillis();
      return 0;
    };
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() } as MastermindTask));
        rows.sort((a, b) => taskCreatedMs(a.createdAt) - taskCreatedMs(b.createdAt));
        setMastermindTasks(rows);
      },
      (err) => console.error('[Meeting tasks] onSnapshot:', err),
    );
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // ── Gedeelde agendapunten (realtime) ───────────────────────────────────────
  // Alleen where(entityId): composite index entityId+eventId ontbreekt vaak in prod → listener faalde stil.
  // Filter client-side op deze meeting (meetingRoom / eventId / roomName / createdInMeeting).
  useEffect(() => {
    if (!entityId || !roomName) return;
    const eventKey = eventId ?? roomName;
    const q = query(collection(db, 'sharedAgendaItems'), where('entityId', '==', entityId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setAgendaListenerError(null);
        const items: AgendaItem[] = snap.docs
          .map((d) => {
            const data = d.data() as Record<string, unknown>;
            return { id: d.id, data };
          })
          .filter(({ data }) => sharedAgendaItemVisibleInMeeting(data, { entityId, roomName, eventId: eventKey }))
          .map(({ id, data }) => ({
            id,
            eventId: (data.eventId as string) ?? eventKey,
            entityId: (data.entityId as string) ?? entityId,
            title: (data.title as string) ?? '',
            description: data.description as string | undefined,
            type: (data.type as AgendaItem['type']) ?? 'action',
            status: (data.status as AgendaItem['status']) ?? 'planned',
            order: typeof data.order === 'number' ? data.order : 0,
            submittedByUid: data.submittedByUid as string | undefined,
            submittedByName: data.submittedByName as string | undefined,
            submittedByAvatar: (data.submittedByAvatar as string | null) ?? null,
            meetingCheckedAt:
              data.meetingCheckedAt && typeof (data.meetingCheckedAt as Timestamp).toDate === 'function'
                ? (data.meetingCheckedAt as Timestamp).toDate()
                : undefined,
            createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
          }))
          .sort((a, b) => a.order - b.order || a.createdAt.getTime() - b.createdAt.getTime());
        setAgendaItems(items);
      },
      (err) => {
        console.error('[Meeting agenda] onSnapshot:', err);
        setAgendaListenerError('Agenda kan niet live laden. Controleer Firestore-regels en de console.');
      },
    );
    return () => unsub();
  }, [entityId, eventId, roomName]);

  const openAgendaItems = useMemo(
    () => agendaItems.filter((i) => !i.meetingCheckedAt),
    [agendaItems],
  );

  const agendaSubmitters = useMemo(() => {
    const m = new Map<string, { uid: string; name: string; avatar: string | null }>();
    for (const it of openAgendaItems) {
      const uid = it.submittedByUid;
      if (!uid) continue;
      if (!m.has(uid)) m.set(uid, { uid, name: it.submittedByName || 'Lid', avatar: it.submittedByAvatar ?? null });
    }
    return [...m.values()];
  }, [openAgendaItems]);

  const displayedAgendaItems = useMemo(() => {
    if (!agendaFilterUid) return openAgendaItems;
    return openAgendaItems.filter((i) => i.submittedByUid === agendaFilterUid);
  }, [openAgendaItems, agendaFilterUid]);

  /** Afgevinkte eigen punten — onder de video (+ fysieke modus in zijbalk) */
  const myAgendaPointsDone = useMemo(() => {
    if (!user?.uid) return [];
    return agendaItems
      .filter((i) => i.submittedByUid === user.uid && i.meetingCheckedAt)
      .sort((a, b) => (b.meetingCheckedAt!.getTime() - a.meetingCheckedAt!.getTime()));
  }, [agendaItems, user?.uid]);

  // ── Fase 1: Luister naar meetingSettings ─────────────────────────────────
  useEffect(() => {
    if (!entityId) return;
    prevCountdownRunningRef.current = null;
    const unsub = onSnapshot(doc(db, 'meetingSettings', entityId), (snap) => {
      if (!snap.exists()) return;
      const data: any = snap.data();
      const mm = data?.meetingMode;
      // Geen fallback naar 'online': ontbreekt het veld, dan behouden we o.a. `initialPhysical` (anders opent Jitsi bij fysieke start).
      if (mm === 'online' || mm === 'physical') {
        if (mm === 'physical') {
          skipCachedOnlineOverPhysicalRef.current = false;
          setMeetingMode('physical');
        } else if (snap.metadata.fromCache && skipCachedOnlineOverPhysicalRef.current) {
          /* cache kan nog 'online' zijn vóór server merge na fysieke start */
        } else {
          skipCachedOnlineOverPhysicalRef.current = false;
          setMeetingMode('online');
        }
      }
      setSelfStartEnabled(!!data?.selfStartEnabled);
      setDefaultMediaDisplaySeconds(data?.defaultMediaDisplaySeconds || 5);
      setCountdownMinutes(typeof data?.countdownMinutes === 'number' ? data.countdownMinutes : 4);
      setCountdownMusicUrl(typeof data?.countdownMusicUrl === 'string' ? data.countdownMusicUrl : null);
      setCountdownVideoUrl(typeof data?.countdownVideoUrl === 'string' ? data.countdownVideoUrl : null);
      setCountdownImageUrl(typeof data?.countdownImageUrl === 'string' ? data.countdownImageUrl : null);
      if (typeof data?.docViewEnabled === 'boolean') setDocViewEnabled(data.docViewEnabled);
      if (typeof data?.memberUploadEnabled === 'boolean') setMemberUploadEnabled(data.memberUploadEnabled);
      if (typeof data?.linkOpenerEnabled === 'boolean') setLinkOpenerEnabled(data.linkOpenerEnabled);
      if (typeof data?.guestSpotlightEnabled === 'boolean') setGuestSpotlightEnabled(data.guestSpotlightEnabled);
      if (typeof data?.commitmentReviewEnabled === 'boolean') setCommitmentReviewEnabled(data.commitmentReviewEnabled);
      if (typeof data?.slideshowEnabled === 'boolean') setSlideshowEnabled(data.slideshowEnabled);
      if (typeof data?.taskReminderEnabled === 'boolean') setTaskReminderEnabled(data.taskReminderEnabled);
      if (typeof data?.timerOverlayEnabled === 'boolean') setTimerOverlayEnabled(data.timerOverlayEnabled);
      if (typeof data?.autoRecordEnabled === 'boolean') {
        setAutoRecordEnabled(data.autoRecordEnabled);
        autoRecordEnabledRef.current = data.autoRecordEnabled;
      }
      const cr = !!data?.countdownRunning;
      setCountdownRunningRemote(cr);
      const prev = prevCountdownRunningRef.current;
      if (!cr) {
        // Sluit de countdown alleen als die eerder via Firestore actief was (prev === true).
        // Bij de eerste load (prev === null) met autoStartCountdown mag de lokale staat open blijven.
        if (prev === true) {
          setShowCountdown(false);
        }
      } else if (prev === false || prev === null) {
        // Countdown is (remote) gestart — open voor alle deelnemers
        setShowCountdown(true);
      }
      prevCountdownRunningRef.current = cr;
      setTransitionMusicUrl(typeof data?.transitionMusicUrl === 'string' ? data.transitionMusicUrl : null);
      setTransitionDurationSeconds(typeof data?.transitionDurationSeconds === 'number' ? data.transitionDurationSeconds : TRANSITION_DEFAULT_SECONDS);
      setTransitionMusicVolume(typeof data?.transitionMusicVolume === 'number' ? data.transitionMusicVolume : 0.5);
      if (typeof data?.templatePdfUrl === 'string' && data.templatePdfUrl.trim()) {
        setTemplatePdfUrl(data.templatePdfUrl.trim());
      } else {
        setTemplatePdfUrl(undefined);
      }
      if (typeof data?.pitchTemplateUrl === 'string' && data.pitchTemplateUrl.trim()) {
        setPitchTemplatePdfUrl(data.pitchTemplateUrl.trim());
      } else {
        setPitchTemplatePdfUrl(undefined);
      }
      if (Array.isArray(data?.pitchTemplateZones)) {
        setPitchTemplateZones(data.pitchTemplateZones);
      }
    });
    return () => {
      unsub();
      prevCountdownRunningRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // Fysieke modus: host — eenmalig volledig scherm voor beamer (browser kan weigeren zonder user gesture; setup-flow telt vaak als context)
  useEffect(() => {
    if (!isHost || meetingMode !== 'physical' || !roomName) return;
    if (physicalFullscreenRequestedRef.current === roomName) return;
    physicalFullscreenRequestedRef.current = roomName;
    const id = window.requestAnimationFrame(() => {
      if (document.fullscreenElement) return;
      containerRef.current?.requestFullscreen().catch(() => {});
    });
    return () => window.cancelAnimationFrame(id);
  }, [isHost, meetingMode, roomName]);

  /* Sidebar staat standaard open voor alle deelnemers (ook fysiek). */

  // ── Fase 1: Foto-carrousel timer bij actieve pitch ───────────────────────
  useEffect(() => {
    const runningEntry = livePitchEntries.find((e) => e.userId === livePitchRunningUserId);
    const photos = runningEntry?.mediaItems?.filter((m) => m.type === 'image') || [];
    if (photos.length <= 1) return;
    const interval = (runningEntry?.mediaDisplaySeconds || defaultMediaDisplaySeconds) * 1000;
    const timer = window.setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % photos.length);
    }, interval);
    return () => window.clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePitchRunningUserId, livePitchEntries, defaultMediaDisplaySeconds]);

  useEffect(() => {
    if (!livePitchRunningUserId || !livePitchStartedAtMs) return;
    const timer = window.setInterval(() => setLivePitchNowMs(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, [livePitchRunningUserId, livePitchStartedAtMs]);

  const saveMyLivePitch = async () => {
    if (!user?.uid) return;
    const title = livePitchCurrentUserTitle.trim();
    const message = livePitchCurrentUserMessage.trim();
    if (!title && !message) return;
    const existing = livePitchEntries.find((entry) => entry.userId === user.uid);

    const recTarget = livePitchCurrentUserRecTarget.trim();
    const recReason = livePitchCurrentUserRecReason.trim();
    const recommendation = recTarget ? { targetName: recTarget, reason: recReason, savedToInbox: false } : undefined;

    const nextEntries: LivePitchEntry[] = [
      ...livePitchEntries.filter((entry) => entry.userId !== user.uid),
      {
        userId: user.uid,
        presenterName: displayName,
        title,
        message,
        searchQuestion: livePitchCurrentUserSearchQ.trim() || undefined,
        recommendation,
        updatedAtMs: Date.now(),
        enabled: existing?.enabled ?? true,
        mediaDisplaySeconds: myMediaDisplaySeconds,
        mediaItems: pitchMediaItems as MediaItem[],
      },
    ].sort((a, b) => a.updatedAtMs - b.updatedAtMs);
    await saveLivePitchDoc({ entries: nextEntries });
  };

  // ── YouTube link toevoegen aan pitch-media ────────────────────────────────
  const handleAddYoutubeLink = async () => {
    const videoId = extractYoutubeVideoId(youtubeInputUrl.trim());
    if (!videoId) {
      setYoutubeInputError('Ongeldige YouTube URL.');
      return;
    }
    const ok = await pitchMedia.addYoutubeLink(youtubeInputUrl.trim(), { loop: youtubeInputLoop });
    if (ok) {
      setYoutubeInputUrl('');
      setYoutubeInputError(null);
    }
  };

  // ── Click-outside: sluit attendance popover ──────────────────────────────
  useEffect(() => {
    if (!showAttendancePopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (attendancePopoverRef.current && !attendancePopoverRef.current.contains(e.target as Node)) {
        setShowAttendancePopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAttendancePopover]);

  // ── Fase 1: Aanwezigheid toggle ──────────────────────────────────────────
  // Centrale helper — voorkomt dubbele Firestore-write logica
  const markAttendance = useCallback(async (status: 'present' | 'absent') => {
    if (!user?.uid || !entityId) return;
    setMyAttendance(status);
    const attendanceDocId = `${entityId}_${roomName}`;
    const entry: AttendanceEntry = {
      userId: user.uid,
      presenterName: displayName,
      status,
      markedAtMs: Date.now(),
    };
    const existing = attendanceEntries.filter((e) => e.userId !== user.uid);
    const nextEntries = [...existing, entry];
    const present = nextEntries.filter((e) => e.status === 'present').length;
    const absent  = nextEntries.filter((e) => e.status === 'absent').length;
    await setDoc(
      doc(db, 'meetingAttendance', attendanceDocId),
      { entityId, roomName, entries: nextEntries, totalPresent: present, totalAbsent: absent, updatedAt: serverTimestamp(), sessionDateMs: entry.markedAtMs },
      { merge: true }
    ).catch(() => {});
  }, [user, entityId, roomName, displayName, attendanceEntries]);

  const toggleMyAttendance = useCallback(async () => {
    const newStatus: 'present' | 'absent' = myAttendance === 'present' ? 'absent' : 'present';
    await markAttendance(newStatus);
  }, [myAttendance, markAttendance]);

  // ── Fase 1: Modus toggle (alleen host) ───────────────────────────────────
  const toggleMeetingMode = async () => {
    if (!isHost || !entityId) return;
    const newMode: 'online' | 'physical' = meetingMode === 'online' ? 'physical' : 'online';
    setMeetingMode(newMode);
    await setDoc(
      doc(db, 'meetingSettings', entityId),
      { meetingMode: newMode, updatedAt: serverTimestamp(), updatedBy: user?.uid || null },
      { merge: true }
    );
  };

  // ── Overgangs-muziek uploaden ─────────────────────────────────────────────
  const uploadTransitionMusic = async (file: File) => {
    if (!entityId || !isHost) return;
    setUploadingTransitionMusic(true);
    try {
      const path = STORAGE_PATHS.transitionMusic(entityId);
      const storageRef = ref(storage, `${path}/${file.name}`);
      const task = uploadBytesResumable(storageRef, file);
      await new Promise<void>((res, rej) => task.on('state_changed', undefined, rej, res));
      const url = await getDownloadURL(task.snapshot.ref);
      await setDoc(
        doc(db, 'meetingSettings', entityId),
        { transitionMusicUrl: url, updatedAt: serverTimestamp(), updatedBy: user?.uid || null },
        { merge: true },
      );
      setTransitionMusicUrl(url);
    } catch (err) {
      console.error('[uploadTransitionMusic]', err);
    } finally {
      setUploadingTransitionMusic(false);
    }
  };

  // Stuurt de aanbeveling naar de inbox wanneer de pitch live gaat (eenmalig)
  const pushRecommendationToInbox = async (entry: LivePitchEntry) => {
    if (!entry.recommendation || entry.recommendation.savedToInbox) return;
    if (!entityId) return;
    try {
      const { addDoc: add, collection: col, serverTimestamp: st } = await import('firebase/firestore');
      // Inbox bericht voor iedereen in de meeting (globale feed)
      await add(col(db, 'inboxItems'), {
        userId: entry.userId,        // Bevestiging aan de aanbeveler
        type: 'notification',
        category: 'system',
        title: `✅ Jouw aanbeveling voor ${entry.recommendation.targetName} is live gezet`,
        message: `"${entry.recommendation.reason}"`,
        isRead: false,
        priority: 'medium',
        createdAt: st(),
      });
      // Mark as sent in Firestore
      const nextEntries = livePitchEntries.map((e) =>
        e.userId === entry.userId && e.recommendation
          ? { ...e, recommendation: { ...e.recommendation, savedToInbox: true } }
          : e
      );
      await saveLivePitchDoc({ entries: nextEntries });
      setRecSavedFeedback(true);
      setTimeout(() => setRecSavedFeedback(false), 3000);
    } catch {}
  };

  const updateLivePitchEntries = async (updater: (entries: LivePitchEntry[]) => LivePitchEntry[]) => {
    const nextEntries = updater(livePitchEntries);
    await saveLivePitchDoc({ entries: nextEntries });
  };

  const startLivePitchForUser = async (targetUserId: string) => {
    if (!isHost) return;
    const entry = livePitchEntries.find((item) => item.userId === targetUserId);
    if (entry?.enabled === false) return;
    const startedAt = Date.now();
    await saveLivePitchDoc({
      runningUserId: targetUserId,
      startedAtMs: startedAt,
      timerDurationSeconds: livePitchTimerDurationSeconds,
    });
    // Push aanbeveling naar inbox zodra pitch live gaat
    if (entry) await pushRecommendationToInbox(entry);
  };

  const stopLivePitch = async (triggerTransition = false) => {
    if (!isHost) return;
    await saveLivePitchDoc({
      runningUserId: null,
      startedAtMs: null,
      timerDurationSeconds: livePitchTimerDurationSeconds,
    });
    // Toon overgangsslide naar volgende pitcher (alleen in fysieke modus)
    if (triggerTransition && meetingMode === 'physical') {
      const currentIdx = livePitchEntries.findIndex((e) => e.userId === livePitchRunningUserId);
      const nextEntry = livePitchEntries.slice(currentIdx + 1).find((e) => e.enabled !== false) ?? null;
      if (nextEntry) {
        setTransitionNextEntry(nextEntry);
        setTransitionActive(true);
      }
    }
  };

  const handleTransitionComplete = async () => {
    setTransitionActive(false);
    if (transitionNextEntry) {
      await startLivePitchForUser(transitionNextEntry.userId);
      setTransitionNextEntry(null);
    }
  };

  const toggleLivePitchEntryEnabled = async (targetUserId: string) => {
    if (!isHost) return;
    await updateLivePitchEntries((entries) =>
      entries.map((entry) =>
        entry.userId === targetUserId ? { ...entry, enabled: entry.enabled === false ? true : false } : entry
      )
    );
  };

  const moveLivePitchEntry = async (targetUserId: string, direction: 'up' | 'down') => {
    if (!isHost) return;
    await updateLivePitchEntries((entries) => {
      const index = entries.findIndex((entry) => entry.userId === targetUserId);
      if (index < 0) return entries;
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= entries.length) return entries;
      const next = [...entries];
      const [item] = next.splice(index, 1);
      next.splice(nextIndex, 0, item);
      return next;
    });
  };

  const savePresentationSlots = useCallback(async (slots: PresentationSlot[]) => {
    if (!entityId) return;
    const timelineDocRef = doc(db, 'meetingPresentationTimelines', `${entityId}_${roomName}`);
    await setDoc(
      timelineDocRef,
      {
        entityId,
        roomName,
        slots,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      },
      { merge: true }
    );
  }, [entityId, roomName, user?.uid]);

  useEffect(() => {
    if (!entityId) return;
    const timelineDocRef = doc(db, 'meetingPresentationTimelines', `${entityId}_${roomName}`);
    const unsub = onSnapshot(timelineDocRef, (snap) => {
      if (!snap.exists()) {
        setPresentationSlots([]);
        return;
      }
      const data: any = snap.data();
      setPresentationSlots(Array.isArray(data?.slots) ? data.slots : []);
    });
    return () => unsub();
  }, [entityId, roomName]);

  useEffect(() => {
    const hasLive = presentationSlots.some((slot) => slot.status === 'live');
    if (!hasLive) return;
    const timer = window.setInterval(() => setPresentationTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [presentationSlots]);

  const addPresentationSlot = async () => {
    const presenterName = presenterNameInput.trim() || displayName;
    const topic = presentationTopicInput.trim();
    const durationMinutes = Math.max(1, Math.min(60, Number(presentationMinutesInput) || 10));
    if (!presenterName || !entityId) return;
    const slot: PresentationSlot = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      presenterName,
      topic: topic || '',
      durationMinutes,
      status: 'planned',
      isGuest: true,
    };
    const next = [...presentationSlots, slot];
    await savePresentationSlots(next);
    setPresenterNameInput('');
    setPresentationTopicInput('');
    setPresentationMinutesInput('10');
  };

  const removePresentationSlot = async (slotId: string) => {
    const next = presentationSlots.filter((s) => s.id !== slotId);
    await savePresentationSlots(next);
  };

  const startPresentationSlot = async (slotId: string) => {
    const nowMs = Date.now();
    const next = presentationSlots.map((slot) => {
      if (slot.id === slotId) {
        return { ...slot, status: 'live' as const, startedAtMs: nowMs, endedAtMs: undefined };
      }
      if (slot.status === 'live') {
        return { ...slot, status: 'done' as const, endedAtMs: nowMs };
      }
      return slot;
    });
    await savePresentationSlots(next);
  };

  const completePresentationSlot = async (slotId: string) => {
    const nowMs = Date.now();
    const next = presentationSlots.map((slot) =>
      slot.id === slotId ? { ...slot, status: 'done' as const, endedAtMs: nowMs } : slot
    );
    await savePresentationSlots(next);
  };

  const exitFullscreenSafe = async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch {}
  };

  const safeLeave = async () => {
    await exitFullscreenSafe();
    onLeave();
  };

  const leaveMeeting = async () => {
    if (apiRef.current) {
      apiRef.current.executeCommand('hangup');
    }
    if (isHost) {
      const docsToDelete = [...sharedDocs];
      setSharedDocs([]);
      setSharedUrl(null);
      setShowUrlView(false);
      Promise.all(docsToDelete.map((d) =>
        d.storagePath ? deleteObject(ref(storage, d.storagePath)).catch(() => {}) : Promise.resolve()
      )).then(() => {
        setDoc(doc(db, 'meetingSharedDocs', roomName), {
          docs: [], active: false, activeIdx: 0, sharedUrl: null, urlActive: false,
        }, { merge: true }).catch(() => {});
      });
    }
    await safeLeave();
  };

  // Recording functions
  const formatRecordingTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
  };

  const startRecording = useCallback(async () => {
    try {
      // Stap 1: vraag microfoon-toestemming expliciet — browser toont nu de popup
      let micStream: MediaStream | null = null;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch (micErr: any) {
        // Microfoon geweigerd — waarschuw maar ga door (tab-audio kan nog werken)
        console.warn('Microfoon toegang geweigerd:', micErr?.message);
      }

      // Stap 2: schermopname met tabblad-audio (lagere kwaliteit = snellere encoding + kleinere file)
      const displayStream = await (navigator.mediaDevices as any).getDisplayMedia({
        video: { frameRate: 15, width: { max: 1280 }, height: { max: 720 }, displaySurface: 'browser' },
        audio: true,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
      } as any);

      // Controleer of er audio tracks zijn
      const displayAudioTracks = displayStream.getAudioTracks();
      const micAudioTracks = micStream?.getAudioTracks() ?? [];
      const totalAudioTracks = displayAudioTracks.length + micAudioTracks.length;
      if (totalAudioTracks === 0) {
        console.warn('Geen audio tracks gevonden — opname zal geen geluid bevatten');
      } else {
        console.log(`Audio tracks: ${displayAudioTracks.length} tab + ${micAudioTracks.length} microfoon`);
      }

      // Stap 3: MIX audio via AudioContext zodat beide bronnen hoorbaar zijn in opname
      let mixedAudioTrack: MediaStreamTrack | null = null;
      if (totalAudioTracks > 0) {
        const audioCtx = new AudioContext();
        const dest = audioCtx.createMediaStreamDestination();

        if (displayAudioTracks.length > 0) {
          const src = audioCtx.createMediaStreamSource(new MediaStream(displayAudioTracks));
          src.connect(dest);
        }
        if (micAudioTracks.length > 0) {
          const src = audioCtx.createMediaStreamSource(new MediaStream(micAudioTracks));
          src.connect(dest);
        }
        mixedAudioTrack = dest.stream.getAudioTracks()[0] ?? null;
      }

      // Combineer video (scherm) + gemixed audio
      const videoTracks = displayStream.getVideoTracks();
      const tracks: MediaStreamTrack[] = [...videoTracks];
      if (mixedAudioTrack) tracks.push(mixedAudioTrack);
      const combinedStream = new MediaStream(tracks);
      recordingStreamRef.current = combinedStream;

      // Set up MediaRecorder — VP8 is significantly faster to encode than VP9
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/webm';
      const recorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: 800_000,  // 800 kbps — fine for a talking-head/screen
        audioBitsPerSecond: 64_000,
      });
      mediaRecorderRef.current = recorder;
      recordedChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
          console.log(`[Recording] Chunk ontvangen: ${(e.data.size / 1024).toFixed(1)} KB (totaal chunks: ${recordedChunksRef.current.length})`);
        }
      };

      // --- Audio-only recorder for Whisper (much smaller than video) ---
      // Mono opus at 16 kbps: a 2-hour meeting ≈ ~14 MB — well within Whisper's 25 MB limit
      const audioMime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const audioTracks = [
        ...(micStream?.getAudioTracks() ?? []),
        ...displayStream.getAudioTracks(),
      ];
      audioChunksRef.current = [];
      if (audioTracks.length > 0) {
        const audioOnlyStream = new MediaStream(audioTracks);
        const audioRec = new MediaRecorder(audioOnlyStream, {
          mimeType: audioMime,
          audioBitsPerSecond: 16_000, // 16 kbps — enough for speech transcription
        });
        audioRecorderRef.current = audioRec;
        audioRec.ondataavailable = (e) => {
          if (e.data.size > 0) audioChunksRef.current.push(e.data);
        };
        audioRec.start(5_000); // chunk every 5 s
        console.log('[Recording] Audio-only recorder gestart voor Whisper');
      }

      recorder.onstop = async () => {
        console.log(`[Recording] Gestopt — ${recordedChunksRef.current.length} chunks verzameld`);
        combinedStream.getTracks().forEach((t) => t.stop());
        micStream?.getTracks().forEach((t) => t.stop());

        // Stop audio recorder and collect chunks
        if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
          audioRecorderRef.current.stop();
        }

        if (recordedChunksRef.current.length === 0) {
          console.warn('[Recording] Geen data opgenomen — upload overgeslagen');
          return;
        }
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const audioBlob = audioChunksRef.current.length > 0
          ? new Blob(audioChunksRef.current, { type: audioMime })
          : null;

        console.log(`[Recording] Video: ${(blob.size / 1024 / 1024).toFixed(1)} MB | Audio: ${audioBlob ? (audioBlob.size / 1024 / 1024).toFixed(1) + ' MB' : 'n/a'}`);

        // Keep blob in memory for retry
        recordedBlobRef.current = blob;

        await uploadRecordingBlob(blob, mimeType, audioBlob);
      };

      const uploadRecordingBlob = async (blob: Blob, mime: string, audioBlob?: Blob | null) => {
        setUpload('uploading');
        onRecordingUploadStatus?.('uploading');
        console.log(`[Recording] Uploaden gestart — grootte: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
        try {
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const fileName = `recordings/${entityId ?? 'general'}/${roomName}_${timestamp}.webm`;
          const storageRef = ref(storage, fileName);
          await uploadBytes(storageRef, blob);
          const downloadURL = await getDownloadURL(storageRef);
          console.log('[Recording] Upload geslaagd:', downloadURL);
          setRecordingUrl(downloadURL);

          // Use the pre-snapshotted participant list (captured while meeting was live)
          // This avoids the issue where the Jitsi API is already gone by the time we upload
          const participants = snapshotParticipantsRef.current.length > 0
            ? snapshotParticipantsRef.current
            : [{ displayName: user?.displayName || user?.email || 'Jij', avatarUrl: null }];

          await addDoc(collection(db, 'meetingRecordings'), {
            entityId: entityId ?? null,
            roomName,
            userId: user?.uid ?? null,
            userName: user?.displayName || user?.email || 'Onbekend',
            downloadURL,
            durationSeconds: recordingSecondsRef.current,
            fileSizeBytes: blob.size,
            mimeType: mime,
            participants,
            createdAt: serverTimestamp(),
          });

          setUpload('done');
          onRecordingUploadStatus?.('done', downloadURL);

          // --- Automatische transcriptie + AI-analyse pipeline ---
          // Runs fully in the background — user can safely close the browser after upload.
          // We use the small audio-only blob for Whisper instead of the full video.
          if (openAI.isConfigured() && entityId && user?.uid) {
            const blobForTranscription = audioBlob ?? blob;
            console.log(`[Analysis] Transcriptie met ${audioBlob ? 'audio-only' : 'video'} blob (${(blobForTranscription.size / 1024 / 1024).toFixed(1)} MB)`);
            (async () => {
              try {
                console.log('[Analysis] Pipeline gestart voor opname:', roomName);

                // 1. Maak direct een pending reflection aan zodat de kaart zichtbaar wordt
                const reflectionRef = await addDoc(collection(db, 'meetingReflections'), {
                  entityId,
                  meetingId: roomName,
                  userId: user.uid,
                  userName: user.displayName || user.email || 'Onbekend',
                  summary: '',
                  transcript: '',
                  answers: {},
                  meetingDuration: recordingSecondsRef.current,
                  bulletPoints: [],
                  actiepunten: [],
                  beslissingen: [],
                  leerpunten: [],
                  status: 'pending_analysis',
                  createdAt: serverTimestamp(),
                });
                console.log('[Analysis] Pending reflection aangemaakt:', reflectionRef.id);

                // 2. Transcribeer via OpenAI Whisper (audio-only = veel kleiner, veel sneller)
                console.log('[Analysis] Whisper transcriptie starten...');
                const transcript = await openAI.transcribeAudioBlob(blobForTranscription);
                console.log(`[Analysis] Transcript klaar: ${transcript.length} tekens`);

                if (!transcript.trim()) {
                  await updateDoc(reflectionRef, { status: 'analyzed', summary: 'Geen spraak herkend in de opname.' });
                  return;
                }

                // 3. Genereer samenvatting + bullet points via OpenAI
                console.log('[Analysis] AI-samenvatting genereren...');
                const notes = await openAI.generateMeetingNotes(transcript, clubName);
                console.log('[Analysis] AI-samenvatting klaar, score:', notes.score);

                // 4. Sla de volledige analyse op
                await updateDoc(reflectionRef, {
                  transcript,
                  summary: notes.samenvatting,
                  bulletPoints: notes.bulletPoints,
                  actiepunten: notes.actiepunten,
                  beslissingen: notes.beslissingen,
                  leerpunten: notes.leerpunten,
                  aiAnalysis: {
                    summary: notes.samenvatting,
                    strengths: notes.leerpunten,
                    improvements: [],
                    overallScore: notes.score,
                    nextSteps: notes.actiepunten,
                  },
                  status: 'analyzed',
                  analyzedAt: serverTimestamp(),
                });
                console.log('[Analysis] Analyse opgeslagen in Firestore:', reflectionRef.id);
              } catch (analysisErr) {
                console.error('[Analysis] Pipeline mislukt:', analysisErr);
              }
            })();
          }
        } catch (err: any) {
          console.error('[Recording] Upload mislukt:', err?.code, err?.message);
          setUpload('error');
          onRecordingUploadStatus?.('error');
        }
      };

      // Stop recording when screen share ends
      displayStream.getVideoTracks()[0]?.addEventListener('ended', () => {
        if (recorder.state !== 'inactive') recorder.stop();
        setRecording(false);
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      });

      recorder.start(1000);
      setRecording(true);
      setRecordingSeconds(0);
      recordingSecondsRef.current = 0;
      setUpload('idle');
      setRecordingUrl(null);
      console.log(`[Recording] Opname gestart — mimeType: ${mimeType}, video tracks: ${videoTracks.length}, audio: ${mixedAudioTrack ? 'ja' : 'nee'}`);

      // Timer — update both state (UI) and ref (for upload callback)
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => {
          const next = s + 1;
          recordingSecondsRef.current = next;
          return next;
        });
      }, 1000);
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        // User cancelled the screen-share picker — show a gentle notice
        console.info('[Recording] Schermopname geannuleerd door gebruiker.');
        setUpload('idle');
      } else {
        console.error('[Recording] Kon opname niet starten:', err?.name, err?.message);
        setUpload('error');
      }
    }
  }, [entityId, roomName, user, recordingSeconds]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    recordingStreamRef.current?.getTracks().forEach((t) => t.stop());
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    setRecording(false);
  }, [setRecording]);

  // Cleanup on unmount — stop media streams maar niet de upload als die bezig is
  useEffect(() => {
    return () => {
      // Alleen media streams stoppen, niet de upload annuleren
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      recordingStreamRef.current?.getTracks().forEach((t) => t.stop());
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Mastermind/Netwerkclub taken handlers ────────────────────────────────
  const handleAddTask = useCallback(async () => {
    if (!newTaskTitle.trim() || !entityId || !user) return;
    const ownerUid = newTaskAssigneeUid === 'self' ? user.uid : newTaskAssigneeUid;
    const ownerMeta = newTaskAssigneeUid === 'self'
      ? { name: displayName, avatar: user.photoURL ?? null }
      : (() => {
          // Look up by UID (index in memberUids matches index in memberNames/memberAvatars)
          const idx = memberUids.indexOf(newTaskAssigneeUid);
          return { name: memberNames[idx] ?? newTaskAssigneeUid, avatar: (memberAvatars ?? [])[idx] ?? null };
        })();
    const resolvedName = newTaskAssigneeUid === 'self'
      ? displayName
      : (() => {
          const idx = memberUids.indexOf(newTaskAssigneeUid);
          return memberNames[idx] ?? newTaskAssigneeUid;
        })();

    try {
      await addDoc(collection(db, 'mastermindTasks'), {
        entityId,
        entityName: clubName ?? '',
        title: newTaskTitle.trim(),
        ownerUid,
        ownerName: resolvedName,
        ownerAvatar: ownerMeta.avatar,
        status: 'open',
        createdInMeeting: roomName,
        createdByUid: user.uid,
        createdByName: displayName,
        createdAt: serverTimestamp(),
      });
      setNewTaskTitle('');
      setNewTaskAssigneeUid('self');
      setShowAddTask(false);
      setTaskFeedback(`Taak toegevoegd voor ${resolvedName}`);
      setTimeout(() => setTaskFeedback(null), 3000);
    } catch (err) {
      console.error('[Tasks] Aanmaken mislukt:', err);
      setTaskFeedback('Fout bij opslaan — probeer opnieuw');
      setTimeout(() => setTaskFeedback(null), 3000);
    }
  }, [newTaskTitle, newTaskAssigneeUid, entityId, user, displayName, clubName, roomName, memberNames, memberUids, memberAvatars]);

  const handleToggleTask = useCallback(async (taskId: string, currentStatus: string) => {
    const nextStatus = currentStatus === 'done' ? 'open' : 'done';
    try {
      await import('firebase/firestore').then(({ updateDoc, doc: fDoc, serverTimestamp: sts }) =>
        updateDoc(fDoc(db, 'mastermindTasks', taskId), {
          status: nextStatus,
          completedAt: nextStatus === 'done' ? sts() : null,
        })
      );
    } catch (err) {
      console.error('[Tasks] Toggle mislukt:', err);
    }
  }, []);

  const handleDeleteTask = useCallback(async (taskId: string) => {
    try {
      await import('firebase/firestore').then(({ deleteDoc, doc: fDoc }) =>
        deleteDoc(fDoc(db, 'mastermindTasks', taskId))
      );
    } catch (err) {
      console.error('[Tasks] Verwijderen mislukt:', err);
    }
  }, []);

  const handleEditTask = useCallback(async (taskId: string) => {
    if (!editingTaskTitle.trim()) return;
    const ownerUid = editingTaskAssigneeUid === 'self' ? (user?.uid ?? '') : editingTaskAssigneeUid;
    const ownerIdx = memberUids.indexOf(editingTaskAssigneeUid);
    const ownerName = editingTaskAssigneeUid === 'self'
      ? displayName
      : (memberNames[ownerIdx] ?? editingTaskAssigneeUid);
    const ownerAvatar = editingTaskAssigneeUid === 'self'
      ? (user?.photoURL ?? null)
      : ((memberAvatars ?? [])[ownerIdx] ?? null);
    try {
      const { updateDoc: upd, doc: fDoc } = await import('firebase/firestore');
      await upd(fDoc(db, 'mastermindTasks', taskId), {
        title: editingTaskTitle.trim(),
        ownerUid,
        ownerName,
        ownerAvatar,
      });
      setEditingTaskId(null);
    } catch (err) {
      console.error('[Tasks] Wijzigen mislukt:', err);
    }
  }, [editingTaskTitle, editingTaskAssigneeUid, user, displayName, memberUids, memberNames, memberAvatars]);
  // ─────────────────────────────────────────────────────────────────────────

  const handleMarkAgendaItemDone = useCallback(
    async (itemId: string) => {
      if (!user?.uid) return;
      const item = agendaItems.find((i) => i.id === itemId);
      if (!item || item.submittedByUid !== user.uid || item.meetingCheckedAt) return;
      try {
        await updateDoc(doc(db, 'sharedAgendaItems', itemId), { meetingCheckedAt: serverTimestamp() });
      } catch (err) {
        console.error('[Agenda] Afvinken mislukt:', err);
      }
    },
    [user?.uid, agendaItems],
  );

  const handleUndoAgendaItemDone = useCallback(
    async (itemId: string) => {
      if (!user?.uid) return;
      const item = agendaItems.find((i) => i.id === itemId);
      if (!item || item.submittedByUid !== user.uid || !item.meetingCheckedAt) return;
      try {
        await updateDoc(doc(db, 'sharedAgendaItems', itemId), { meetingCheckedAt: deleteField() });
      } catch (err) {
        console.error('[Agenda] Ongedaan mislukt:', err);
      }
    },
    [user?.uid, agendaItems],
  );

  const clearAgendaDeleteUndoTimer = useCallback(() => {
    if (agendaDeleteUndoTimerRef.current) {
      clearTimeout(agendaDeleteUndoTimerRef.current);
      agendaDeleteUndoTimerRef.current = null;
    }
  }, []);

  const scheduleAgendaDeleteUndoClear = useCallback(() => {
    clearAgendaDeleteUndoTimer();
    agendaDeleteUndoTimerRef.current = setTimeout(() => {
      setAgendaDeleteUndo(null);
      agendaDeleteUndoTimerRef.current = null;
    }, 8000);
  }, [clearAgendaDeleteUndoTimer]);

  useEffect(() => () => clearAgendaDeleteUndoTimer(), [clearAgendaDeleteUndoTimer]);

  const softDeleteAgendaItem = useCallback(
    async (item: AgendaItem) => {
      try {
        await updateDoc(doc(db, 'sharedAgendaItems', item.id), { deletedAt: serverTimestamp() });
        setAgendaDeleteUndo({ id: item.id, title: item.title });
        scheduleAgendaDeleteUndoClear();
      } catch (err) {
        console.error('[Agenda] Verwijderen mislukt:', err);
      }
    },
    [scheduleAgendaDeleteUndoClear],
  );

  const undoAgendaSoftDelete = useCallback(async () => {
    if (!agendaDeleteUndo) return;
    try {
      await updateDoc(doc(db, 'sharedAgendaItems', agendaDeleteUndo.id), { deletedAt: deleteField() });
      clearAgendaDeleteUndoTimer();
      setAgendaDeleteUndo(null);
    } catch (err) {
      console.error('[Agenda] Undo verwijderen mislukt:', err);
    }
  }, [agendaDeleteUndo, clearAgendaDeleteUndoTimer]);

  const commitAgendaTitleEdit = useCallback(async () => {
    if (!editingAgendaItemId) return;
    const t = editingAgendaDraft.trim();
    if (!t) {
      setEditingAgendaItemId(null);
      return;
    }
    try {
      await updateDoc(doc(db, 'sharedAgendaItems', editingAgendaItemId), { title: t, updatedAt: serverTimestamp() });
    } catch (err) {
      console.error('[Agenda] Titel opslaan mislukt:', err);
    }
    setEditingAgendaItemId(null);
  }, [editingAgendaItemId, editingAgendaDraft]);

  // Add agenda item — open to all meeting participants
  const handleAddAgendaItem = async () => {
    if (!newAgendaItem.title.trim()) {
      setAgendaSaveError('Vul een titel in.');
      return;
    }
    if (!entityId) {
      setAgendaSaveError('Geen groep-context — agenda kan niet worden opgeslagen.');
      return;
    }
    if (!roomName) {
      setAgendaSaveError('Geen meeting-room — agenda kan niet worden opgeslagen.');
      return;
    }

    setAgendaSaveError(null);
    try {
      const nextOrder =
        agendaItems.reduce((m, it) => Math.max(m, typeof it.order === 'number' ? it.order : 0), 0) + 1;
      const agendaItem: Omit<AgendaItem, 'id' | 'createdAt'> = {
        eventId: eventId ?? roomName,
        entityId: entityId,
        title: newAgendaItem.title.trim(),
        description: newAgendaItem.description.trim() || undefined,
        type: 'action',
        status: 'planned',
        order: nextOrder,
        submittedByUid: user?.uid ?? undefined,
        submittedByName: user?.displayName || user?.email?.split('@')[0] || 'Deelnemer',
        submittedByAvatar: user?.photoURL ?? null,
      };

      await addDoc(collection(db, 'sharedAgendaItems'), {
        ...agendaItem,
        preMeetingEntity: false,
        meetingRoom: roomName,
        createdInMeeting: roomName,
        createdAt: serverTimestamp(),
      });

      setNewAgendaItem({ title: '', description: '' });
      setShowAddAgendaItem(false);
    } catch (error: unknown) {
      console.error('Error adding agenda item:', error);
      const code = error && typeof error === 'object' && 'code' in error ? String((error as { code: string }).code) : '';
      setAgendaSaveError(
        code === 'permission-denied'
          ? 'Geen rechten om agendapunten op te slaan (Firestore-regels).'
          : 'Opslaan mislukt. Probeer opnieuw of open de console.',
      );
    }
  };

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const getSlotProgress = (slot: PresentationSlot) => {
    if (slot.status !== 'live' || !slot.startedAtMs) return 0;
    const durationMs = slot.durationMinutes * 60 * 1000;
    const elapsed = Math.max(0, presentationTick - slot.startedAtMs);
    return Math.min(100, Math.round((elapsed / durationMs) * 100));
  };
  const runningEntry = livePitchEntries.find((entry) => entry.userId === livePitchRunningUserId) || null;
  const livePitchRemainingSeconds = (() => {
    if (!livePitchStartedAtMs) return livePitchTimerDurationSeconds;
    const elapsed = Math.max(0, Math.floor((livePitchNowMs - livePitchStartedAtMs) / 1000));
    return Math.max(0, livePitchTimerDurationSeconds - elapsed);
  })();

  // ── Fase 2: SelfStart — should this user see the overlay? ────────────────
  const isMySelfStartTurn =
    selfStartEnabled &&
    !!user?.uid &&
    livePitchRunningUserId === user.uid &&
    !!livePitchStartedAtMs === false; // not yet started (startedAtMs null)

  // ── Fase 2: Rapport trigger ───────────────────────────────────────────────
  const triggerRapport = (): void => {
    if (!user?.uid) return;
    void generateRapport({
      entityId: entityId ?? '',
      roomName,
      clubName: clubName ?? undefined,
      hostUserId: user.uid,
      attendance: attendanceEntries,
      pitches: livePitchEntries.map((e) => ({
        userId: e.userId,
        presenterName: e.presenterName,
        title: e.title,
        message: e.message,
        searchQuestion: e.searchQuestion,
        recommendation: e.recommendation,
      })),
    });
    setShowRapportPanel(true);
  };

  // ── Tijdlijn: afgeleid uit SessionStructure-blokken (zelfde als mastermind) ──
  // Hotseat-blokken worden uitgevouwen naar individuele pitch-slots.
  // Zonder ingestelde blokken: fallback naar vaste structuur.
  const timelineSlots: Array<{
    key: string;
    label: string;
    slotNumber: number;
    slotType: SlotType;
    userId?: string;
    isAbsent: boolean;
    isActive: boolean;
    isDone: boolean;
  }> = (() => {
    const currentFlowBlockIdx = sgSessionFlow.flow.currentBlockIndex;

    if (sgBlocks) {
      const raw: Array<{
        key: string; label: string; slotType: SlotType;
        userId?: string; isAbsent?: boolean; sourceBlockIdx: number;
      }> = [];
      let pitchCursor = 0;

      for (let bi = 0; bi < sgBlocks.length; bi++) {
        const block = sgBlocks[bi];

        if (block.type === 'hotseat' && block.perPerson) {
          const count = block.memberSlotCount ?? Math.max(0, livePitchEntries.length - pitchCursor);
          const entries = livePitchEntries.slice(pitchCursor, pitchCursor + count);
          for (const entry of entries) {
            raw.push({
              key: `pitch_${entry.userId}`,
              label: entry.presenterName?.split(' ')[0] || `#${raw.length + 1}`,
              slotType: 'pitch',
              userId: entry.userId,
              isAbsent: attendanceEntries.find((a) => a.userId === entry.userId)?.status === 'absent',
              sourceBlockIdx: bi,
            });
          }
          pitchCursor += count;
        } else {
          const slotType: SlotType =
            block.type === 'intro' ? 'intro'
            : block.type === 'commitment' ? 'closing'
            : block.type === 'reflection' ? 'opening'
            : 'pause';
          raw.push({ key: block.id, label: block.label, slotType, sourceBlockIdx: bi });
        }
      }

      return raw.map((s, i) => ({
        key: s.key,
        label: s.label,
        slotNumber: i,
        slotType: s.slotType,
        userId: s.userId,
        isAbsent: s.isAbsent || false,
        isActive: s.userId
          ? s.userId === livePitchRunningUserId
          : s.sourceBlockIdx === currentFlowBlockIdx && sgSessionFlow.flow.isRunning,
        isDone: s.sourceBlockIdx < currentFlowBlockIdx,
      }));
    }

    // Fallback: geen SessionStructure ingesteld → vaste structuur
    const fixed: Array<{ key: string; label: string; slotType: SlotType }> = [
      { key: 'intro',   label: 'Intro',   slotType: 'intro' },
      { key: 'opening', label: 'Opening', slotType: 'opening' },
    ];
    const pitchSlots = livePitchEntries.map((e) => ({
      key: `pitch_${e.userId}`,
      label: e.presenterName?.split(' ')[0] || `#${livePitchEntries.indexOf(e) + 3}`,
      slotType: 'pitch' as SlotType,
      userId: e.userId,
      isAbsent: attendanceEntries.find((a) => a.userId === e.userId)?.status === 'absent',
    }));
    const closing: Array<{ key: string; label: string; slotType: SlotType }> = [
      { key: 'presentation', label: 'Presentatie', slotType: 'presentation' },
      { key: 'analysis',     label: 'Analyse',     slotType: 'analysis' },
      { key: 'closing',      label: 'Sluiting',    slotType: 'closing' },
    ];
    return [...fixed, ...pitchSlots, ...closing].map((s, i) => ({
      ...s,
      slotNumber: i,
      isAbsent: (s as any).isAbsent || false,
      isActive: (s as any).userId ? (s as any).userId === livePitchRunningUserId : false,
      isDone: false,
      userId: (s as any).userId,
    }));
  })();

  const presentCount = attendanceEntries.filter((e) => e.status === 'present').length;
  const absentCount = attendanceEntries.filter((e) => e.status === 'absent').length;

  // True when any full-screen content overlay is visible — Jitsi floats as PiP above it
  const contentOverlayActive = (showDocView && docViewEnabled && sharedDocs.length > 0)
    || (slideshowEnabled && showSlideshow && slides.length > 0)
    || (showUrlView && !!sharedUrl);

  // Carrousel foto's van de actieve pitcher
  const runningPhotos = (runningEntry?.mediaItems || []).filter((m) => m.type === 'image');
  const activePhoto = runningPhotos.length > 0 ? runningPhotos[carouselIndex % runningPhotos.length] : null;

  return (
    <div
      ref={containerRef}
      className={inlineMode
        ? "relative w-full h-full flex flex-col bg-[#0a0f1a] overflow-hidden rounded-xl"
        : "fixed inset-0 z-50 flex flex-col bg-[#0a0f1a] overflow-hidden"}
      style={{ aspectRatio: 'unset' }}
    >
      {/* Hidden photo input */}
      {/* File input: accepts image AND video (Fase 2) */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*,video/mp4,video/webm,video/ogg,video/quicktime"
        multiple
        className="hidden"
        onChange={(e) => {
          Array.from(e.target.files || []).forEach((f) => void pitchMedia.uploadFile(f));
          if (photoInputRef.current) photoInputRef.current.value = '';
        }}
      />

      {/* ══ Top Bar — alleen online (fysiek: schone beamer-UI, bediening via onderbalk + sidebar) ══ */}
      {meetingMode !== 'physical' && (
      <div className="bg-[#111827] text-white border-b border-white/8" style={{ minHeight: '52px' }}>
        <div className="flex items-stretch h-full divide-x divide-white/8">

          {/* ── ZONE 1: Meeting identiteit (links) ──────────────────────── */}
          <div className="flex items-center gap-3 px-4 py-2 min-w-0 flex-shrink-0">
            {/* Modus pill — alleen online-topbalk; fysiek heeft geen topbalk */}
            <span className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold tracking-wide bg-emerald-700 text-white">
              💻
              Online
            </span>
            {/* Titel */}
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight truncate max-w-[200px]">
              {meetingTitle || `${clubName || 'Netwerkclub'} Meeting`}
              </p>
              <p className="text-[10px] text-white/40 truncate max-w-[200px]">
                {roomName.slice(0, 36)}{roomName.length > 36 ? '…' : ''}
              </p>
          </div>
        </div>
        
          {/* ── ZONE 2: Live stats (midden) ─────────────────────────────── */}
          <div className="flex items-center gap-4 px-4 py-2 text-xs text-white/70 flex-shrink-0">
            {/* Deelnemers */}
            <div className="flex items-center gap-1.5">
              <Icon icon={Users} size={14} strokeWidth={1.5} className="text-white/50" />
              <span>{participantCount}</span>
              <span className="text-white/40 hidden sm:inline">deelnemers</span>
            </div>
            {/* Aanwezigheidsteller (host) — klikbaar voor namen */}
            {isHost && (presentCount + absentCount) > 0 && (
              <>
                <div className="w-px h-4 bg-white/15" />
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowAttendancePopover(v => !v)}
                    className="flex items-center gap-2 text-[11px] hover:bg-white/5 rounded-lg px-1.5 py-0.5 transition-colors"
                    title="Klik voor overzicht aanwezigheid"
                  >
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                      <span className="text-emerald-400 font-medium">{presentCount}</span>
                    </span>
                    {absentCount > 0 && (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />
                        <span className="text-red-400 font-medium">{absentCount}</span>
                      </span>
                    )}
                    {(() => {
                      const notConfirmed = memberNames.filter(n => !attendanceEntries.find(e => e.presenterName === n)).length;
                      return notConfirmed > 0 ? (
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-white/25 inline-block" />
                          <span className="text-white/30 font-medium">{notConfirmed}</span>
                        </span>
                      ) : null;
                    })()}
                  </button>
                  {showAttendancePopover && (
                    <div ref={attendancePopoverRef} className="absolute top-full left-0 mt-1.5 w-52 bg-gray-800 border border-white/10 rounded-xl shadow-2xl z-[70] py-2 overflow-hidden">
                      <p className="text-[9px] font-bold text-white/30 uppercase tracking-wider px-3 pb-1.5 border-b border-white/5">Aanwezigheid</p>
                      {attendanceEntries.filter(e => e.status === 'present').map(e => (
                        <div key={e.userId} className="flex items-center gap-2 px-3 py-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                          <span className="text-[10px] text-white/70 truncate">{e.presenterName}</span>
                          <span className="text-[9px] text-emerald-400/60 ml-auto shrink-0">aanwezig</span>
                        </div>
                      ))}
                      {attendanceEntries.filter(e => e.status === 'absent').map(e => (
                        <div key={e.userId} className="flex items-center gap-2 px-3 py-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                          <span className="text-[10px] text-white/70 truncate">{e.presenterName}</span>
                          <span className="text-[9px] text-red-400/60 ml-auto shrink-0">afwezig</span>
                        </div>
                      ))}
                      {memberNames.filter(n => !attendanceEntries.find(e => e.presenterName === n)).map(n => (
                        <div key={n} className="flex items-center gap-2 px-3 py-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-white/20 shrink-0" />
                          <span className="text-[10px] text-white/35 truncate">{n}</span>
                          <span className="text-[9px] text-white/20 ml-auto shrink-0">onbevestigd</span>
                        </div>
                      ))}
                      <button type="button" onClick={() => setShowAttendancePopover(false)} className="w-full text-[9px] text-white/20 hover:text-white/40 pt-1.5 pb-0.5 border-t border-white/5 mt-1 transition-colors">Sluiten</button>
                    </div>
                  )}
                </div>
              </>
            )}
            {/* Icon toggles */}
            <div className="w-px h-4 bg-white/15" />
            <div className="flex items-center gap-1">
          {canUseMeetingHostSidebar && (
          <button
            onClick={() => setShowAgenda(!showAgenda)}
                className={`p-1.5 rounded-md transition-colors ${showAgenda ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
                title="Meeting Tijdlijn"
                aria-pressed={showAgenda}
              >
                <Icon icon={Calendar} size={15} strokeWidth={1.5} />
          </button>
          )}
          <button
            onClick={() => setShowParticipants(!showParticipants)}
                className={`p-1.5 rounded-md transition-colors ${showParticipants ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
            title="Deelnemers"
                aria-pressed={showParticipants}
          >
                <Icon icon={Users} size={15} strokeWidth={1.5} />
          </button>
          <button
            onClick={() => setShowChat(!showChat)}
                className={`p-1.5 rounded-md transition-colors ${showChat ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
            title="Chat"
                aria-pressed={showChat}
          >
                <Icon icon={MessageSquare} size={15} strokeWidth={1.5} />
          </button>
            </div>
          </div>

          {/* ── ZONE 3: Acties (rechts) ──────────────────────────────────── */}
          <div className="flex items-center gap-1.5 px-3 py-2 ml-auto flex-wrap">

            {/* Opnemen — alleen voor host/organisator */}
            {isHost && (
              <>
                {/* Scheidingslijn */}
                <div className="w-px h-5 bg-white/12 mx-0.5" />
          {isRecording ? (
            <button
              onClick={stopRecording}
              title="Opname stoppen"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[11px] font-semibold transition-colors"
            >
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
              <span>REC {formatRecordingTime(recordingSeconds)}</span>
            </button>
          ) : (
            <button
              onClick={() => setShowRecordingPrompt(true)}
              title="Meeting opnemen"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/8 hover:bg-white/15 border border-white/12 text-white/80 text-[11px] font-medium transition-colors"
            >
                    <span className="w-2 h-2 rounded-full bg-white/60" />
                    <span className="hidden sm:inline">Opnemen</span>
            </button>
                )}
              </>
          )}

            {/* Presentatie live — alleen voor networkclubs */}
            {enableLivePresentation && (
          <button
                onClick={() => setShowLivePitch(true)}
                title="Presentatie live openen"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition-colors"
                style={{ backgroundColor: '#7A9488' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#4F6660')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#7A9488')}
              >
                <Icon icon={Monitor} size={13} strokeWidth={2} />
                <span>Presentatie</span>
          </button>
            )}

            {/* Scheidingslijn */}
            <div className="w-px h-5 bg-white/12 mx-0.5" />

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Volledig scherm verlaten' : 'Volledig scherm'}
              aria-label={isFullscreen ? 'Volledig scherm verlaten' : 'Volledig scherm'}
              className="p-1.5 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            >
              <Icon icon={isFullscreen ? Minimize2 : Maximize2} size={15} strokeWidth={1.8} />
            </button>

            {/* Host-only acties */}
            {isHost && (
              <>
                <div className="w-px h-5 bg-white/12 mx-0.5" />
                {/* Countdown */}
                <button
                  onClick={() => setShowCountdown(true)}
                  title="Countdown starten"
                  aria-label="Countdown starten"
                  className="px-2 py-1.5 rounded-lg text-[10px] font-bold bg-white/8 border border-white/12 text-white/60 hover:bg-white/15 transition-colors"
                >
                  ⏱
                </button>
                {/* Rapport */}
                <button
                  onClick={() => triggerRapport()}
                  title="Rapport genereren"
                  aria-label="Meeting sluiten en rapport genereren"
                  className="px-2 py-1.5 rounded-lg text-[10px] font-bold bg-rose-500/15 border border-rose-400/30 text-rose-300 hover:bg-rose-500/25 transition-colors"
                >
                  📋
                </button>
              </>
            )}

            {/* Verlaten — altijd zichtbaar */}
            <div className="w-px h-5 bg-white/12 mx-0.5" />
            <button
              onClick={async () => {
                if (isHost && isRecordingRef.current) {
                  if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                    mediaRecorderRef.current.stop();
                  }
                  setRecording(false);
                  if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
                  const check = setInterval(() => {
                    if (uploadProgressRef.current !== 'uploading') {
                      clearInterval(check);
                      safeLeave();
                    }
                  }, 500);
                  setTimeout(() => { clearInterval(check); safeLeave(); }, 45000);
                } else if (isHost && uploadProgressRef.current === 'uploading') {
                  const check = setInterval(() => {
                    if (uploadProgressRef.current !== 'uploading') {
                      clearInterval(check);
                      safeLeave();
                    }
                  }, 500);
                  setTimeout(() => { clearInterval(check); safeLeave(); }, 45000);
                } else {
                  await safeLeave();
                }
              }}
              aria-label="Meeting verlaten"
              title={isHost && (isRecording || uploadProgress === 'uploading') ? 'Opname wordt opgeslagen...' : 'Meeting verlaten'}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-red-600 hover:bg-red-700 text-white transition-colors"
            >
              <Icon icon={PhoneOff} size={13} strokeWidth={2} />
              <span className="hidden sm:inline">
                {isHost && isRecording ? 'Stop & Verlaten' : isHost && uploadProgress === 'uploading' ? 'Uploaden...' : 'Verlaten'}
              </span>
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Jitsi of Beamer — op mobiel in fysiek verborgen (leden gebruiken sidebar) */}
        <div className={`flex-1 flex flex-col min-h-0 min-w-0 relative ${meetingMode === 'physical' ? 'hidden md:flex' : ''}`}>
          {meetingMode === 'online' ? (
            <>
          <div className="flex-1 min-h-0 relative">
          {/* ── Camera PiP wrapper — floats above content overlays when active ── */}
          <div
            className={`transition-all duration-300 ease-in-out ${
              contentOverlayActive
                ? 'absolute bottom-4 right-4 z-[60] rounded-2xl overflow-hidden shadow-2xl border border-white/20 ring-1 ring-white/10'
                : 'absolute inset-0'
            }`}
            style={contentOverlayActive ? { width: 300, height: 188 } : {}}
          >
          <JitsiMeeting
            domain="video.superkonnected.com"
            roomName={roomName}
            configOverwrite={{
              startWithAudioMuted: false,
              startWithVideoMuted: false,
              prejoinConfig: { enabled: false },
              prejoinPageEnabled: false,
              requireDisplayName: false,
              disableDeepLinking: true,
              enableWelcomePage: false,
              enableClosePage: false,
              enableInsecureRoomNameWarning: false,
              enableLayerSuspension: true,
              enableNoAudioDetection: true,
              enableNoisyMicDetection: true,
              enableTalkWhileMuted: false,
              enableTCC: true,
              enableRemb: true,
              enableIceRestart: true,
              useStunTurn: true,
              enableRecording: false,
              fileRecordingsEnabled: false,
              liveStreamingEnabled: false,
              localRecording: { disable: true },
              enableScreenSharing: true,
              disableChat: false,
              enableLobbyChat: false,
              enableModeration: isHost,
              hideLobbyButton: true,
              lobby: { enabled: isHost },
              // Eén toolbar — nieuwe stijl (vervangt TOOLBAR_BUTTONS in interfaceConfigOverwrite)
              toolbarButtons: [
                'microphone',
                'camera',
                'desktop',
                'chat',
                'raisehand',
                'tileview',
                'participants-pane',
                'fodeviceselection',
                'videoquality',
                '__end',
              ],
              customTheme: {
                palette: {
                  action01: '#6F879A',
                  action01Hover: '#647D91',
                  action01Disabled: '#B6C4CF',
                  action02: '#86A0B3',
                  ui01: 'rgba(17, 24, 39, 0.70)',
                  ui02: 'rgba(17, 24, 39, 0.82)',
                },
              },
            }}
            interfaceConfigOverwrite={{
              // TOOLBAR_BUTTONS weggehaald — wordt nu via configOverwrite.toolbarButtons gestuurd
              SHOW_JITSI_WATERMARK: false,
              SHOW_WATERMARK_FOR_GUESTS: false,
              SHOW_BRAND_WATERMARK: false,
              SHOW_POWERED_BY: false,
              DISPLAY_WELCOME_PAGE_CONTENT: false,
              APP_NAME: clubName || 'SuperKonnected',
              NATIVE_APP_NAME: clubName || 'SuperKonnected',
              PROVIDER_NAME: clubName || 'SuperKonnected',
              DEFAULT_BACKGROUND: 'rgba(10, 15, 24, 0.62)',
              DEFAULT_REMOTE_DISPLAY_NAME: 'Deelnemer',
              DEFAULT_LOCAL_DISPLAY_NAME: displayName,
              INITIAL_TOOLBAR_TIMEOUT: 20000,
              TOOLBAR_TIMEOUT: 4000,
              TOOLBAR_ALWAYS_VISIBLE: false,
              HIDE_INVITE_MORE_HEADER: true,
              SETTINGS_SECTIONS: ['devices', 'language', 'moderator', 'profile'],
            }}
            userInfo={{
              displayName: displayName,
              email: user?.email || `${user?.uid}@superkonnected.nl`,
            }}
            onApiReady={handleApiReady}
            getIFrameRef={(iframeRef) => {
              if (iframeRef) {
                iframeRef.style.height = '100%';
                iframeRef.style.width = '100%';
                iframeRef.style.border = 'none';
              }
            }}
          />

          {/* ── Video Meeting Overlay — boven Jitsi, alleen online modus ── */}
          {meetingMode === 'online' && !contentOverlayActive && (
            <VideoMeetingOverlay
              isActive={!!livePitchRunningUserId}
              currentEntry={runningEntry ? {
                userId:         runningEntry.userId,
                presenterName:  runningEntry.presenterName,
                slotNumber:     timelineSlots.find(s => s.userId === runningEntry.userId)?.slotNumber ?? 0,
                photoURL:       (runningEntry as any).photoURL,
                searchQuestion: (runningEntry as any).searchQuestion,
                message:        runningEntry.message,
                title:          runningEntry.title,
                company:        (runningEntry as any).company,
                enabled:        runningEntry.enabled !== false,
              } : null}
              nextEntry={(() => {
                const currentIdx = timelineSlots.findIndex(s => s.userId === livePitchRunningUserId);
                const nextSlot = timelineSlots.slice(currentIdx + 1).find(s => s.slotType === 'pitch');
                if (!nextSlot) return null;
                const nextLivePitch = livePitchEntries.find(e => e.userId === nextSlot.userId);
                if (!nextLivePitch) return null;
                return {
                  userId:        nextLivePitch.userId,
                  presenterName: nextLivePitch.presenterName,
                  slotNumber:    nextSlot.slotNumber,
                  photoURL:      (nextLivePitch as any).photoURL,
                  enabled:       nextLivePitch.enabled !== false,
                };
              })()}
              remainingSeconds={livePitchRemainingSeconds}
              totalSeconds={livePitchTimerDurationSeconds}
              clubLogoUrl={clubLogoUrl}
              settings={overlaySettings || {}}
            />
          )}
          </div>{/* end PiP wrapper */}

          {/* ── Slideshow overlay — over Jitsi video ── */}
          {slideshowEnabled && showSlideshow && slides.length > 0 && (() => {
            const currentSlide = slides[slideIndex];
            const duration = currentSlide?.durationSeconds ?? slideDefaultDuration;
            return (
              <div className="absolute inset-0 z-30 bg-black flex flex-col items-center justify-center">
                {/* Auto-advance timer bar (host-only, synced via Firestore) */}
                {slideAutoPlay && currentSlide?.type !== 'video' && (
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/10">
                    <div
                      key={`${slideIndex}-${duration}`}
                      className="h-full bg-white/60 rounded-full"
                      style={{ animation: `autoSlideProgress ${duration}s linear forwards` }}
                      onAnimationEnd={() => {
                        if (!slideAutoPlayRef.current) return;
                        const next = slideIndex < slides.length - 1 ? slideIndex + 1 : 0;
                        setRemoteSlideIndex(next);
            }}
          />
        </div>
                )}

                {currentSlide?.type === 'video' ? (
                  <video
                    src={currentSlide.url}
                    controls
                    autoPlay
                    className="max-h-[90%] max-w-[95%] rounded-lg"
                    onEnded={() => {
                      if (!slideAutoPlayRef.current) return;
                      const next = slideIndex < slides.length - 1 ? slideIndex + 1 : 0;
                      setRemoteSlideIndex(next);
                    }}
                  />
                ) : (
                  <img src={currentSlide?.url} alt={currentSlide?.caption ?? ''} className="max-h-[90%] max-w-[95%] object-contain rounded-lg" />
                )}

                {/* Caption */}
                {currentSlide?.caption && (
                  <div className="absolute bottom-16 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/60 rounded-xl backdrop-blur-sm max-w-[80%] text-center">
                    <p className="text-sm text-white font-medium">{currentSlide.caption}</p>
                  </div>
                )}

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
                  <button onClick={() => setRemoteSlideIndex(Math.max(0, slideIndex - 1))} disabled={slideIndex === 0}
                    className="w-8 h-8 rounded-full bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 flex items-center justify-center transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <span className="text-xs text-white/60 font-medium tabular-nums">{slideIndex + 1} / {slides.length}</span>
                  <button onClick={() => setRemoteSlideIndex(Math.min(slides.length - 1, slideIndex + 1))} disabled={slideIndex >= slides.length - 1}
                    className="w-8 h-8 rounded-full bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 flex items-center justify-center transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </button>
                  {isHost && (
                    <button onClick={() => toggleSlideshowActive(false)}
                      className="ml-4 h-7 px-3 bg-white/15 hover:bg-white/25 text-white text-[10px] font-semibold rounded-lg transition-colors">
                      Sluiten
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Floating quick-share panel verwijderd — functionaliteit zit in de sidebar */}

          {/* ── DocumentView overlay — gedeeld scherm ── */}
          {showDocView && docViewEnabled && sharedDocs.length > 0 && (() => {
            const current = sharedDocs[activeDocIdx] ?? sharedDocs[0];
            return (
              <div className="absolute inset-0 z-40 bg-black/92 flex flex-col items-center justify-center backdrop-blur-sm">
                {/* Render based on type */}
                {current.type === 'image' && (
                  <img src={current.url} alt={current.name}
                    className="max-h-[88%] max-w-[95%] object-contain rounded-xl shadow-2xl" />
                )}
                {current.type === 'video' && (
                  <video src={current.url} controls autoPlay
                    className="max-h-[88%] max-w-[95%] rounded-xl shadow-2xl" />
                )}
                {current.type === 'pdf' && (
                  <iframe src={current.url} title={current.name}
                    className="w-full h-[88%] rounded-xl border-0" />
                )}
                {current.type === 'other' && (
                  <div className="flex flex-col items-center gap-3 text-white/60">
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm">{current.name}</span>
                    <a href={current.url} download={current.name} target="_blank" rel="noreferrer"
                      className="h-8 px-4 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-medium transition-colors">
                      Downloaden
                    </a>
                  </div>
                )}

                {/* File name + uploader */}
                <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-black/60 rounded-xl backdrop-blur-sm max-w-[80%]">
                  {current.uploadedByAvatar ? (
                    <img src={current.uploadedByAvatar} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center shrink-0">
                      <span className="text-[8px] font-bold text-gray-300">{(current.uploadedByName || '?')[0].toUpperCase()}</span>
                    </div>
                  )}
                  <span className="text-[11px] text-white/70 truncate">{current.name}</span>
                  <span className="text-[10px] text-white/35 shrink-0">· {current.uploadedByName}</span>
                </div>

                {/* Controls */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                  {sharedDocs.length > 1 && (
                    <>
                      <button onClick={() => toggleDocViewActive(true, Math.max(0, activeDocIdx - 1))}
                        disabled={activeDocIdx === 0}
                        className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white disabled:opacity-30 flex items-center justify-center transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="text-xs text-white/50 tabular-nums">{activeDocIdx + 1} / {sharedDocs.length}</span>
                      <button onClick={() => toggleDocViewActive(true, Math.min(sharedDocs.length - 1, activeDocIdx + 1))}
                        disabled={activeDocIdx >= sharedDocs.length - 1}
                        className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white disabled:opacity-30 flex items-center justify-center transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  )}
                  <a href={current.url} download={current.name} target="_blank" rel="noreferrer"
                    className="h-7 px-3 bg-white/10 hover:bg-white/20 text-white text-[10px] font-medium rounded-lg transition-colors flex items-center gap-1.5">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </a>
                  <button onClick={() => toggleDocViewActive(false)}
                    className="h-7 px-3 bg-white/10 hover:bg-white/20 text-white text-[10px] font-semibold rounded-lg transition-colors">
                    Sluiten
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ── Website URL overlay — los van "gedeeld scherm" (bestanden); sync via urlActive ── */}
          {showUrlView && sharedUrl && (
            <div className="absolute inset-0 z-41 bg-gray-950 flex flex-col">
              {/* Top bar */}
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-900/90 border-b border-white/5 shrink-0">
                {urlFrameState === 'loading' && (
                  <div className="w-3 h-3 rounded-full border-2 border-violet-400/40 border-t-violet-400 animate-spin shrink-0" />
                )}
                {urlFrameState === 'loaded' && (
                  <svg className="w-3.5 h-3.5 text-emerald-400/70 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                  </svg>
                )}
                {urlFrameState === 'blocked' && (
                  <svg className="w-3.5 h-3.5 text-amber-400/70 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                )}
                <span className="text-[10px] text-white/50 truncate flex-1">{sharedUrl}</span>
                <a href={sharedUrl} target="_blank" rel="noreferrer"
                  className="h-6 px-2 rounded bg-white/10 hover:bg-white/20 text-white text-[10px] font-medium flex items-center gap-1 transition-colors shrink-0">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Nieuw tabblad
                </a>
                <button onClick={closeUrlView}
                  className="h-6 px-2 rounded bg-white/10 hover:bg-white/20 text-white text-[10px] font-semibold transition-colors shrink-0">
                  Sluiten
                </button>
              </div>

              {/* Geblokkeerd melding */}
              {urlFrameState === 'blocked' && (
                <div className="shrink-0 flex items-center gap-3 px-4 py-2.5 bg-amber-950/60 border-b border-amber-500/20">
                  <span className="text-[11px] text-amber-300/90 flex-1">
                    Deze website blokkeert embedding (bijv. Google, LinkedIn). Gebruik "Nieuw tabblad" en deel je scherm via de schermdeelknop in de videocall.
                  </span>
                  <a href={sharedUrl} target="_blank" rel="noreferrer"
                    className="h-6 px-3 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-[10px] font-semibold transition-colors shrink-0">
                    Open ↗
                  </a>
                </div>
              )}

              {/* iframe */}
              <div className="flex-1 relative">
                {urlFrameState === 'loading' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-950 z-10 pointer-events-none">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 rounded-full border-2 border-violet-400/30 border-t-violet-400 animate-spin" />
                      <span className="text-[11px] text-white/30">Pagina laden…</span>
                    </div>
                  </div>
                )}
                <iframe
                  key={sharedUrl}
                  src={sharedUrl}
                  title="Gedeelde website"
                  className="w-full h-full border-0 bg-gray-950"
                  // Geen sandbox: strenge sandbox breekt veel externe sites + soms geen betrouwbare onLoad
                  onLoad={() => {
                    setUrlFrameState((prev) => (prev === 'loading' ? 'loaded' : prev));
                  }}
                  onError={() => setUrlFrameState('blocked')}
                />
              </div>
            </div>
          )}

          {/* ── Widgets linksonder — RSVP / volgende meeting ── */}
          {entityId && (
            <div className="absolute bottom-4 left-4 z-[55] flex flex-col items-start gap-2">
              <MeetingWidgetsOverlay
                entityId={entityId}
                entityType={(entityType as 'mastermind' | 'community' | 'networkclub') ?? 'mastermind'}
                meetingId={roomName}
                clubId={clubId ?? null}
                clubName={clubName}
                userId={user?.uid ?? null}
                userDisplayName={displayName}
              />
            </div>
          )}

          {/* ── Session timer rechtsonder ── */}
          {entityId && timerOverlayEnabled && (
            <div className="absolute bottom-4 right-4 z-[55] flex flex-col items-end gap-2">
              <SessionTimerOverlay
                entityId={entityId}
                entityType={(entityType as 'mastermind' | 'community' | 'networkclub') ?? 'mastermind'}
                meetingId={roomName}
                memberCount={memberCount ?? memberNames.length}
                memberNames={memberNames}
                memberAvatars={memberAvatars}
                memberMeta={memberMeta}
                isHost={canControlSessionTimeline}
              />
            </div>
          )}

          {/* ── Blok countdown indicator — midden-links in beeld ── */}
          {entityId && timerOverlayEnabled && (
            <BlockCountdownOverlay
              entityId={entityId}
              entityType={(entityType as 'mastermind' | 'community' | 'networkclub') ?? 'mastermind'}
              meetingId={roomName}
              memberCount={memberCount ?? memberNames.length}
              memberNames={memberNames}
              memberAvatars={memberAvatars}
            />
          )}

          {/* ── Join toasts — lid komt online ── */}
          {joinToasts.length > 0 && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 pointer-events-none">
              {joinToasts.map((t) => (
                <div key={t.id}
                  className="flex items-center gap-2.5 px-4 py-2 bg-black/70 backdrop-blur-md rounded-full shadow-lg pointer-events-auto"
                  style={{ animation: 'joinToastSlideIn 0.35s ease-out, joinToastFadeOut 0.4s ease-in 3.4s forwards' }}>
                  {t.avatar ? (
                    <img src={t.avatar} alt="" className="w-6 h-6 rounded-full object-cover ring-2 ring-emerald-400/60" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-emerald-500/30 ring-2 ring-emerald-400/60 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-emerald-300">{(t.name || '?')[0].toUpperCase()}</span>
                    </div>
                  )}
                  <span className="text-xs font-medium text-white whitespace-nowrap">{t.name}</span>
                  <span className="text-[10px] text-emerald-400 font-medium">is erbij</span>
                </div>
              ))}
              <style>{`
                @keyframes joinToastSlideIn {
                  from { opacity: 0; transform: translateY(-12px) scale(0.92); }
                  to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes joinToastFadeOut {
                  from { opacity: 1; transform: translateY(0) scale(1); }
                  to   { opacity: 0; transform: translateY(-8px) scale(0.95); }
                }
              `}</style>
            </div>
          )}
          </div>
            <div className="shrink-0 border-t border-white/10 bg-[#0d1420]/95 px-3 py-2 max-h-[min(28vh,220px)] overflow-y-auto">
              <div className="text-[10px] font-semibold text-white/50 uppercase tracking-wide mb-1">Mijn agenda punten</div>
              <p className="text-[9px] text-white/30 leading-snug mb-1.5">Afgevinkt in de zijbalk; hier blijven ze staan tot je ze terugzet.</p>
              {myAgendaPointsDone.length === 0 ? (
                <p className="text-[10px] text-white/35 leading-snug">
                  {openAgendaItems.some((i) => i.submittedByUid === user?.uid)
                    ? 'Vink je eigen punten links af om ze hier te zien.'
                    : 'Nog geen afgevinkte punten. Voeg agendapunten toe via de zijbalk.'}
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {myAgendaPointsDone.map((p) => (
                    <li key={p.id} className="flex items-start gap-2 justify-between">
                      <span className="text-[11px] text-white/85 leading-snug min-w-0 flex-1">{linkifyMeetingText(p.title)}</span>
                      <button
                        type="button"
                        onClick={() => void handleUndoAgendaItemDone(p.id)}
                        className="shrink-0 text-[9px] font-semibold text-violet-300/90 hover:text-violet-200 underline underline-offset-2"
                      >
                        Ongedaan
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            </>
          ) : (
            <BeamerPitchView
              runningEntry={runningEntry}
              remainingSeconds={livePitchRemainingSeconds}
              totalSeconds={livePitchTimerDurationSeconds}
              timelineSlots={timelineSlots}
              isHost={isHost}
              primaryColor={livePitchTheme.primary}
              accentColor={livePitchTheme.accent}
              overlaySettings={overlaySettings || {}}
              clubLogoUrl={clubLogoUrl}
              templateBackgroundUrl={(overlaySettings as any)?.templateBackgroundUrl || undefined}
              templatePdfUrl={templatePdfUrl}
              activePageNumber={activePageNumber}
              pitchTemplatePdfUrl={pitchTemplatePdfUrl}
              pitchTemplateZones={pitchTemplateZones}
              nextEntry={(() => {
                const currentIdx = timelineSlots.findIndex(s => s.userId === livePitchRunningUserId);
                const nextSlot = timelineSlots.slice(currentIdx + 1).find(s => s.slotType === 'pitch');
                if (!nextSlot) return null;
                const nextLP = livePitchEntries.find(e => e.userId === nextSlot.userId);
                return nextLP ? { ...nextLP, photoURL: (nextLP as any).photoURL } : null;
              })()}
              nextSlotNumber={(() => {
                const currentIdx = timelineSlots.findIndex(s => s.userId === livePitchRunningUserId);
                const nextSlot = timelineSlots.slice(currentIdx + 1).find(s => s.slotType === 'pitch');
                return nextSlot?.slotNumber ?? 0;
              })()}
              transitionActive={transitionActive}
              transitionNextEntry={transitionNextEntry}
              transitionNextSlotNumber={
                transitionNextEntry
                  ? (timelineSlots.find((s) => s.userId === transitionNextEntry.userId)?.slotNumber ?? 0)
                  : 0
              }
              totalPitchSlots={timelineSlots.filter((s) => s.slotType === 'pitch').length}
              transitionDurationSeconds={transitionDurationSeconds}
              transitionMusicUrl={transitionMusicUrl}
              transitionMusicVolume={transitionMusicVolume}
              onTransitionComplete={() => void handleTransitionComplete()}
              onStopPitch={() => void stopLivePitch(true)}
            />
          )}
        </div>

        {/* Sidebar — op mobiel in fysiek: volle breedte als hoofdweergave */}
        {showAgenda && canUseMeetingHostSidebar && (
          <div className={`bg-gray-800 text-white border-l border-gray-700 flex flex-col min-h-0 min-w-0 ${meetingMode === 'physical' ? 'w-full md:w-80' : 'w-80'}`}>

            {/* ── Sidebar header met sluitknop ── */}
            <div className="shrink-0 flex items-center justify-between px-3 py-2 border-b border-white/10 bg-gray-900/40">
              <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest select-none">Zijpaneel</span>
              <button
                type="button"
                onClick={() => setShowAgenda(false)}
                className="p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                title="Zijpaneel sluiten"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* ── Live timer + next — fysiek, alleen op mobiel (desktop heeft de volledige session engine in de sidebar) ── */}
            {meetingMode === 'physical' && (
              <div className="shrink-0 border-b border-white/10 bg-[#0d1420] md:hidden">
                {/* Session timer (structured growth blok-timer) */}
                {entityId && timerOverlayEnabled && (
                  <div className="flex items-center justify-center py-3">
                    <SessionTimerOverlay
                      entityId={entityId}
                      entityType={(entityType as 'mastermind' | 'community' | 'networkclub') ?? 'mastermind'}
                      meetingId={roomName}
                      memberCount={memberCount ?? memberNames.length}
                      memberNames={memberNames}
                      memberAvatars={memberAvatars}
                      memberMeta={memberMeta}
                      isHost={canControlSessionTimeline}
                    />
                  </div>
                )}

                {/* Actieve pitch: timer + next coming up */}
                {runningEntry && (
                  <div className="px-4 pb-3 space-y-2">
                    <div className="flex items-center gap-3">
                      {(runningEntry as any).photoURL ? (
                        <img src={(runningEntry as any).photoURL} alt="" className="w-10 h-10 rounded-xl object-cover border border-white/20 shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                          <span className="text-sm font-bold text-white/80">{runningEntry.presenterName[0]?.toUpperCase()}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-white truncate">{runningEntry.presenterName}</p>
                        {runningEntry.title && <p className="text-[10px] text-white/50 truncate">{runningEntry.title}</p>}
                      </div>
                      <div className={`text-right shrink-0 tabular-nums ${livePitchRemainingSeconds <= 10 ? 'text-red-400' : livePitchRemainingSeconds <= 30 ? 'text-amber-300' : 'text-white'}`}>
                        <p className="text-2xl font-black leading-none">
                          {Math.floor(livePitchRemainingSeconds / 60).toString().padStart(2, '0')}:{(livePitchRemainingSeconds % 60).toString().padStart(2, '0')}
                        </p>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${livePitchTimerDurationSeconds > 0 ? Math.max(0, Math.min(100, (livePitchRemainingSeconds / livePitchTimerDurationSeconds) * 100)) : 0}%`,
                          backgroundColor: livePitchRemainingSeconds <= 10 ? '#f87171' : livePitchRemainingSeconds <= 30 ? '#fbbf24' : '#34d399',
                        }}
                      />
                    </div>
                    {/* Next coming up */}
                    {(() => {
                      const currentIdx = timelineSlots.findIndex(s => s.userId === livePitchRunningUserId);
                      const nextSlot = timelineSlots.slice(currentIdx + 1).find(s => s.slotType === 'pitch');
                      const nextLP = nextSlot ? livePitchEntries.find(e => e.userId === nextSlot.userId) : null;
                      if (!nextLP) return null;
                      return (
                        <div className="flex items-center gap-2 pt-1">
                          <span className="text-[9px] font-bold text-white/35 uppercase tracking-wider">Next</span>
                          {(nextLP as any).photoURL ? (
                            <img src={(nextLP as any).photoURL} alt="" className="w-6 h-6 rounded-lg object-cover border border-white/15 shrink-0" />
                          ) : (
                            <div className="w-6 h-6 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
                              <span className="text-[9px] font-bold text-white/60">{nextLP.presenterName[0]?.toUpperCase()}</span>
                            </div>
                          )}
                          <span className="text-[10px] text-white/60 truncate">{nextLP.presenterName}</span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 custom-scrollbar">
              {/* Meeting tijdlijn + timer — standaard ingeklapt */}
              <div className="bg-gray-800/60 rounded-xl overflow-hidden border border-white/5 shrink-0">
                <button
                  type="button"
                  onClick={() => toggleSection('timeline')}
                  className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors"
                >
                  <Icon icon={Calendar} size={14} strokeWidth={2} className="text-white/50 shrink-0" />
                  <span className="text-[11px] font-semibold text-white/70 uppercase tracking-wider flex-1 text-left">Meeting tijdlijn</span>
                  <svg className={`w-3 h-3 text-white/30 transition-transform shrink-0 ${!collapsedSections.has('timeline') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {!collapsedSections.has('timeline') && (
                  <div className="border-t border-white/5 p-3 space-y-3">
                    {countdownRunningRemote && !showCountdown && entityId && (
                      <button
                        type="button"
                        onClick={() => setShowCountdown(true)}
                        className="w-full rounded-xl bg-emerald-500/20 border border-emerald-400/35 px-3 py-2.5 text-left hover:bg-emerald-500/30 transition-colors"
                      >
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300">Countdown loopt</div>
                        <div className="text-xs text-white/90 mt-0.5 font-medium">Tik hier om live mee te kijken</div>
                      </button>
                    )}
                    {isHost && (
                      <div className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-white/5">
                        <span className="text-xs text-white/60">Timer overlay op video</span>
                        <button
                          type="button"
                          onClick={async () => {
                            const next = !timerOverlayEnabled;
                            setTimerOverlayEnabled(next);
                            if (entityId) {
                              await setDoc(doc(db, 'meetingSettings', entityId), { timerOverlayEnabled: next, updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
                            }
                          }}
                          className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${timerOverlayEnabled ? 'bg-emerald-500' : 'bg-gray-600'}`}
                          title={timerOverlayEnabled ? 'Timer overlay uitzetten' : 'Timer overlay aanzetten'}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${timerOverlayEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    )}
                    {entityId && (
                      <SessionStructureEngine
                        entityId={entityId}
                        entityType={(entityType as 'mastermind' | 'community' | 'networkclub') ?? 'mastermind'}
                        isAdmin={canControlSessionTimeline}
                        userId={user?.uid ?? ''}
                        liveMode={true}
                        meetingId={roomName}
                        memberCount={memberCount ?? memberNames.length}
                        memberNames={memberNames}
                        memberAvatars={memberAvatars}
                      />
                    )}
                  </div>
                )}
              </div>

              {(commitmentReviewEnabled || isHost) && entityId && user?.uid && (
                <div className={`bg-gray-800/60 rounded-xl overflow-hidden border border-white/5 relative shrink-0 ${!commitmentReviewEnabled ? 'opacity-50' : ''}`}>
                  {!commitmentReviewEnabled && isHost && (
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 backdrop-blur-sm pointer-events-none">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400/80" />
                      <span className="text-[8px] font-bold text-amber-300 uppercase tracking-wider">Uit voor leden</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleSection('commitments')}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5 text-amber-400/70 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-[11px] font-semibold text-white/70 uppercase tracking-wider flex-1 text-left">Commitments</span>
                    <svg className={`w-3 h-3 text-white/30 transition-transform shrink-0 ${!collapsedSections.has('commitments') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {!collapsedSections.has('commitments') && (
                    <div className="border-t border-white/5 p-2 relative">
                      <LiveCommitmentReview
                        entityId={entityId}
                        meetingId={roomName}
                        currentUserId={user.uid}
                        isAdmin={isHost}
                        memberNames={memberNames}
                        memberAvatars={memberAvatars}
                      />
                    </div>
                  )}
                </div>
              )}

              {(slideshowEnabled || isHost) && slides.length > 0 && (
                <div className={`bg-gray-800/60 rounded-xl overflow-hidden border border-white/5 relative shrink-0 ${!slideshowEnabled ? 'opacity-50' : ''}`}>
                  {!slideshowEnabled && isHost && (
                    <div className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 backdrop-blur-sm pointer-events-none">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400/80" />
                      <span className="text-[8px] font-bold text-amber-300 uppercase tracking-wider">Uit voor leden</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => toggleSection('slideshow')}
                      className="flex flex-1 min-w-0 items-center gap-2 text-left rounded-lg -my-1 -ml-1 pl-1 py-1 pr-2 hover:bg-white/5 transition-colors"
                    >
                      <Icon icon={Monitor} size={14} strokeWidth={2} className="text-white/40 shrink-0" />
                      <span className="text-[11px] font-semibold text-white/70 uppercase tracking-wider flex-1 truncate">Slideshow</span>
                      <span className="text-[9px] text-white/30 shrink-0">{slides.length} slide{slides.length !== 1 ? 's' : ''}</span>
                      <svg className={`w-3 h-3 text-white/30 transition-transform shrink-0 ${!collapsedSections.has('slideshow') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isHost && (
                      <button
                        type="button"
                        onClick={() => toggleSlideshowActive(!showSlideshow)}
                        className={`h-6 px-2 rounded-lg text-[10px] font-semibold transition-colors shrink-0 ${showSlideshow ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30' : 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30'}`}
                      >
                        {showSlideshow ? '■' : '▶'}
                      </button>
                    )}
                  </div>
                  {!collapsedSections.has('slideshow') && (
                    <div className="border-t border-white/5">
                      <div className="flex gap-1.5 p-2 overflow-x-auto custom-scrollbar">
                        {slides.map((s, i) => (
                          <div
                            key={`slide-${i}`}
                            role="button"
                            tabIndex={0}
                            className={`relative shrink-0 w-14 h-10 rounded overflow-hidden border-2 cursor-pointer transition-all ${i === slideIndex && showSlideshow ? 'border-emerald-400 ring-1 ring-emerald-400/40' : 'border-white/10 hover:border-white/25'}`}
                            onClick={() => setRemoteSlideIndex(i)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') setRemoteSlideIndex(i);
                            }}
                          >
                            {s.type === 'video' ? (
                              <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white/30" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                              </div>
                            ) : (
                              <img src={s.url} alt="" className="w-full h-full object-cover" />
                            )}
                          </div>
                        ))}
                      </div>
                      {/* Vorige / Volgende navigatie */}
                      {isHost && slides.length > 1 && (
                        <div className="flex items-center justify-between gap-2 px-2 pb-2">
                          <button
                            type="button"
                            onClick={() => setRemoteSlideIndex(Math.max(0, slideIndex - 1))}
                            disabled={slideIndex <= 0}
                            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-white/8 hover:bg-white/15 text-white/70 hover:text-white border border-white/10"
                            title="Vorige slide"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                            <span>Vorige</span>
                          </button>
                          <span className="text-[10px] text-white/40 tabular-nums font-medium shrink-0">
                            {slideIndex + 1} / {slides.length}
                          </span>
                          <button
                            type="button"
                            onClick={() => setRemoteSlideIndex(Math.min(slides.length - 1, slideIndex + 1))}
                            disabled={slideIndex >= slides.length - 1}
                            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed bg-white/8 hover:bg-white/15 text-white/70 hover:text-white border border-white/10"
                            title="Volgende slide"
                          >
                            <span>Volgende</span>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                      )}
                      <p className="text-[8px] text-white/20 text-center pb-1.5">Slides beheren via Instellingen op countdown-scherm</p>
                    </div>
                  )}
                </div>
              )}

              {/* ── SECTIE: Gedeeld Scherm ─────────────────────────────── */}
              {(docViewEnabled || isHost) && (
                <div className={`bg-gray-800/60 rounded-xl mb-3 overflow-hidden border border-white/5 relative ${!docViewEnabled && isHost ? 'opacity-60' : ''}`}>
                    <button
                    onClick={() => toggleSection('gedeeldScherm')}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5 text-sky-400/60 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4" />
                    </svg>
                    <span className="text-[11px] font-semibold text-white/70 uppercase tracking-wider flex-1 text-left">Gedeeld scherm</span>
                    {sharedDocs.length > 0 && (
                      <span className="text-[9px] bg-sky-500/20 text-sky-300 px-1.5 py-0.5 rounded-full font-semibold">{sharedDocs.length}</span>
                    )}
                    {isHost && (
                      <div className="flex items-center gap-2 shrink-0">
                        {/* Presentatie toestemming voor leden */}
                        <span
                          role="switch"
                          aria-checked={memberUploadEnabled}
                          title={memberUploadEnabled ? 'Leden mogen uploaden (klik om uit te zetten)' : 'Leden mogen niet uploaden (klik om aan te zetten)'}
                          onClick={(e) => { e.stopPropagation(); void toggleMemberUploadEnabled(!memberUploadEnabled); }}
                          className={`relative w-7 h-4 rounded-full transition-colors ${memberUploadEnabled ? 'bg-amber-500' : 'bg-white/10'}`}
                        >
                          <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${memberUploadEnabled ? 'left-3.5' : 'left-0.5'}`} />
                        </span>
                        {/* Gedeeld Scherm aan/uit */}
                        <span
                          role="switch"
                          aria-checked={docViewEnabled}
                          onClick={(e) => { e.stopPropagation(); toggleDocViewEnabled(!docViewEnabled); }}
                          className={`relative w-7 h-4 rounded-full transition-colors shrink-0 ${docViewEnabled ? 'bg-emerald-500' : 'bg-white/15'}`}
                        >
                          <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${docViewEnabled ? 'left-3.5' : 'left-0.5'}`} />
                        </span>
                      </div>
                    )}
                    <svg className={`w-3 h-3 text-white/30 transition-transform shrink-0 ${!collapsedSections.has('gedeeldScherm') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {!collapsedSections.has('gedeeldScherm') && (
                    <div className="border-t border-white/5">
                      {/* ── Wat kun je delen? info-blok ── */}
                      <div className="mx-2.5 mt-2.5 mb-1 rounded-xl bg-sky-950/40 border border-sky-500/15 px-3 py-2.5 space-y-1.5">
                        <p className="text-[9px] font-bold text-sky-400/70 uppercase tracking-wider mb-1">Wat kun je delen?</p>
                        <div className="flex items-start gap-2">
                          <span className="text-[11px] shrink-0">📄</span>
                          <div>
                            <p className="text-[10px] font-semibold text-white/60">Bestanden uploaden</p>
                            <p className="text-[9px] text-white/30 leading-snug">PDF · Afbeelding · Video (mp4) · Word · PowerPoint</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-[11px] shrink-0">🌐</span>
                          <div>
                            <p className="text-[10px] font-semibold text-white/60">Website of link (hieronder)</p>
                            <p className="text-[9px] text-white/30 leading-snug">YouTube · Notion · Externe site · ⚠️ sommige sites blokkeren embedding, gebruik "Open in tabblad"</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-[11px] shrink-0">🖥️</span>
                          <div>
                            <p className="text-[10px] font-semibold text-white/60">Scherm delen → via de schermdeelknop in de videocall</p>
                            <p className="text-[9px] text-white/30 leading-snug">Voor live software, browsers of andere bronnen</p>
                          </div>
                        </div>
                      </div>
                      {/* Presentatie toestemming info voor leden */}
                      {!isHost && memberUploadEnabled && (
                        <div className="mx-2.5 mb-1 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                          <svg className="w-3 h-3 text-amber-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-[9px] text-amber-300/80">De host heeft presentatie-uploads voor iedereen ingeschakeld.</p>
                        </div>
                      )}
                      {!isHost && !memberUploadEnabled && (
                        <div className="mx-2.5 mb-1 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/4 border border-white/8">
                          <svg className="w-3 h-3 text-white/25 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <p className="text-[9px] text-white/30">Alleen de host kan uploaden. Wacht op toestemming.</p>
                        </div>
                      )}
                      {/* Host: label voor de amber toggle */}
                      {isHost && (
                        <div className="mx-2.5 mb-1 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/3">
                          <span className={`w-2 h-2 rounded-full shrink-0 ${memberUploadEnabled ? 'bg-amber-400' : 'bg-white/20'}`} />
                          <p className="text-[9px] text-white/40">
                            Presentatie toestemming: <span className={memberUploadEnabled ? 'text-amber-300 font-semibold' : 'text-white/30'}>
                              {memberUploadEnabled ? 'Aan · leden mogen uploaden' : 'Uit · alleen host kan uploaden'}
                            </span>
                          </p>
                        </div>
                      )}
                      <div className="flex items-center justify-between px-3 py-2">
                        <input type="file" ref={docFileRef} className="hidden" accept="image/*,video/mp4,video/webm,application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx" multiple onChange={handleDocUpload} />
                        {/* Upload knop — zichtbaar voor host altijd, voor leden alleen met toestemming */}
                        {(isHost || memberUploadEnabled) && (
                          <button
                            onClick={() => docFileRef.current?.click()}
                            disabled={docUploading}
                            title="Bestand uploaden om te delen"
                            className="h-6 px-2.5 rounded-lg bg-sky-500/20 text-sky-300 text-[10px] font-medium hover:bg-sky-500/30 transition-colors disabled:opacity-40 flex items-center gap-1"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h.586a1 1 0 01.707.293l2.414 2.414A1 1 0 009.414 8H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                            </svg>
                            {docUploading ? 'Uploaden…' : 'Upload'}
                          </button>
                        )}
                      </div>
                      {docUploadError && (
                        <div className="mx-2 mb-2 px-2 py-1.5 rounded-lg bg-red-500/15 border border-red-500/25 flex items-start gap-2">
                          <svg className="w-3 h-3 text-red-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <p className="text-[10px] text-red-300 leading-snug">{docUploadError}</p>
                          <button onClick={() => setDocUploadError(null)} className="ml-auto text-red-400/60 hover:text-red-300 shrink-0">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      )}
                      {sharedDocs.length > 0 ? (
                        <div className="p-2 space-y-1.5 max-h-[220px] overflow-y-auto custom-scrollbar">
                          {sharedDocs.map((d, i) => {
                            const isActive = i === activeDocIdx && showDocView;
                            const ext = d.name.split('.').pop()?.toLowerCase() ?? '';
                            return (
                              <div key={i} className={`rounded-lg border transition-all ${isActive ? 'border-sky-500/40 bg-sky-950/30' : 'border-white/5 bg-white/3'}`}>
                                <div className="flex items-center gap-2 p-1.5">
                                  <div className="shrink-0 w-10 h-8 rounded overflow-hidden bg-gray-700 flex items-center justify-center">
                                    {d.type === 'image' ? <img src={d.url} alt="" className="w-full h-full object-cover" />
                                     : d.type === 'video' ? <svg className="w-4 h-4 text-white/40" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                     : d.type === 'pdf' ? <span className="text-[9px] font-bold text-red-400">PDF</span>
                                     : <span className="text-[9px] font-bold text-white/40 uppercase">{ext || 'DOC'}</span>}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[10px] text-white/70 truncate">{d.name}</p>
                                    <div className="flex items-center gap-1 mt-0.5">
                                      {d.uploadedByAvatar ? <img src={d.uploadedByAvatar} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                                       : <div className="w-3.5 h-3.5 rounded-full bg-gray-600 flex items-center justify-center"><span className="text-[7px] font-bold text-gray-300">{(d.uploadedByName || '?')[0].toUpperCase()}</span></div>}
                                      <span className="text-[9px] text-white/30 truncate">{d.uploadedByName}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => isActive ? toggleDocViewActive(false) : toggleDocViewActive(true, i)} title={isActive ? 'Verberg' : 'Toon in meeting'} className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${isActive ? 'text-sky-400 bg-sky-500/15' : 'text-white/25 hover:text-sky-300'}`}>
                                      {isActive
                                        ? <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                                        : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}
                                    </button>
                                    <a href={d.url} download={d.name} target="_blank" rel="noreferrer" title="Downloaden" className="w-6 h-6 rounded flex items-center justify-center text-white/25 hover:text-emerald-300 transition-colors">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                    </a>
                                    {(isHost || d.uploadedByUid === user?.uid) && (
                                      <button onClick={() => deleteSharedDoc(i)} title="Verwijderen" className="w-6 h-6 rounded flex items-center justify-center text-white/20 hover:text-red-400 transition-colors">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="px-3 py-4 flex flex-col items-center gap-1.5 text-center">
                          <svg className="w-6 h-6 text-white/10" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h.586a1 1 0 01.707.293l2.414 2.414A1 1 0 009.414 8H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                          </svg>
                          <p className="text-[10px] text-white/25 leading-snug">
                            Klik op <strong className="text-white/40">Upload</strong> om een afbeelding, video of PDF te delen.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── SECTIE: Website ──────────────────────────────────────── */}
              {(linkOpenerEnabled || isHost) && (
                <div className={`bg-gray-800/60 rounded-xl mb-3 overflow-hidden border border-white/5 relative ${!linkOpenerEnabled ? 'opacity-60' : ''}`}>
                  <button
                    onClick={() => toggleSection('website')}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5 text-violet-400/60 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                    </svg>
                    <span className="text-[11px] font-semibold text-white/70 uppercase tracking-wider flex-1 text-left">Website</span>
                    {showUrlView && (
                      <span className="flex items-center gap-1 text-[9px] text-violet-400 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                        Live
                      </span>
                    )}
                    <svg className={`w-3 h-3 text-white/30 transition-transform shrink-0 ${!collapsedSections.has('website') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {!collapsedSections.has('website') && (
                    <div className="border-t border-white/5 px-3 py-2.5 space-y-2">

                      {/* Opgeslagen links (max 3) */}
                      {savedLinks.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-[9px] font-semibold text-white/30 uppercase tracking-wider">Opgeslagen links</p>
                          {savedLinks.map(link => {
                            const isLive = sharedUrl === link.url && showUrlView;
                            return (
                              <div key={link.id} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border transition-all ${isLive ? 'border-violet-500/40 bg-violet-950/30' : 'border-white/8 bg-white/3 hover:border-white/15'}`}>
                                <svg className="w-3 h-3 text-violet-400/50 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3" />
                                </svg>
                                <span className="text-[10px] text-white/70 truncate flex-1 font-medium">{link.label}</span>
                                {/* Toon live knop */}
                                <button
                                  type="button"
                                  onClick={() => isLive ? closeUrlView() : pushUrlLive(link.url)}
                                  title={isLive ? 'Verberg voor iedereen' : 'Toon live voor iedereen'}
                                  className={`h-5 px-1.5 rounded text-[9px] font-semibold shrink-0 transition-colors ${isLive ? 'bg-violet-500/20 text-violet-300' : 'bg-white/8 text-white/40 hover:bg-violet-500/15 hover:text-violet-300'}`}
                                >
                                  {isLive ? '● Live' : '▶ Toon'}
                                </button>
                                <a href={link.url} target="_blank" rel="noreferrer" title="Open in nieuw tabblad" className="w-5 h-5 rounded flex items-center justify-center text-white/20 hover:text-white/60 transition-colors shrink-0">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                </a>
                                {isHost && (
                                  <button type="button" onClick={() => handleRemoveSavedLink(link.id)} title="Verwijder" className="w-5 h-5 rounded flex items-center justify-center text-white/15 hover:text-red-400 transition-colors shrink-0">
                                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Link toevoegen aan lijst (max 3) */}
                      {isHost && savedLinks.length < 3 && (
                        <div>
                          {showAddLink ? (
                            <div className="space-y-1.5 p-2 rounded-xl bg-gray-700/40 border border-white/8">
                              <input
                                autoFocus
                                type="text"
                                value={newLinkUrl}
                                onChange={e => setNewLinkUrl(e.target.value)}
                                placeholder="https://website.nl of link plakken…"
                                className="w-full h-7 px-2 text-[10px] text-white bg-gray-700 border border-gray-600 rounded-lg placeholder:text-gray-500 focus:outline-none focus:border-violet-500/50"
                              />
                              <input
                                type="text"
                                value={newLinkLabel}
                                onChange={e => setNewLinkLabel(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') void handleAddSavedLink(); if (e.key === 'Escape') setShowAddLink(false); }}
                                placeholder="Label (optioneel, bv. 'Boek: Atomic Habits')"
                                className="w-full h-7 px-2 text-[10px] text-white bg-gray-700 border border-gray-600 rounded-lg placeholder:text-gray-500 focus:outline-none focus:border-violet-500/50"
                              />
                              <div className="flex gap-1">
                                <button type="button" onClick={() => void handleAddSavedLink()} disabled={!newLinkUrl.trim()} className="flex-1 h-6 rounded-lg bg-violet-500/25 text-violet-300 text-[10px] font-semibold hover:bg-violet-500/40 transition-colors disabled:opacity-30">Opslaan</button>
                                <button type="button" onClick={() => { setShowAddLink(false); setNewLinkUrl(''); setNewLinkLabel(''); }} className="h-6 px-2 rounded-lg bg-white/8 text-white/40 text-[10px] hover:bg-white/15 transition-colors">Annuleer</button>
                              </div>
                            </div>
                          ) : (
                            <button type="button" onClick={() => setShowAddLink(true)} className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-dashed border-white/10 text-white/30 hover:text-white/60 hover:border-white/20 text-[10px] transition-colors">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                              Link toevoegen aan lijst ({savedLinks.length}/3)
                            </button>
                          )}
                        </div>
                      )}
                      {savedLinks.length >= 3 && (
                        <p className="text-[9px] text-white/20 text-center">Maximum van 3 links bereikt</p>
                      )}

                      {/* Direct tonen (bestaand) */}
                      <div className="border-t border-white/5 pt-2">
                        <p className="text-[9px] font-semibold text-white/30 uppercase tracking-wider mb-1.5">Direct tonen (tijdelijk)</p>
                        <form onSubmit={(e) => { e.preventDefault(); pushUrlLive(sharedUrlInput); }} className="flex gap-1.5">
                          <input type="text" value={sharedUrlInput} onChange={(e) => { setSharedUrlInput(e.target.value); setUrlShareError(null); }} placeholder="URL direct live tonen…" className="flex-1 h-7 px-2 text-[10px] text-white bg-gray-700 border border-gray-600 rounded-lg placeholder:text-gray-500 focus:outline-none focus:border-violet-500/50" />
                          <button type="submit" disabled={!sharedUrlInput.trim()} className="h-7 px-2.5 rounded-lg bg-violet-500/20 text-violet-300 text-[10px] font-medium hover:bg-violet-500/30 transition-colors disabled:opacity-30">Toon</button>
                        </form>
                        {urlShareError && <p className="text-[9px] text-amber-300/90 leading-snug mt-1">{urlShareError}</p>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── SECTIE: Gasten Spotlight ─────────────────────────────── */}
              {(guestSpotlightEnabled || isHost) && isHost && (() => {
                const liveGuests = attendanceEntries
                  .filter((e) => e.isGuest)
                  .map((e) => ({
                    key: `live-${e.userId}`,
                    name: e.guestName || e.presenterName || 'Gast',
                    company: (e as any).guestCompany as string | undefined,
                    source: 'live' as const,
                  }));
                const preGuests = preEventGuests
                  .filter((g) => g.status !== 'cancelled')
                  .map((g) => ({
                    key: `pre-${g.id}`,
                    name: g.name,
                    company: g.company,
                    source: 'pre' as const,
                  }));
                const seen = new Set<string>();
                const allGuests = [...liveGuests, ...preGuests].filter((g) => {
                  const k = g.name.toLowerCase();
                  if (seen.has(k)) return false;
                  seen.add(k);
                  return true;
                });
                const slottedNames = new Set(presentationSlots.filter(s => s.isGuest).map(s => s.presenterName.toLowerCase()));
                const unslottedGuests = allGuests.filter((g) => !slottedNames.has(g.name.toLowerCase()));
                const slottedGuests = allGuests.filter((g) => slottedNames.has(g.name.toLowerCase()));

                return (
                  <div className={`bg-gray-800/60 rounded-xl mb-3 overflow-hidden border border-white/5 relative ${!guestSpotlightEnabled ? 'opacity-60' : ''}`}>
                    <button
                      onClick={() => toggleSection('guestSpotlight')}
                      className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5 text-amber-400/70 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-[11px] font-semibold text-white/70 uppercase tracking-wider flex-1 text-left">Gasten spotlight</span>
                      {presentationSlots.filter(s => s.isGuest).length > 0 && (
                        <span className="text-[9px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full font-semibold">{presentationSlots.filter(s => s.isGuest).length}</span>
                      )}
                      <svg className={`w-3 h-3 text-white/30 transition-transform shrink-0 ${!collapsedSections.has('guestSpotlight') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {!collapsedSections.has('guestSpotlight') && (<div className="border-t border-white/5">

                    {/* Guest cards — always shown, live-updating */}
                    {allGuests.length === 0 ? (
                      <div className="px-3 py-4 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                          <svg className="w-4 h-4 text-white/15" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                          </svg>
                        </div>
                        <p className="text-[10px] text-white/25 leading-snug">Gasten die zich aanmelden<br/>via QR verschijnen hier live.</p>
                      </div>
                    ) : (
                      <div className="p-2 space-y-1.5">
                        {unslottedGuests.map((g) => (
                          <div key={g.key} className="flex items-center gap-2.5 px-2 py-2 rounded-xl bg-amber-500/8 border border-amber-500/12 group">
                            {/* Avatar */}
                            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-[11px] font-bold text-amber-200 shrink-0 border border-amber-500/20">
                              {g.name.charAt(0).toUpperCase()}
                            </div>
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-white/90 truncate">{g.name}</p>
                              {g.company && <p className="text-[9px] text-white/35 truncate">{g.company}</p>}
                              <span className="inline-flex items-center gap-1 text-[8px] text-amber-400/60 mt-0.5">
                                <span className="w-1 h-1 rounded-full bg-amber-400/60 inline-block" />
                                {g.source === 'live' ? 'Live aangemeld' : 'Uitgenodigd'}
                              </span>
                            </div>
                            {/* Inplannen */}
                            <button
                              onClick={() => { setPresenterNameInput(g.name); setPresentationTopicInput(''); }}
                              className="h-7 px-2.5 text-[9px] font-semibold rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/35 transition-colors shrink-0"
                            >
                              Inplannen
                            </button>
                          </div>
                        ))}
                        {slottedGuests.map((g) => (
                          <div key={g.key} className="flex items-center gap-2.5 px-2 py-1.5 rounded-xl opacity-40">
                            <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center text-[10px] font-bold text-emerald-300 shrink-0">
                              {g.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white/60 truncate">{g.name}</p>
                            </div>
                            <span className="text-[9px] text-emerald-400/70">✓ Ingepland</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Manual add form — compact */}
                    <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-white/5">
                      <p className="text-[9px] text-white/25 pt-1">Of voeg handmatig toe:</p>
                      <div className="flex gap-1.5">
                  <input
                    type="text"
                          value={presenterNameInput}
                          onChange={(e) => setPresenterNameInput(e.target.value)}
                          placeholder="Naam gast"
                          className="flex-1 px-2.5 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-xs text-white placeholder:text-gray-400 focus:outline-none focus:border-gray-500"
                        />
                        <input
                          type="number" min={1} max={60}
                          value={presentationMinutesInput}
                          onChange={(e) => setPresentationMinutesInput(e.target.value)}
                          className="w-12 text-center px-1.5 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-xs text-white focus:outline-none focus:border-gray-500"
                        />
                        <span className="text-[10px] text-white/30 self-center">m</span>
                  <button
                          onClick={addPresentationSlot}
                          disabled={!presenterNameInput.trim()}
                          className="h-8 px-2.5 text-[10px] font-semibold rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 disabled:opacity-40 transition-colors"
                        >
                          +
                  </button>
                </div>
                    </div>

                    {/* Slot list */}
                    {presentationSlots.filter(s => s.isGuest).length > 0 && (
                      <div className="p-2 space-y-1.5">
                        {presentationSlots.filter(s => s.isGuest).map((slot, index) => {
                          const progress = getSlotProgress(slot);
                          const isLive = slot.status === 'live';
                          const isDone = slot.status === 'done';
                          return (
                            <div key={slot.id} className={`rounded-lg overflow-hidden border transition-all ${isLive ? 'border-amber-500/40 bg-amber-950/20' : isDone ? 'border-white/5 opacity-50' : 'border-white/5 bg-white/3'}`}>
                              <div className="flex items-center gap-2 px-2 py-1.5">
                                <span className="text-[9px] text-white/30 w-4 shrink-0 tabular-nums">{index + 1}</span>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-xs font-semibold truncate ${isLive ? 'text-amber-300' : 'text-white/80'}`}>{slot.presenterName}</p>
                                  {slot.topic && <p className="text-[10px] text-white/40 truncate">{slot.topic}</p>}
                                </div>
                                <span className="text-[10px] text-white/30 shrink-0">{slot.durationMinutes}m</span>
                                <div className="flex items-center gap-1 shrink-0">
                                  {!isDone && !isLive && (
                                    <button onClick={() => startPresentationSlot(slot.id)}
                                      className="h-6 px-2 text-[9px] font-semibold rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 transition-colors">
                                      Start
                                    </button>
                                  )}
                                  {isLive && (
                                    <button onClick={() => completePresentationSlot(slot.id)}
                                      className="h-6 px-2 text-[9px] font-semibold rounded-lg bg-amber-500/30 text-amber-300 hover:bg-amber-500/40 transition-colors">
                                      Klaar
                                    </button>
                                  )}
                                  <button onClick={() => removePresentationSlot(slot.id)}
                                    className="w-5 h-5 flex items-center justify-center rounded text-white/20 hover:text-red-400 transition-colors">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                  </button>
                                </div>
                              </div>
                              {isLive && (
                                <div className="px-2 pb-1.5">
                                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                                    <div className="h-full bg-amber-400 transition-all" style={{ width: `${progress}%` }} />
                                  </div>
                                  <p className="text-[9px] text-amber-300/60 mt-0.5">Live · {progress}%</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    </div>)}
                  </div>
                );
              })()}

              <div className="bg-gray-800/60 rounded-xl mb-3 overflow-hidden border border-white/5">
                <button
                  onClick={() => toggleSection('agenda')}
                  className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors"
                >
                  <svg className="w-3.5 h-3.5 text-white/50 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-[11px] font-semibold text-white/70 uppercase tracking-wider flex-1 text-left">Agenda punten</span>
                  {openAgendaItems.length > 0 && (
                    <span className="text-[9px] bg-white/10 text-white/50 px-1.5 py-0.5 rounded-full font-semibold">{openAgendaItems.length}</span>
                  )}
                  <svg className={`w-3 h-3 text-white/30 transition-transform shrink-0 ${!collapsedSections.has('agenda') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {!collapsedSections.has('agenda') && (
                  <div className="border-t border-white/5 p-2 space-y-1.5">
                    {agendaListenerError && (
                      <div className="px-2 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/25 text-[10px] text-amber-200/95 leading-snug">
                        {agendaListenerError}
                      </div>
                    )}
                    {agendaSaveError && (
                      <div className="px-2 py-1.5 rounded-lg bg-red-500/15 border border-red-500/25 text-[10px] text-red-200/95 leading-snug">
                        {agendaSaveError}
                      </div>
                    )}
                    {agendaDeleteUndo && (
                      <div className="px-2 py-1.5 rounded-lg bg-violet-500/15 border border-violet-400/30 flex items-center justify-between gap-2">
                        <span className="text-[10px] text-violet-100/90 leading-snug min-w-0 flex-1">
                          Verwijderd: «{agendaDeleteUndo.title.length > 36 ? `${agendaDeleteUndo.title.slice(0, 34)}…` : agendaDeleteUndo.title}»
                        </span>
                        <button
                          type="button"
                          onClick={() => void undoAgendaSoftDelete()}
                          className="shrink-0 text-[9px] font-semibold text-violet-200 hover:text-white underline underline-offset-2"
                        >
                          Ongedaan
                        </button>
                      </div>
                    )}
                    {agendaSubmitters.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 px-1 pb-0.5">
                        <button
                          type="button"
                          onClick={() => setAgendaFilterUid(null)}
                          className={`text-[9px] font-bold px-2 py-1 rounded-full border transition-colors ${agendaFilterUid === null ? 'border-violet-400/60 bg-violet-500/15 text-violet-200' : 'border-white/10 text-white/40 hover:border-white/25'}`}
                        >
                          Iedereen
                        </button>
                        {agendaSubmitters.map((s) => {
                          const initial = (s.name || 'L').charAt(0).toUpperCase();
                          const active = agendaFilterUid === s.uid;
                          return (
                            <button
                              key={s.uid}
                              type="button"
                              title={s.name}
                              onClick={() => setAgendaFilterUid((prev) => (prev === s.uid ? null : s.uid))}
                              className={`shrink-0 w-7 h-7 rounded-full overflow-hidden border-2 transition-colors ${active ? 'border-violet-400 ring-1 ring-violet-400/40' : 'border-white/10 hover:border-white/30'}`}
                            >
                              {s.avatar ? (
                                <img src={s.avatar} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full bg-white/15 flex items-center justify-center text-[8px] font-bold text-white/60">{initial}</div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {displayedAgendaItems.map((item, index) => {
                      const initial = (item.submittedByName || 'D').charAt(0).toUpperCase();
                      const canDelete = isHost || item.submittedByUid === user?.uid;
                      const canEditAgenda = canDelete;
                      const expanded = expandedAgendaItemId === item.id;
                      const isEditingTitle = editingAgendaItemId === item.id;
                      return (
                        <div
                          key={item.id || index}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            if (isEditingTitle) return;
                            setExpandedAgendaItemId((prev) => (prev === item.id ? null : item.id));
                          }}
                          onKeyDown={(e) => {
                            if (isEditingTitle) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setExpandedAgendaItemId((prev) => (prev === item.id ? null : item.id));
                            }
                          }}
                          className={`flex items-start gap-2.5 px-2 py-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.07] transition-colors group cursor-pointer text-left border ${expanded ? 'border-white/15' : 'border-transparent'}`}
                        >
                          {item.submittedByUid === user?.uid && !item.meetingCheckedAt && (
                            <button
                              type="button"
                              aria-label="Afvinken"
                              className="shrink-0 mt-0.5 w-5 h-5 rounded-md border border-white/30 bg-white/[0.06] hover:bg-emerald-500/25 hover:border-emerald-400/55 transition-colors"
                              title="Afvinken — verschijnt onder de video bij Mijn agenda punten"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleMarkAgendaItemDone(item.id);
                              }}
                            >
                              <span className="sr-only">Afvinken</span>
                            </button>
                          )}
                          <button
                            type="button"
                            className="shrink-0 mt-0.5 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60"
                            title="Alleen punten van deze persoon"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!item.submittedByUid) return;
                              setAgendaFilterUid((prev) => (prev === item.submittedByUid ? null : item.submittedByUid!));
                            }}
                          >
                            {item.submittedByAvatar
                              ? <img src={item.submittedByAvatar} alt="" className="w-5 h-5 rounded-full object-cover pointer-events-none" />
                              : <div className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center text-[8px] font-bold text-white/60 pointer-events-none">{initial}</div>}
                          </button>
                          <div className="flex-1 min-w-0">
                            {isEditingTitle ? (
                              <input
                                value={editingAgendaDraft}
                                onChange={(e) => setEditingAgendaDraft(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onBlur={() => void commitAgendaTitleEdit()}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === 'Enter') void commitAgendaTitleEdit();
                                  if (e.key === 'Escape') setEditingAgendaItemId(null);
                                }}
                                autoFocus
                                className="w-full text-[11px] font-medium text-white bg-white/10 border border-violet-400/50 rounded-md px-1.5 py-1 outline-none focus:ring-1 focus:ring-violet-400/60"
                              />
                            ) : (
                              <p className="text-[11px] font-medium text-white/90 leading-snug">{linkifyMeetingText(item.title)}</p>
                            )}
                            {item.description && (expanded ? (
                              <p className="text-[10px] text-white/45 mt-1 leading-snug">{linkifyMeetingText(item.description)}</p>
                            ) : (
                              <p className="text-[10px] text-white/40 mt-0.5 leading-snug line-clamp-2">{item.description}</p>
                            ))}
                            <p className="text-[9px] text-white/20 mt-0.5">{item.submittedByName || 'Deelnemer'}</p>
                            {!expanded && !isEditingTitle && item.description && item.description.length > 80 && (
                              <p className="text-[9px] text-violet-400/80 mt-1">Tik om volledige tekst en links te zien</p>
                            )}
                          </div>
                          {canEditAgenda && !isEditingTitle && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingAgendaItemId(item.id);
                                setEditingAgendaDraft(item.title);
                              }}
                              className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded text-white/25 hover:text-violet-300 transition-all shrink-0 mt-0.5"
                              title="Titel wijzigen"
                              aria-label="Titel wijzigen"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void softDeleteAgendaItem(item);
                              }}
                              className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded text-white/20 hover:text-red-400 transition-all shrink-0 mt-0.5"
                              title="Verwijderen"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {showAddAgendaItem ? (
                      <div className="flex items-center gap-2 bg-gray-700/50 rounded-xl px-2 py-2 border border-white/8">
                        <div className="shrink-0">
                          {user?.photoURL
                            ? <img src={user.photoURL} alt="" className="w-5 h-5 rounded-full object-cover" />
                            : <div className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center text-[8px] font-bold text-white/60">{(user?.displayName || user?.email || 'J').charAt(0).toUpperCase()}</div>}
                        </div>
                        <input type="text" value={newAgendaItem.title} onChange={(e) => { setAgendaSaveError(null); setNewAgendaItem({ ...newAgendaItem, title: e.target.value }); }} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) void handleAddAgendaItem(); if (e.key === 'Escape') { setShowAddAgendaItem(false); setAgendaSaveError(null); setNewAgendaItem({ title: '', description: '' }); } }} placeholder="Typ agendapunt…" autoFocus className="flex-1 bg-transparent text-xs text-white placeholder:text-white/25 focus:outline-none min-w-0" />
                        <button type="button" onClick={() => void handleAddAgendaItem()} disabled={!newAgendaItem.title.trim()} className="h-6 px-2.5 text-[10px] font-semibold rounded-lg bg-white/15 text-white hover:bg-white/25 disabled:opacity-30 transition-colors shrink-0">+</button>
                        <button type="button" onClick={() => { setShowAddAgendaItem(false); setAgendaSaveError(null); setNewAgendaItem({ title: '', description: '' }); }} className="text-white/25 hover:text-white/60 shrink-0 transition-colors"><svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => { setAgendaSaveError(null); setShowAddAgendaItem(true); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-xl border border-dashed border-white/10 hover:border-white/25 transition-all group">
                        <div className="w-5 h-5 rounded-full bg-white/8 group-hover:bg-white/15 flex items-center justify-center transition-colors shrink-0">
                          <svg className="w-2.5 h-2.5 text-white/40 group-hover:text-white/70 transition-colors" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                        </div>
                        <span className="text-[10px] text-white/25 group-hover:text-white/50 transition-colors">{agendaItems.length === 0 ? 'Voeg het eerste agendapunt toe…' : 'Agenda punt toevoegen'}</span>
                      </button>
                    )}
                    {meetingMode === 'physical' && myAgendaPointsDone.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-white/10 space-y-1.5">
                        <div className="text-[9px] font-semibold text-white/40 uppercase tracking-wide px-0.5">Mijn agenda punten (afgevinkt)</div>
                        <ul className="space-y-1.5">
                          {myAgendaPointsDone.map((p) => (
                            <li key={p.id} className="flex items-start gap-2 justify-between">
                              <span className="text-[10px] text-white/80 leading-snug min-w-0 flex-1">{linkifyMeetingText(p.title)}</span>
                              <button
                                type="button"
                                onClick={() => void handleUndoAgendaItemDone(p.id)}
                                className="shrink-0 text-[9px] font-semibold text-violet-300/90 hover:text-violet-200 underline underline-offset-2"
                              >
                                Ongedaan
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

            {/* ── SECTIE: Taken ─────────────────────────────────────── */}
            {(() => {
              const openTasks = mastermindTasks.filter(t => t.status !== 'done');
              const doneTasks = mastermindTasks.filter(t => t.status === 'done');
              // Group by ownerName so legacy tasks (name-as-UID) and new tasks merge correctly
              const byOwner: Record<string, typeof mastermindTasks> = {};
              openTasks.forEach(t => {
                const key = t.ownerName ?? t.ownerUid;
                if (!byOwner[key]) byOwner[key] = [];
                byOwner[key].push(t);
              });
              const doneByOwner: Record<string, typeof mastermindTasks> = {};
              doneTasks.forEach(t => {
                const key = t.ownerName ?? t.ownerUid;
                if (!doneByOwner[key]) doneByOwner[key] = [];
                doneByOwner[key].push(t);
              });

              const cardUser = taskCardUser;
              const cardTasks = cardUser ? mastermindTasks.filter(t => t.ownerUid === cardUser) : [];
              const cardOpen = cardTasks.filter(t => t.status !== 'done');
              const cardDone = cardTasks.filter(t => t.status === 'done');
              const cardName = cardTasks[0]?.ownerName ?? '';
              const cardAvatar = cardTasks[0]?.ownerAvatar ?? null;
              const cardInitials = cardName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

              return (
                <div className="bg-gray-800/60 rounded-xl mb-3 overflow-hidden border border-white/5">
                  <button
                    onClick={() => toggleSection('taken')}
                    className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5 text-emerald-400/70 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-[11px] font-semibold text-white/70 uppercase tracking-wider flex-1 text-left">
                      Taken · {clubName ?? 'Groep'}
                    </span>
                    {openTasks.length > 0 && (
                      <span className="text-[9px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-full font-semibold">{openTasks.length}</span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowAddTask(v => !v); setNewTaskTitle(''); setNewTaskAssigneeUid('self'); }}
                      className="w-5 h-5 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center transition-colors"
                      title="Taak toevoegen"
                    >
                      <svg className="w-2.5 h-2.5 text-white/60" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                    <svg className={`w-3 h-3 text-white/30 transition-transform shrink-0 ${!collapsedSections.has('taken') ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {!collapsedSections.has('taken') && (
                    <div className="border-t border-white/5 p-2.5">
                      {/* Feedback toast */}
                      {taskFeedback && (
                        <div className={`mb-2 px-3 py-2 rounded-lg text-[11px] font-medium flex items-center gap-2 animate-pulse ${taskFeedback.startsWith('Fout') ? 'bg-red-500/15 text-red-300 border border-red-500/20' : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/20'}`}>
                          <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            {taskFeedback.startsWith('Fout')
                              ? <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              : <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
                          </svg>
                          {taskFeedback}
                        </div>
                      )}

                      {/* Add task form */}
                      {showAddTask && (
                        <div className="mb-2.5 p-2.5 rounded-xl bg-gray-700/50 border border-white/8 space-y-2">
                          <input autoFocus value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleAddTask(); if (e.key === 'Escape') setShowAddTask(false); }} placeholder="Beschrijf de taak…" className="w-full bg-gray-700 text-white text-xs placeholder:text-gray-400 rounded-lg px-2.5 py-1.5 outline-none border border-white/10 focus:border-emerald-500/50" />
                          <select value={newTaskAssigneeUid} onChange={e => setNewTaskAssigneeUid(e.target.value)} className="w-full bg-gray-700 text-white text-xs rounded-lg px-2 py-1.5 outline-none border border-white/10">
                            <option value="self">{displayName} (ikzelf)</option>
                            {memberNames.map((n, i) => {
                              const uid = memberUids[i] ?? n;
                              if (uid === user?.uid) return null;
                              return <option key={uid} value={uid}>{n}</option>;
                            })}
                          </select>
                          <div className="flex gap-1.5">
                            <button onClick={handleAddTask} disabled={!newTaskTitle.trim()} className="flex-1 h-7 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-[11px] font-semibold transition-colors">Opslaan</button>
                            <button onClick={() => setShowAddTask(false)} className="h-7 px-2 rounded-lg bg-white/8 hover:bg-white/15 text-white/50 text-[11px] transition-colors">Annuleer</button>
                          </div>
                        </div>
                      )}

                      {mastermindTasks.length === 0 && !showAddTask && (
                        <p className="text-[11px] text-white/25 text-center py-3">Nog geen taken. Klik op + om een taak toe te voegen.</p>
                      )}

                      {/* Open taken per lid */}
                      {Object.entries(byOwner).map(([ownerName, tasks]) => {
                        const first = tasks[0];
                        const initials = (first.ownerName ?? '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                        return (
                          <div key={ownerName} className="mb-2">
                            {/* Owner header */}
                            <div className="flex items-center gap-1.5 mb-1 px-1 py-0.5">
                              {first.ownerAvatar
                                ? <img src={first.ownerAvatar} alt={first.ownerName} className="w-5 h-5 rounded-full object-cover shrink-0" />
                                : <div className="w-5 h-5 rounded-full bg-emerald-700 flex items-center justify-center text-[8px] font-bold text-white shrink-0">{initials}</div>}
                              <span className="text-[10px] font-semibold text-white/60 flex-1">{first.ownerName}</span>
                              <span className="text-[9px] text-white/25">{tasks.length}</span>
                            </div>
                            {/* Tasks */}
                            {tasks.map(task => (
                              <div key={task.id} className="mb-0.5">
                                {editingTaskId === task.id ? (
                                  /* ── Edit mode ── */
                                  <div className="px-2 py-1.5 rounded-lg bg-gray-700/60 border border-white/10 space-y-1.5">
                                    <input
                                      autoFocus
                                      value={editingTaskTitle}
                                      onChange={e => setEditingTaskTitle(e.target.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') void handleEditTask(task.id); if (e.key === 'Escape') setEditingTaskId(null); }}
                                      className="w-full bg-gray-700 text-white text-[11px] placeholder:text-gray-400 rounded px-2 py-1 outline-none border border-white/10 focus:border-emerald-500/50"
                                    />
                                    <select
                                      value={editingTaskAssigneeUid}
                                      onChange={e => setEditingTaskAssigneeUid(e.target.value)}
                                      className="w-full bg-gray-700 text-white text-[11px] rounded px-2 py-1 outline-none border border-white/10"
                                    >
                                      <option value="self">{displayName} (ikzelf)</option>
                                      {memberNames.map((n, i) => {
                                        const uid = memberUids[i] ?? n;
                                        if (uid === user?.uid) return null;
                                        return <option key={uid} value={uid}>{n}</option>;
                                      })}
                                    </select>
                                    <div className="flex gap-1">
                                      <button
                                        type="button"
                                        onClick={() => void handleEditTask(task.id)}
                                        disabled={!editingTaskTitle.trim()}
                                        className="flex-1 h-6 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-[10px] font-semibold transition-colors"
                                      >Opslaan</button>
                                      <button
                                        type="button"
                                        onClick={() => setEditingTaskId(null)}
                                        className="h-6 px-2 rounded bg-white/8 hover:bg-white/15 text-white/50 text-[10px] transition-colors"
                                      >Annuleer</button>
                                    </div>
                                  </div>
                                ) : (
                                  /* ── View mode ── */
                                  <div className="flex items-start gap-2 px-2 py-1 rounded-lg hover:bg-white/5 group transition-colors">
                                    <button
                                      type="button"
                                      onClick={() => handleToggleTask(task.id, task.status)}
                                      className={`w-4 h-4 rounded border flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${task.status === 'done' ? 'bg-emerald-500 border-emerald-500' : 'border-white/25 hover:border-emerald-400'}`}
                                    >
                                      {task.status === 'done' && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                    </button>
                                    <span className="text-[11px] flex-1 leading-snug text-white/75">{task.title}</span>
                                    {(isHost || task.ownerUid === user?.uid || task.ownerName === displayName) && (
                                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {/* Edit button */}
                                        <button
                                          type="button"
                                          title="Wijzigen"
                                          onClick={() => {
                                            setEditingTaskId(task.id);
                                            setEditingTaskTitle(task.title);
                                            const uidIdx = memberUids.indexOf(task.ownerUid);
                                            setEditingTaskAssigneeUid(
                                              task.ownerUid === user?.uid ? 'self'
                                              : uidIdx >= 0 ? task.ownerUid
                                              : 'self'
                                            );
                                          }}
                                          className="text-white/25 hover:text-blue-400"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                                          </svg>
                                        </button>
                                        {/* Delete button */}
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteTask(task.id)}
                                          className="text-white/25 hover:text-red-400"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}

                      {/* Klaar sectie — afgeronde taken per groep */}
                      {doneTasks.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-white/5">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <svg className="w-3 h-3 text-emerald-500/50 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-[10px] font-semibold text-emerald-400/50 uppercase tracking-wider">Klaar · {clubName ?? 'Groep'}</span>
                            <span className="text-[9px] text-white/20">{doneTasks.length}</span>
                          </div>
                          {Object.entries(doneByOwner).map(([ownerUid, tasks]) => {
                            const first = tasks[0];
                            return (
                              <div key={ownerUid} className="mb-1.5">
                                <button
                                  onClick={() => setTaskCardUser(ownerUid)}
                                  className="flex items-center gap-1.5 mb-0.5 hover:bg-white/5 rounded-lg px-1 py-0.5 transition-colors cursor-pointer"
                                >
                                  {first.ownerAvatar
                                    ? <img src={first.ownerAvatar} alt="" className="w-4 h-4 rounded-full object-cover opacity-50" />
                                    : <div className="w-4 h-4 rounded-full bg-emerald-700/50 flex items-center justify-center text-[7px] font-bold text-white/40 shrink-0">{(first.ownerName ?? '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}</div>}
                                  <span className="text-[9px] text-white/30">{first.ownerName}</span>
                                </button>
                                {tasks.map(task => (
                                  <div key={task.id} className="flex items-center gap-2 px-2 py-0.5 rounded-lg group hover:bg-white/5 transition-colors">
                                    <button type="button" onClick={() => handleToggleTask(task.id, task.status)} className="w-3.5 h-3.5 rounded border bg-emerald-500/60 border-emerald-500/60 flex items-center justify-center flex-shrink-0">
                                      <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                    </button>
                                    <span className="text-[10px] text-white/25 line-through flex-1 min-w-0">{task.title}</span>
                                    <button
                                      type="button"
                                      title="Ongedaan maken"
                                      onClick={() => handleToggleTask(task.id, task.status)}
                                      className="shrink-0 text-[9px] font-semibold text-amber-400/85 hover:text-amber-300 px-1.5 py-0.5 rounded-md bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
                                    >
                                      Ongedaan
                                    </button>
                                    {(isHost || task.ownerUid === user?.uid) && (
                                      <button type="button" onClick={() => handleDeleteTask(task.id)} className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-white/20 hover:text-red-400 shrink-0">
                                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── User Task Card Overlay ── */}
                  {cardUser && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={() => setTaskCardUser(null)}>
                      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                      <div className="relative bg-gray-900 rounded-2xl shadow-2xl border border-white/10 w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
                        {/* Card header */}
                        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5 bg-gray-800/50">
                          {cardAvatar
                            ? <img src={cardAvatar} alt={cardName} className="w-10 h-10 rounded-full object-cover" />
                            : <div className="w-10 h-10 rounded-full bg-emerald-700 flex items-center justify-center text-sm font-bold text-white">{cardInitials}</div>}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-white truncate">{cardName}</p>
                            <p className="text-[10px] text-white/40">{cardOpen.length} open · {cardDone.length} afgerond</p>
                          </div>
                          <button onClick={() => setTaskCardUser(null)} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                            <svg className="w-3.5 h-3.5 text-white/60" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                        {/* Open tasks */}
                        <div className="max-h-[50vh] overflow-y-auto custom-scrollbar">
                          {cardOpen.length > 0 && (
                            <div className="px-4 pt-3 pb-2">
                              <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">Open taken</p>
                              {cardOpen.map(task => (
                                <div key={task.id} className="flex items-start gap-2.5 py-1.5 group">
                                  <button onClick={() => handleToggleTask(task.id, task.status)} className="w-4 h-4 rounded border border-white/25 hover:border-emerald-400 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors" />
                                  <span className="text-xs text-white/80 leading-snug flex-1">{task.title}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {cardDone.length > 0 && (
                            <div className="px-4 pt-2 pb-3 border-t border-white/5">
                              <p className="text-[10px] font-semibold text-emerald-400/50 uppercase tracking-wider mb-2">Afgerond</p>
                              {cardDone.map(task => (
                                <div key={task.id} className="flex items-start gap-2.5 py-1 group">
                                  <button type="button" onClick={() => handleToggleTask(task.id, task.status)} className="w-4 h-4 rounded border bg-emerald-500 border-emerald-500 flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors">
                                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                  </button>
                                  <span className="text-xs text-white/30 line-through leading-snug flex-1 min-w-0">{task.title}</span>
                                  <button
                                    type="button"
                                    title="Ongedaan maken"
                                    onClick={() => handleToggleTask(task.id, task.status)}
                                    className="shrink-0 text-[10px] font-semibold text-amber-400/90 hover:text-amber-300 px-2 py-0.5 rounded-md bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
                                  >
                                    Ongedaan
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {cardTasks.length === 0 && (
                            <p className="text-xs text-white/25 text-center py-6">Geen taken voor dit lid.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* ── Einde Taken ── */}

            {/* ── Widgets — RSVP / volgende meeting (fysiek mobiel: in sidebar; desktop heeft overlay op beamer) ── */}
            {meetingMode === 'physical' && entityId && (
              <div className="bg-gray-800/60 rounded-xl overflow-hidden border border-white/5 p-3 md:hidden">
                <MeetingWidgetsOverlay
                  entityId={entityId}
                  entityType={(entityType as 'mastermind' | 'community' | 'networkclub') ?? 'mastermind'}
                  meetingId={roomName}
                  clubId={clubId ?? null}
                  clubName={clubName}
                  userId={user?.uid ?? null}
                  userDisplayName={displayName}
                />
              </div>
            )}
            </div>
          </div>
        )}

        {/* ── Side Panel: Deelnemers ─────────────────────────────────── */}
        {showParticipants && (
          <div className="w-80 bg-gray-800 text-white border-l border-gray-700 flex flex-col">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Deelnemers ({participantCount})</h3>
              <button onClick={() => setShowParticipants(false)} className="text-gray-500 hover:text-gray-300">
                <Icon icon={X} size={14} strokeWidth={2} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {liveParticipants.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-4">Wachten op deelnemers...</p>
              ) : (
                liveParticipants.map((p, i) => {
                  const meta = memberMeta.find(m => m.name === p.displayName);
                  const flag = meta?.country ? (COUNTRY_FLAGS[meta.country] || '🌍') : '';
                  return (
                    <div key={`${p.displayName}-${i}`} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/5">
                      <div className="relative flex-shrink-0">
                        {p.avatarUrl ? (
                          <img src={p.avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-gray-600 flex items-center justify-center text-[10px] font-bold text-gray-300">
                            {(p.displayName || '?')[0].toUpperCase()}
            </div>
                        )}
                        {flag && <span className="absolute -bottom-0.5 -right-1 text-[10px]">{flag}</span>}
                      </div>
                      <span className="text-xs font-medium text-gray-200 truncate flex-1">{p.displayName}</span>
                      {i === 0 && <span className="text-[9px] text-gray-500 bg-gray-700 px-1.5 py-0.5 rounded">Jij</span>}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ── Side Panel: Chat ────────────────────────────────────────── */}
        {showChat && (
          <div className="w-80 bg-gray-800 text-white border-l border-gray-700 flex flex-col">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Chat</h3>
              <button onClick={() => setShowChat(false)} className="text-gray-500 hover:text-gray-300">
                <Icon icon={X} size={14} strokeWidth={2} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
              {chatMessages.length === 0 && (
                <p className="text-xs text-gray-500 text-center py-6">Nog geen berichten. Stuur het eerste bericht!</p>
              )}
              {chatMessages.map((msg) => {
                const isMe = msg.uid === user?.uid;
                return (
                  <div key={msg.id} className={`flex gap-2 ${isMe ? 'flex-row-reverse' : ''}`}>
                    {!isMe && (
                      msg.avatar ? (
                        <img src={msg.avatar} alt="" className="w-6 h-6 rounded-full object-cover shrink-0 mt-0.5" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center shrink-0 mt-0.5">
                          <span className="text-[8px] font-bold text-gray-300">{(msg.name || '?')[0].toUpperCase()}</span>
                        </div>
                      )
                    )}
                    <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
                      {!isMe && <p className="text-[9px] text-gray-500 mb-0.5 px-1">{msg.name}</p>}
                      <div className={`rounded-xl px-3 py-1.5 text-xs leading-relaxed ${isMe ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200'}`}>
                        {msg.text && <p>{msg.text}</p>}
                        {msg.imageUrl && (
                          <img src={msg.imageUrl} alt="" className="mt-1 rounded-lg max-w-full max-h-40 object-cover cursor-pointer" onClick={() => window.open(msg.imageUrl, '_blank')} />
                        )}
                        {msg.fileUrl && (
                          <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer"
                            className="mt-1 flex items-center gap-1 text-[10px] underline opacity-80 hover:opacity-100">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
                            {msg.fileName || 'Bestand'}
                          </a>
                        )}
                      </div>
                      <p className="text-[8px] text-gray-600 mt-0.5 px-1">
                        {new Date(msg.ts).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>
            {/* Chat input */}
            <div className="px-3 py-2 border-t border-gray-700">
              <div className="flex items-center gap-1.5">
                <input type="file" ref={chatFileRef} className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip" onChange={handleChatFileUpload} />
                <button onClick={() => chatFileRef.current?.click()} disabled={chatUploading}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40 shrink-0"
                  title="Bestand/afbeelding delen">
                  {chatUploading ? <Loader size={12} strokeWidth={2} className="animate-spin" /> : <Icon icon={Plus} size={13} strokeWidth={2} />}
                </button>
                <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(chatInput); } }}
                  placeholder="Typ een bericht..."
                  className="flex-1 h-8 bg-gray-700 border-none rounded-lg px-3 text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                <button onClick={() => sendChatMessage(chatInput)} disabled={chatSending || !chatInput.trim()}
                  className="w-7 h-7 rounded-lg flex items-center justify-center bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 transition-colors shrink-0">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Lobby knock requests — toast voor host ──────────────── */}
        {isHost && knockRequests.length > 0 && (
          <div className={`absolute left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 ${meetingMode === 'physical' ? 'top-4' : 'top-14'}`}>
            {knockRequests.map((kr) => (
              <div key={kr.id} className="flex items-center gap-3 px-4 py-2.5 bg-black/80 backdrop-blur-md rounded-xl shadow-lg border border-gray-600">
                <div className="w-8 h-8 rounded-full bg-amber-500/30 flex items-center justify-center">
                  <span className="text-sm font-bold text-amber-300">{(kr.name || '?')[0].toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">{kr.name}</p>
                  <p className="text-[10px] text-gray-400">wil deelnemen aan de meeting</p>
                </div>
                <button onClick={() => admitKnock(kr.id)}
                  className="h-7 px-3 bg-emerald-600 text-white text-[10px] font-semibold rounded-lg hover:bg-emerald-500 transition-colors">
                  Toelaten
                </button>
                <button onClick={() => rejectKnock(kr.id)}
                  className="h-7 px-2 bg-red-600/80 text-white text-[10px] font-semibold rounded-lg hover:bg-red-500 transition-colors">
                  Weigeren
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Beamer preview overlay — fullscreen preview van wat op de projector staat ── */}
      {showBeamerPreview && meetingMode === 'physical' && (
        <div className="fixed inset-0 z-[200] bg-black flex flex-col" style={{ height: '100dvh' }}>
          <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
            <BeamerPitchView
              runningEntry={runningEntry}
              remainingSeconds={livePitchRemainingSeconds}
              totalSeconds={livePitchTimerDurationSeconds}
              timelineSlots={timelineSlots}
              isHost={false}
              primaryColor={livePitchTheme.primary}
              accentColor={livePitchTheme.accent}
              overlaySettings={overlaySettings || {}}
              clubLogoUrl={clubLogoUrl}
              templateBackgroundUrl={(overlaySettings as any)?.templateBackgroundUrl || undefined}
              templatePdfUrl={templatePdfUrl}
              activePageNumber={activePageNumber}
              pitchTemplatePdfUrl={pitchTemplatePdfUrl}
              pitchTemplateZones={pitchTemplateZones}
              nextEntry={(() => {
                const currentIdx = timelineSlots.findIndex(s => s.userId === livePitchRunningUserId);
                const nextSlot = timelineSlots.slice(currentIdx + 1).find(s => s.slotType === 'pitch');
                if (!nextSlot) return null;
                const nextLP = livePitchEntries.find(e => e.userId === nextSlot.userId);
                return nextLP ? { ...nextLP, photoURL: (nextLP as any).photoURL } : null;
              })()}
              nextSlotNumber={(() => {
                const currentIdx = timelineSlots.findIndex(s => s.userId === livePitchRunningUserId);
                const nextSlot = timelineSlots.slice(currentIdx + 1).find(s => s.slotType === 'pitch');
                return nextSlot?.slotNumber ?? 0;
              })()}
              transitionActive={transitionActive}
              transitionNextEntry={transitionNextEntry}
              transitionNextSlotNumber={
                transitionNextEntry
                  ? (timelineSlots.find((s) => s.userId === transitionNextEntry.userId)?.slotNumber ?? 0)
                  : 0
              }
              totalPitchSlots={timelineSlots.filter((s) => s.slotType === 'pitch').length}
              transitionDurationSeconds={transitionDurationSeconds}
              transitionMusicUrl={transitionMusicUrl}
              transitionMusicVolume={transitionMusicVolume}
              onTransitionComplete={() => void handleTransitionComplete()}
              onStopPitch={() => void stopLivePitch(true)}
            />
          </div>
          <div className="shrink-0 flex items-center justify-center gap-3 px-4 py-3 bg-black/90 border-t border-white/10">
            <span className="text-xs text-white/50">Beamer preview · dit zien deelnemers op het projectiescherm</span>
            <button
              type="button"
              onClick={() => setShowBeamerPreview(false)}
              className="px-4 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-semibold transition-colors"
            >
              Sluiten
            </button>
          </div>
        </div>
      )}

      {/* Bottom bar — online: Jitsi-achtige controls; fysiek: alleen context + verlaten (geen videocall-UI) */}
      {meetingMode === 'physical' ? (
        <div className="bg-[#0c1220] border-t border-white/10 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 shrink-0">
          <div className="text-white text-sm min-w-0">
            <p className="font-medium truncate">{meetingTitle || 'Fysieke bijeenkomst'}</p>
            <p className="text-white/45 text-xs">
              {participantCount} {participantCount === 1 ? 'deelnemer' : 'deelnemers'} · geen videocall
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            {enableLivePresentation && isHost && (
              <button
                type="button"
                onClick={() => setShowLivePitch(true)}
                className="px-3 py-2 rounded-lg text-white text-xs font-bold transition-colors flex items-center gap-1.5"
                style={{ backgroundColor: '#7A9488' }}
                title="Presentatie live"
              >
                <Icon icon={Monitor} size={14} strokeWidth={2} />
                <span>Presentatie</span>
              </button>
            )}
            {isHost && (
              <button
                type="button"
                onClick={() => setShowBeamerPreview(true)}
                className="px-3 py-2 rounded-lg text-white text-xs font-semibold bg-white/10 hover:bg-white/15 transition-colors flex items-center gap-1.5"
                title="Bekijk wat er op de beamer staat"
              >
                <Icon icon={Maximize2} size={14} strokeWidth={2} />
                <span className="hidden sm:inline">Beamer view</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowAgenda(!showAgenda)}
              className={`hidden md:flex p-2.5 rounded-lg transition-colors ${showAgenda ? 'bg-white/20 text-white' : 'bg-white/10 hover:bg-white/15 text-white/80'}`}
              title="Meeting tijdlijn"
              aria-pressed={showAgenda}
            >
              <Icon icon={Calendar} size={18} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={toggleFullscreen}
              className="hidden md:flex p-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors"
              title={isFullscreen ? 'Volledig scherm uit' : 'Volledig scherm'}
            >
              <Icon icon={isFullscreen ? Minimize2 : Maximize2} size={18} strokeWidth={1.8} />
            </button>
            <button
              type="button"
              onClick={leaveMeeting}
              className="px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors flex items-center gap-2"
            >
              <Icon icon={PhoneOff} size={18} strokeWidth={1.5} />
              <span>Verlaten</span>
            </button>
          </div>
        </div>
      ) : (
      <div className="bg-gray-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="text-white text-sm">
            <p className="font-medium">{meetingTitle || 'Netwerkclub Meeting'}</p>
            <p className="text-gray-400 text-xs">{participantCount} deelnemers</p>
          </div>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center space-x-2">
          <button
            onClick={toggleMute}
            className="p-3 bg-gray-700 hover:bg-gray-600 rounded-full text-white transition-colors"
            title="Microfoon aan/uit"
          >
            <Icon icon={Mic} size={20} strokeWidth={1.5} />
          </button>

          <button
            onClick={toggleVideo}
            className="p-3 bg-gray-700 hover:bg-gray-600 rounded-full text-white transition-colors"
            title="Video aan/uit"
          >
            <Icon icon={Video} size={20} strokeWidth={1.5} />
          </button>

          <button
            onClick={startScreenShare}
            className="p-3 bg-gray-700 hover:bg-gray-600 rounded-full text-white transition-colors"
            title="Scherm delen"
          >
            <Icon icon={Monitor} size={20} strokeWidth={1.5} />
          </button>

          <button
            className="p-3 bg-gray-700 hover:bg-gray-600 rounded-full text-white transition-colors"
            title="Meer opties"
          >
            <Icon icon={MoreVertical} size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Right Side - Leave Meeting */}
        <div className="flex items-center space-x-2">
          <button
            onClick={leaveMeeting}
            className="px-6 py-3 bg-sage-600 hover:bg-sage-700 rounded-lg text-white font-medium transition-colors flex items-center space-x-2"
          >
            <Icon icon={PhoneOff} size={20} strokeWidth={1.5} />
            <span>Verlaten</span>
          </button>
        </div>
      </div>
      )}

      {/* Recording upload status bar */}
      {uploadProgress === 'uploading' && (
        <div className="bg-gray-900 border-t border-gray-700 px-4 py-3 space-y-1.5">
          <div className="flex items-center gap-3">
            <Icon icon={Loader} size={14} strokeWidth={2} className="text-blue-400 animate-spin shrink-0" />
            <span className="text-xs text-gray-300 flex-1">Opname wordt geüpload…</span>
          </div>
          {/* Animated indeterminate progress bar */}
          <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full animate-pulse w-1/2 origin-left" style={{ animation: 'uploadSlide 1.8s ease-in-out infinite' }} />
          </div>
          <p className="text-[11px] text-gray-500">De AI-analyse start automatisch na de upload. Je hoeft hier niet op te wachten.</p>
        </div>
      )}
      {uploadProgress === 'done' && recordingUrl && (
        <div className="bg-emerald-950/80 border-t border-emerald-800/40 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-emerald-200">Opname opgeslagen ✓ — je kunt nu veilig afsluiten</p>
              <p className="text-[11px] text-emerald-500/80 mt-0.5">AI-analyse loopt op achtergrond verder. Resultaat verschijnt vanzelf.</p>
          </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
          <a
            href={recordingUrl}
            target="_blank"
            rel="noopener noreferrer"
              className="text-[11px] font-medium text-emerald-400 hover:text-emerald-300 underline transition-colors whitespace-nowrap"
          >
              Downloaden
          </a>
            <button onClick={() => setUpload('idle')} className="text-emerald-600 hover:text-emerald-400 transition-colors">
            <Icon icon={X} size={14} strokeWidth={2} />
          </button>
          </div>
        </div>
      )}
      {uploadProgress === 'error' && (
        <div className="bg-amber-50 border-t border-amber-200 px-4 py-3 flex items-center gap-3">
          <span className="text-xs text-amber-700 flex-1">Upload mislukt — probeer opnieuw of download handmatig.</span>
          {recordedBlobRef.current && (
            <button
              onClick={async () => {
                const blob = recordedBlobRef.current!;
                const mime = blob.type || 'video/webm';
                setUpload('uploading');
                try {
                  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                  const storageRef = ref(storage, `recordings/${entityId ?? 'general'}/${roomName}_${timestamp}.webm`);
                  await uploadBytes(storageRef, blob);
                  const downloadURL = await getDownloadURL(storageRef);
                  setRecordingUrl(downloadURL);
                  await addDoc(collection(db, 'meetingRecordings'), {
                    entityId: entityId ?? null, roomName,
                    userId: user?.uid ?? null,
                    userName: user?.displayName || user?.email || 'Onbekend',
                    downloadURL, durationSeconds: recordingSeconds,
                    fileSizeBytes: blob.size, mimeType: mime,
                    participants: [], createdAt: serverTimestamp(),
                  });
                  setUpload('done');
                } catch {
                  setUpload('error');
                }
              }}
              className="shrink-0 h-7 px-3 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors"
            >
              ↺ Opnieuw proberen
            </button>
          )}
          <button onClick={() => setUpload('idle')} className="text-gray-500 hover:text-gray-300">
            <Icon icon={X} size={12} strokeWidth={2} />
          </button>
        </div>
      )}

      {/* ─── Recording instruction prompt ───────────────────────────── */}
      {enableLivePresentation && showLivePitch && (
        <div className="absolute inset-0 z-40 bg-black/65 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-5xl max-h-[90vh] overflow-auto rounded-2xl border border-white/10 bg-[#0f172a] text-white shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <h3 className="text-base font-semibold">Presentatie live</h3>
                <p className="text-xs text-white/70">Pitch modus voor online en fysieke bijeenkomsten</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleFullscreen}
                  className="h-9 px-3 rounded-lg text-xs font-semibold border border-white/20 hover:bg-white/10 transition-colors"
                >
                  Fullscreen
                </button>
                <button
                  onClick={startScreenShare}
                  className="h-9 px-3 rounded-lg text-xs font-semibold border border-white/20 hover:bg-white/10 transition-colors"
                >
                  Deel scherm
                </button>
                <button
                  onClick={() => setShowLivePitch(false)}
                  className="h-9 px-3 rounded-lg text-xs font-semibold border border-white/20 hover:bg-white/10 transition-colors"
                >
                  Sluiten
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 p-5">
              <div className="space-y-4">
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-2">
                  <p className="text-xs uppercase tracking-wide text-white/70">Jouw pitch</p>
                  <input
                    value={livePitchCurrentUserTitle}
                    onChange={(e) => setLivePitchCurrentUserTitle(e.target.value)}
                    placeholder="Titel van je presentatie"
                    className="w-full h-10 px-3 rounded-lg bg-white/10 border border-white/15 text-sm placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                  <textarea
                    value={livePitchCurrentUserMessage}
                    onChange={(e) => setLivePitchCurrentUserMessage(e.target.value)}
                    placeholder="Je kernboodschap in 1-3 zinnen"
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-sm placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-white/30 resize-none"
                  />

                  {/* Zoekvraag */}
                  <div className="pt-1">
                    <p className="text-[11px] text-white/60 mb-1 uppercase tracking-wide">Wat zoek ik? <span className="normal-case text-white/40">(matchmaking)</span></p>
                    <input
                      value={livePitchCurrentUserSearchQ}
                      onChange={(e) => setLivePitchCurrentUserSearchQ(e.target.value)}
                      placeholder="Bijv. investeerder, samenwerkingspartner, klant…"
                      className="w-full h-9 px-3 rounded-lg bg-white/10 border border-white/15 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
                    />
                  </div>

                  {/* Live aanbeveling */}
                  <div className="pt-1 rounded-xl border border-amber-400/25 bg-amber-400/8 p-3 space-y-2">
                    <p className="text-[11px] text-amber-300/90 uppercase tracking-wide font-semibold">⭐ Aanbeveling voor een lid</p>
                    <input
                      value={livePitchCurrentUserRecTarget}
                      onChange={(e) => setLivePitchCurrentUserRecTarget(e.target.value)}
                      placeholder="Naam van het lid dat je aanbeveelt"
                      className="w-full h-9 px-3 rounded-lg bg-white/10 border border-white/15 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                    />
                    <textarea
                      value={livePitchCurrentUserRecReason}
                      onChange={(e) => setLivePitchCurrentUserRecReason(e.target.value)}
                      placeholder="Waarom beveel je deze persoon aan? (1-2 zinnen)"
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-amber-400/50 resize-none"
                    />
                    <p className="text-[10px] text-white/45">De aanbeveling wordt live getoond tijdens jouw pitch en bezorgd in de inbox.</p>
                  </div>

                  {/* ── Fase 2.5: Media upload — foto, video, YouTube ─── */}
                  <div className="pt-1 space-y-2">
                    <p className="text-[11px] text-white/60 uppercase tracking-wide">
                      Foto&rsquo;s, video of YouTube voor je pitch
                    </p>

                    {/* Foto-weergave seconden per foto */}
                    <div className="flex items-center gap-2">
                      <label className="text-[10px] text-white/50 whitespace-nowrap">Seconden per foto:</label>
                      <input
                        type="number"
                        min={2}
                        max={30}
                        value={myMediaDisplaySeconds}
                        onChange={(e) => setMyMediaDisplaySeconds(Math.max(2, Math.min(30, Number(e.target.value) || 5)))}
                        className="w-16 h-7 px-2 rounded bg-white/10 border border-white/15 text-xs focus:outline-none focus:ring-2 focus:ring-white/30"
                        aria-label="Seconden per foto"
                      />
                    </div>

                    {/* Duur-waarschuwing video */}
                    {pitchMedia.uploadState.durationWarning && (
                      <p role="status" className="text-[10px] text-amber-400 leading-snug">
                        {pitchMedia.uploadState.durationWarning}
                        <button onClick={pitchMedia.clearDurationWarning} className="ml-2 text-white/40 hover:text-white underline">Sluit</button>
                      </p>
                    )}

                    {pitchMedia.uploadState.softLimitWarning && (
                      <p role="status" className="text-[10px] text-amber-400">{STRINGS.upload.photoSoftLimit}</p>
                    )}
                    {pitchMedia.uploadState.isUploading && (
                      <div className="space-y-1">
                        <p className="text-[11px] text-white/70">{STRINGS.upload.videoProgress(pitchMedia.uploadState.progress)}</p>
                        <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
                          <div className="h-full rounded-full bg-orange-400 transition-all" style={{ width: `${pitchMedia.uploadState.progress}%` }} />
                        </div>
                      </div>
                    )}
                    {pitchMedia.uploadState.error && (
                      <p role="alert" className="text-[10px] text-rose-400">{pitchMedia.uploadState.error}</p>
                    )}

                    {/* Media thumbnails */}
                    {pitchMediaItems.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {pitchMediaItems.map((m) => (
                          <div key={m.id} className="relative group">
                            {m.type === 'image' ? (
                              <img src={m.storageUrl} alt="" className="w-14 h-14 object-cover rounded-lg border border-white/15" />
                            ) : m.type === 'youtube' ? (
                              <div className="w-14 h-14 rounded-lg border border-red-400/40 bg-red-900/30 flex flex-col items-center justify-center gap-0.5">
                                <span className="text-red-400 text-xs font-bold">YT</span>
                              </div>
                            ) : (
                              <div className="w-14 h-14 rounded-lg border border-white/15 bg-white/10 flex items-center justify-center text-lg">
                                {m.loop ? '\u21BA' : '\u25B6'}
                              </div>
                            )}
                            {/* Loop badge */}
                            {(m.type === 'video' || m.type === 'youtube') && (
                              <button
                                onClick={() => void pitchMedia.updateItem(m.id, { loop: !m.loop })}
                                title={m.loop ? 'Loop aan — klik om uit te zetten' : 'Loop uit — klik om aan te zetten'}
                                className="absolute bottom-0 left-0 w-5 h-5 bg-black/70 rounded-br-lg text-[8px] text-white flex items-center justify-center"
                              >
                                {m.loop ? '\u21BA' : '\u25B6'}
                              </button>
                            )}
                            <button
                              onClick={() => void pitchMedia.removeItem(m.id)}
                              aria-label="Media verwijderen"
                              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-[9px] hidden group-hover:flex items-center justify-center"
                            >✕</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload knop */}
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={pitchMedia.uploadState.isUploading}
                      aria-label="Foto of video toevoegen aan pitch"
                      className="min-h-[36px] px-3 text-[11px] font-medium rounded-lg border border-white/20 hover:bg-white/10 disabled:opacity-50 transition-colors"
                    >
                      {pitchMedia.uploadState.isUploading ? 'Uploaden\u2026' : 'Foto of video toevoegen'}
                    </button>

                    {/* YouTube URL invoer */}
                    <div className="rounded-lg bg-white/5 border border-white/10 p-2 space-y-1.5">
                      <p className="text-[10px] text-white/50 uppercase tracking-wide">Of voeg een YouTube link toe</p>
                      {youtubeInputError && <p role="alert" className="text-[10px] text-rose-400">{youtubeInputError}</p>}
                      <div className="flex gap-1.5">
                        <input
                          type="url"
                          value={youtubeInputUrl}
                          onChange={(e) => { setYoutubeInputUrl(e.target.value); setYoutubeInputError(null); }}
                          placeholder="https://youtu.be/…"
                          className="flex-1 h-8 px-2 rounded bg-white/10 border border-white/15 text-[11px] placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-red-400/40"
                          aria-label="YouTube URL"
                        />
                        <button
                          onClick={() => void handleAddYoutubeLink()}
                          disabled={!youtubeInputUrl.trim()}
                          className="h-8 px-3 rounded text-[11px] font-semibold bg-red-600/70 hover:bg-red-600 border border-red-400/30 text-white disabled:opacity-40 transition-colors"
                          aria-label="YouTube video toevoegen"
                        >
                          Toevoegen
                        </button>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={youtubeInputLoop}
                          onChange={(e) => setYoutubeInputLoop(e.target.checked)}
                          className="accent-orange-400"
                        />
                        <span className="text-[10px] text-white/50">Video loopt terug bij overschrijding pitch-tijd</span>
                      </label>
                    </div>
                  </div>

                  <button
                    onClick={() => void saveMyLivePitch()}
                    className="h-9 px-3 rounded-lg text-xs font-semibold text-white transition-colors"
                    style={{ backgroundColor: livePitchTheme.primary }}
                  >
                    Opslaan voor live pitch
                  </button>
                </div>

                <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                  {isHost && (
                    <div className="mb-3 rounded-lg bg-white/5 border border-white/10 p-2.5 flex items-center gap-2">
                      <span className="text-[11px] text-white/70">Timer per pitch (sec):</span>
                      <input
                        type="number"
                        min={15}
                        max={600}
                        value={livePitchTimerDurationSeconds}
                        onChange={(e) => setLivePitchTimerDurationSeconds(Math.max(15, Math.min(600, Number(e.target.value) || 60)))}
                        className="w-20 h-8 px-2 rounded bg-white/10 border border-white/15 text-xs focus:outline-none focus:ring-2 focus:ring-white/30"
                      />
                      <button
                        onClick={() => void saveLivePitchDoc({ timerDurationSeconds: livePitchTimerDurationSeconds })}
                        className="h-8 px-2.5 rounded text-[11px] font-semibold border border-white/25 hover:bg-white/10"
                      >
                        Opslaan
                      </button>
                    </div>
                  )}
                  <p className="text-xs uppercase tracking-wide text-white/70 mb-2">Volgorde deelnemers</p>
                  <div className="space-y-2">
                    {livePitchEntries.length === 0 ? (
                      <p className="text-xs text-white/60">Nog geen pitches ingevuld.</p>
                    ) : livePitchEntries.map((entry, idx) => (
                      <div key={entry.userId} className="rounded-lg bg-white/5 border border-white/10 p-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-xs font-semibold">{idx + 1}. {entry.presenterName}</p>
                            <p className="text-[11px] text-white/70">{entry.title || 'Zonder titel'}</p>
                            <p className="text-[10px] text-white/55">{entry.enabled === false ? 'Uitgeschakeld voor pitch' : 'Actief'}</p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {isHost && (
                              <>
                                <button
                                  onClick={() => void moveLivePitchEntry(entry.userId, 'up')}
                                  className="h-7 px-2 rounded text-[10px] font-semibold border border-white/25 hover:bg-white/10"
                                  title="Omhoog in tijdlijn"
                                >
                                  ↑
                                </button>
                                <button
                                  onClick={() => void moveLivePitchEntry(entry.userId, 'down')}
                                  className="h-7 px-2 rounded text-[10px] font-semibold border border-white/25 hover:bg-white/10"
                                  title="Omlaag in tijdlijn"
                                >
                                  ↓
                                </button>
                                <button
                                  onClick={() => void toggleLivePitchEntryEnabled(entry.userId)}
                                  className={`h-7 px-2.5 rounded text-[10px] font-semibold ${entry.enabled === false ? 'bg-rose-700/70' : 'bg-emerald-700/70'}`}
                                  title="Meedoen aan/uit zetten"
                                >
                                  {entry.enabled === false ? 'Uit' : 'Aan'}
                                </button>
                              </>
                            )}
                            {isHost && (
                              <button
                                onClick={() => void startLivePitchForUser(entry.userId)}
                                disabled={entry.enabled === false}
                                className="h-7 px-2.5 rounded text-[11px] font-semibold text-white disabled:opacity-40"
                                style={{ backgroundColor: livePitchTheme.accent }}
                              >
                                Start
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── NU LIVE paneel — licht, theembaar ─────────────── */}
              <div className="space-y-4">
                <NuLivePanel
                  colors={livePitchTheme}
                  entry={runningEntry}
                  startedAtMs={livePitchStartedAtMs}
                  duration={livePitchTimerDurationSeconds}
                  totalSlots={timelineSlots.filter((s) => s.slotType === 'pitch').length}
                  isLive={!!livePitchRunningUserId}
                />
                {/* Stop pitch — host only */}
                {isHost && runningEntry && (
                  <button
                    onClick={() => void stopLivePitch(meetingMode === 'physical')}
                    className="w-full h-9 rounded-xl text-xs font-semibold border border-white/20 bg-white/8 hover:bg-white/15 text-white/80 transition-colors"
                  >
                    Stop pitch {meetingMode === 'physical' ? '(overgang)' : ''}
                  </button>
                )}
                {/* ── Fase 3: Emoji reacties ───────────────────────── */}
                {user?.uid && entityId && runningEntry && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-[11px] text-white/60 mb-2 uppercase tracking-wide">Reageer op deze pitch</p>
                    <PitchReactions
                      entityId={entityId}
                      roomName={roomName}
                      currentUserId={user.uid}
                      currentUserName={displayName}
                      activePitcherUserId={runningEntry.userId}
                      activePitcherName={runningEntry.presenterName}
                      activePitchSlotNumber={timelineSlots.find((s) => s.userId === runningEntry.userId)?.slotNumber ?? 0}
                      accentColor={livePitchTheme.accent}
                    />
                  </div>
                )}

                {/* ── Fase 2.5: Overgangs-muziek instellen (host) ─── */}
                {isHost && entityId && (
                  <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                    <p className="text-[11px] text-white/60 uppercase tracking-wide">Overgangs-instellingen</p>
                    <div className="flex items-center gap-2">
                      <label className="text-[10px] text-white/50 whitespace-nowrap">Overgang (sec):</label>
                      <input
                        type="number"
                        min={5}
                        max={30}
                        value={transitionDurationSeconds}
                        onChange={(e) => setTransitionDurationSeconds(Math.max(5, Math.min(30, Number(e.target.value) || 10)))}
                        onBlur={() => entityId && void setDoc(doc(db, 'meetingSettings', entityId), { transitionDurationSeconds, updatedAt: serverTimestamp() }, { merge: true })}
                        className="w-16 h-7 px-2 rounded bg-white/10 border border-white/15 text-xs focus:outline-none focus:ring-2 focus:ring-white/30"
                        aria-label="Overgang duur in seconden"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-[10px] text-white/50 whitespace-nowrap">Volume muziek:</label>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={transitionMusicVolume}
                        onChange={(e) => setTransitionMusicVolume(Number(e.target.value))}
                        onMouseUp={() => entityId && void setDoc(doc(db, 'meetingSettings', entityId), { transitionMusicVolume, updatedAt: serverTimestamp() }, { merge: true })}
                        className="flex-1 accent-orange-400"
                        aria-label="Volume overgangsmuziek"
                      />
                      <span className="text-[10px] text-white/40 w-8">{Math.round(transitionMusicVolume * 100)}%</span>
                    </div>
                    {transitionMusicUrl && (
                      <p className="text-[10px] text-emerald-400">Muziek ingesteld: {transitionMusicUrl.split('/').pop()?.split('?')[0] ?? 'bestand'}</p>
                    )}
                    <input
                      ref={transitionMusicInputRef}
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void uploadTransitionMusic(f);
                        if (transitionMusicInputRef.current) transitionMusicInputRef.current.value = '';
                      }}
                    />
                    <button
                      onClick={() => transitionMusicInputRef.current?.click()}
                      disabled={uploadingTransitionMusic}
                      className="min-h-[36px] px-3 text-[11px] font-medium rounded-lg border border-white/20 hover:bg-white/10 disabled:opacity-50 transition-colors"
                      aria-label="Overgangs-muziek uploaden"
                    >
                      {uploadingTransitionMusic ? 'Uploaden\u2026' : transitionMusicUrl ? 'Muziek vervangen' : 'Overgangs-muziek uploaden'}
                    </button>
                  </div>
                )}

                {/* ── Fase 3: QR-code voor gasten (host only) ─────── */}
                {isHost && entityId && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <button
                      onClick={() => setShowQrCode((v) => !v)}
                      aria-expanded={showQrCode}
                      className="min-h-[36px] px-3 text-[11px] font-medium rounded-lg border border-white/20 hover:bg-white/10 text-white transition-colors"
                    >
                      {showQrCode ? 'QR verbergen' : '🔲 Toon QR-code voor gasten'}
                    </button>
                    {showQrCode && (
                      <div className="mt-3 flex flex-col items-center">
                        <QrDisplay
                          entityId={entityId}
                          roomName={roomName}
                          accentColor={livePitchTheme.accent}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* ── Fase 3: Aanwezigheidshistorie (host only) ───── */}
                {isHost && entityId && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <button
                      onClick={() => setShowAttendanceHistory((v) => !v)}
                      aria-expanded={showAttendanceHistory}
                      className="min-h-[36px] px-3 text-[11px] font-medium rounded-lg border border-white/20 hover:bg-white/10 text-white transition-colors"
                    >
                      {showAttendanceHistory ? 'Sluit historieoverzicht' : '📊 Aanwezigheidshistorie'}
                    </button>
                    {showAttendanceHistory && (
                      <div className="mt-3">
                        <AttendanceHistory entityId={entityId} />
                      </div>
                    )}
                  </div>
                )}

                <p className="text-[11px] text-white/60">
                  Tip: deelnemers vullen eerst hun pitch in; host/manager bepaalt volgorde, wie meedoet en start daarna per spreker de timer.
                </p>
              </div>
            </div>

            {/* ── Visuele tijdlijn — ONDERAAN ─────────────────────────── */}
            <div className="px-5 py-3 overflow-x-auto border-t border-white/8 bg-white/3">
              <div className="flex items-center gap-1 min-w-max">
                {timelineSlots.map((slot, i) => (
                  <React.Fragment key={slot.key}>
                    <div
                      className={`flex flex-col items-center gap-0.5 transition-all ${slot.isAbsent ? 'opacity-30 grayscale' : ''}`}
                      title={`Slot ${slot.slotNumber}: ${slot.label}`}
                    >
                      <div
                        className={`h-7 px-2.5 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all whitespace-nowrap ${
                          slot.isActive
                            ? 'bg-orange-500 text-white ring-2 ring-orange-300 ring-offset-1 ring-offset-[#0f172a]'
                            : slot.isDone
                            ? 'bg-white/10 text-white/40'
                            : slot.slotType === 'pitch'
                            ? 'bg-white/15 text-white/80 hover:bg-white/20'
                            : 'bg-white/8 text-white/50'
                        }`}
                      >
                        {slot.isDone && <span>&#10003;</span>}
                        {slot.isAbsent && <span>&#10005;</span>}
                        <span>{slot.label}</span>
                      </div>
                      <span className="text-[9px] text-white/30">#{slot.slotNumber}</span>
                    </div>
                    {i < timelineSlots.length - 1 && (
                      <div className="w-3 h-px bg-white/15 flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showRecordingPrompt && (
        <div className="absolute inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm rounded-xl p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shrink-0" />
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">Meeting opnemen</p>
                <p className="text-white/50 text-[11px] mt-0.5">Elke meeting wordt automatisch opgenomen</p>
              </div>
            </div>

            {/* Info */}
            <div className="px-5 py-4 space-y-3">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 space-y-2">
                <p className="text-amber-300 text-[11px] font-semibold">Twee stappen na klikken:</p>
                <div className="space-y-1.5">
                  <div className="flex items-start gap-2">
                    <span className="text-amber-400 text-[11px] font-bold shrink-0 mt-0.5">1.</span>
                    <span className="text-white/70 text-[11px]">Browser vraagt microfoon — klik <strong className="text-white">Toestaan</strong></span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-amber-400 text-[11px] font-bold shrink-0 mt-0.5">2.</span>
                    <span className="text-white/70 text-[11px]">Selecteer dit tabblad en vink <strong className="text-white">"Tabbladaudio toestaan"</strong> aan</span>
                  </div>
                </div>
              </div>
              <p className="text-white/40 text-[10px] text-center">
                Na stoppen wordt de opname automatisch opgeslagen voor alle leden.
              </p>
            </div>

            {/* Actions */}
            <div className="px-5 pb-5 flex flex-col gap-2">
              <button
                onClick={() => { setShowRecordingPrompt(false); startRecording(); }}
                className="w-full h-11 text-sm font-bold text-white bg-red-600 hover:bg-red-500 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                Start opname
              </button>
              <button
                onClick={() => setShowRecordingPrompt(false)}
                className="w-full h-9 text-[11px] text-white/40 hover:text-white/70 transition-colors"
              >
                Overslaan (niet opnemen)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fase 2: Selfstart overlay ────────────────────────────────── */}
      {isMySelfStartTurn && user?.uid && entityId && (
        <SelfStartOverlay
          entityId={entityId}
          roomName={roomName}
          userId={user.uid}
          presenterName={displayName}
          timerDurationSeconds={livePitchTimerDurationSeconds ?? TIMER_DEFAULT_SECONDS}
          accentColor={livePitchTheme.accent}
          onStarted={() => {
            // overlay will close automatically when runningUserId updates via onSnapshot
          }}
          onDismiss={() => {
            // Dismiss without starting — host can start manually
          }}
        />
      )}

      {/* ── Fase 2: Countdown overlay ────────────────────────────────── */}
      {showCountdown && entityId && (
        <MeetingCountdown
          entityId={entityId}
          clubName={clubName}
          logoUrl={clubLogoUrl}
          musicUrl={countdownMusicUrl}
          videoUrl={countdownVideoUrl}
          imageUrl={countdownImageUrl}
          slides={slides.length > 0 ? slides : undefined}
          countdownMinutes={countdownMinutes}
          primaryColor={livePitchTheme.primary}
          isHost={isHost}
          sessionTemplateEditor={
            isHost && user?.uid
              ? {
                  userId: user.uid,
                  entityType: (entityType as 'mastermind' | 'community' | 'networkclub') ?? 'mastermind',
                  memberCount: memberCount ?? memberNames.length,
                  memberNames,
                  memberAvatars,
                }
              : null
          }
          featureToggles={{
            docView: docViewEnabled,
            linkOpener: linkOpenerEnabled,
            guestSpotlight: guestSpotlightEnabled,
            commitmentReview: commitmentReviewEnabled,
            slideshow: slideshowEnabled,
            taskReminder: taskReminderEnabled,
            autoRecord: autoRecordEnabled,
          }}
          onFeatureToggle={async (key, val) => {
            const map: Record<string, (v: boolean) => void> = {
              docView: setDocViewEnabled,
              linkOpener: setLinkOpenerEnabled,
              guestSpotlight: setGuestSpotlightEnabled,
              commitmentReview: setCommitmentReviewEnabled,
              slideshow: setSlideshowEnabled,
              taskReminder: setTaskReminderEnabled,
              autoRecord: (v: boolean) => { setAutoRecordEnabled(v); autoRecordEnabledRef.current = v; },
            };
            map[key]?.(val);
            await setDoc(doc(db, 'meetingSettings', entityId ?? roomName), {
              [`${key}Enabled`]: val,
            }, { merge: true }).catch(() => {});
            // taskReminder wordt ook in entitySettings opgeslagen (voor reminder-check bij app-start)
            if (key === 'taskReminder' && entityId) {
              setDoc(doc(db, 'entitySettings', entityId), {
                taskReminderEnabled: val,
              }, { merge: true }).catch(() => {});
            }
            // taskReminder staat ook in entitySettings (voor reminder check bij app-open)
            if (key === 'taskReminder' && entityId) {
              await setDoc(doc(db, 'entitySettings', entityId), {
                taskReminderEnabled: val,
              }, { merge: true }).catch(() => {});
            }
          }}
          startLabel={meetingMode === 'physical' ? "Start bijeenkomst \u2192" : undefined}
          onMeetingStart={() => {
            setShowCountdown(false);
            window.dispatchEvent(new CustomEvent('requestMeetingFullscreen'));
          }}
          onClose={() => setShowCountdown(false)}
        />
      )}

      {/* ── Fase 2: Post-meeting rapport panel ───────────────────────── */}
      {showRapportPanel && (
        <div className="absolute inset-0 z-[88] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0f172a] text-white rounded-2xl w-full max-w-sm p-6 space-y-4 border border-white/10">
            <h3 className="text-base font-bold">📋 Post-meeting rapport</h3>

            {rapportState.status === 'generating' && (
              <p className="text-sm text-white/70">{STRINGS.rapport.generating}</p>
            )}
            {rapportState.status === 'uploading' && (
              <div className="space-y-1">
                <p className="text-sm text-white/70">{STRINGS.rapport.generating}</p>
                <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
                  <div className="h-full rounded-full bg-orange-400 transition-all" style={{ width: `${rapportState.progress}%` }} />
                </div>
              </div>
            )}
            {rapportState.status === 'done' && (
              <div className="space-y-3">
                <p className="text-sm text-emerald-400 font-semibold">{STRINGS.rapport.success}</p>
                <a
                  href={rapportState.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block min-h-[44px] px-5 py-2.5 rounded-xl text-center font-semibold text-white bg-orange-500 hover:bg-orange-600 transition-colors"
                >
                  {STRINGS.rapport.downloadButton}
                </a>
              </div>
            )}
            {rapportState.status === 'error' && (
              <p role="alert" className="text-sm text-rose-400">{rapportState.message}</p>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => { setShowRapportPanel(false); resetRapport(); }}
                className="flex-1 min-h-[44px] rounded-xl text-sm font-medium bg-white/10 hover:bg-white/20 transition-colors"
              >
                Sluiten
              </button>
              {rapportState.status !== 'generating' && rapportState.status !== 'uploading' && (
                <button
                  onClick={() => { safeLeave(); }}
                  className="flex-1 min-h-[44px] rounded-xl text-sm font-semibold bg-rose-600 hover:bg-rose-700 text-white transition-colors"
                >
                  {STRINGS.rapport.closeButton}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkClubMeeting;
